import { state, api, $, $$, toast, escHtml, truncate, todayStr, localDateKey, _timeAgo, _ensureMachineCache, _allMachinesCache, _lineMap, _lineLabels, _populateLineSelect, customConfirm, customPrompt, customSelect, formatDateRO, formatDateTimeRO, intervalHoursProgress, shouldShowMaintenanceInterval } from './core';
import type { StareLucrare } from './core';
import { askText, askConfirm, askConfirmCuBifa } from './dialogs';
import { grupeazaEchivalente, ordoneazaCuEchivalente } from './echivalente';
import {
    fetchMachinePhotosBatch, fetchMachinePhotos,
    openLightbox as openMachineLightbox,
    buildPhotoThumbHTML,
    type LightboxPhoto,
} from './lightbox';
import { colleagueOfferLesson, colleagueSuggestField, takePendingWorkflowLesson } from './colleague';
import { colegIntreaba, notaCuRaspuns, colegDupaInchidere } from './coleg';
import { confirmaUnText } from './corectura';
import { randeazaCeUrmeaza, legaActiunile, alegeLinia } from './ce-urmeaza';
import { temaD, sim } from './tema';
import type { RandInterventionProcedures, RandInterventionProcedureSteps, RandMachineIssues, RandMaintenanceIntervals,
    RandMaintenanceLog, RandMaintenanceWorkflows, RandOperatorTasks, RandParts } from './tipuri-baza';
import type { EquipmentItem, FactoryStructure, ProductionLine } from './types';

/** cadența pe care o numără singură mașina (routes/maintenance/_comun.py:contoare_masina); `citit_la` e UTC */
interface CeasMasina { cadenta_h: number; scurse_h: number; ramase_h: number; depasit: boolean; citit_la: string | null; vechi: boolean; }
/** de ce e sărită lucrarea (routes/maintenance/taskuri.py:_should_skip_task_in_pause); `state` lipsește pe calea veche */
interface InfoPauza { offline_since: string | null; offline_duration_hours: number; threshold_hours: number; state?: string; }
/** o lucrare din /api/mytasks/daily (routes/maintenance/taskuri.py:api_mytasks_daily): ocurență din plan (`source`
 *  'calendar') sau lucrare alocată ('engineer'). Cheile opționale le pune doar una dintre ele, ori doar în anumite stări */
export interface LucrarePlan {
    source: string;
    interval_id: number | null;
    machine_id: string | null;
    line_id: string;
    action_ro: string | null;
    interval_hours: number;
    interval_text: string | null;
    category: string | null;
    part_number: string | null;
    lubricant: string | null;
    quantity: string | null;
    critical: number | null;
    due_date: string;
    /* ocurențele din plan */
    last_service_date?: string | null;
    document_id?: number | null;
    page_number?: number | null;
    needs_parts?: number;
    has_linked_parts?: boolean;
    machine_state?: string;
    ceas_masina?: CeasMasina;
    maintenance_suggestion?: boolean;
    workflow_id?: number | null;
    skipped?: boolean;
    skip_reason?: string;
    pause_info?: InfoPauza;
    /* lucrările alocate; `assigned_to` vine și la ocurența alocată altcuiva */
    task_id?: number;
    assigned_by?: string | null;
    assigned_to?: string | null;
    task_type?: string;
    status?: string | null;
    steps_data?: string | null;
    notes?: string | null;
    priority?: string;
    /** doar pe ziua de azi */
    completed_today?: boolean;
    /** 1 nov 2026 (Dan): lucrarea a fost RESPINSĂ de inginer după ultima lucrare bună — a revenit la „de făcut” */
    respinsa?: { data: string | null; de: string; motiv: string; log_id: number; la: string | null } | null;
}
/** /api/mytasks/overdue (routes/maintenance/taskuri.py:calculeaza_restante): cheile din plan + întârzierea și marcajele
 *  restanței; `due_date` e null la o restanță pe ore fără scadență calculabilă */
export interface LucrareRestanta extends Omit<LucrarePlan, 'due_date'> {
    due_date: string | null;
    overdue_days: number;
    overdue_hours: number;
    /* ocurențele din plan */
    restanta_pe_ore?: boolean;
    ore_de_la_service?: number | null;
    amanata?: boolean;
    amanata_pana_la?: string | null;
    amanare_motiv?: string;
    amanata_de?: string;
    inghetat?: boolean;
    inghetat_din?: string;
    cere_masina_pornita?: boolean;
    cere_masina_pornita_motiv?: string | null;
    alocata_manual?: boolean;
    /* lucrările alocate cu amânarea expirată */
    amanare_expirata?: boolean;
    waiting_part_number?: string | null;
    postpone_reason?: string;
    postponed_until?: string | null;
}
/** cardul din „Lucrările mele”: lucrare din plan sau restantă, ori lucrare alocată rescrisă de `_mapOperatorTaskToCard` /
 *  amânatele din `_renderMyTasks` — acelea n-au cheile intervalului, iar `postpone_reason` e coloana (poate fi null) */
type CardLucrare = Partial<Omit<LucrareRestanta, 'postpone_reason'>> & { machine_id: string | null; postpone_reason?: string | null };
/** ce citesc filtrele „Lucrărilor mele” (linie, utilaj, „cine”) */
interface FiltruLucrare { machine_id?: string | null; line_id?: string | null; skipped?: boolean; assigned_by?: string | null; assigned_to?: string | null; }
/** /api/mytasks/facute_azi (routes/maintenance/taskuri.py, 26 oct 2026): lucrările din plan închise azi */
interface FacutAzi extends FiltruLucrare {
    id: number; machine_id: string; machine_name: string | null; action: string; technician: string | null;
    ora: string | null; workflow_id: number | null; interval_id: number | null; category: string | null;
    validation_status: string | null; poate_anula: boolean;
}
/** /api/mytasks/postponed (routes/maintenance/taskuri.py): amânările active ale omului; `id`, `line_id`, `description`
 *  și `postpone_reason` sunt dubluri păstrate pentru codul vechi */
export interface LucrareAmanata extends Pick<RandOperatorTasks, 'id' | 'machine_id' | 'interval_id' | 'waiting_part_id'
        | 'waiting_part_number' | 'notes'> {
    task_id: number; machine_name: string | null; line: string; line_id: string; task_name: string; description: string;
    frequency: string; reason_code: string; reason_text: string; postpone_reason: string; postponed_until: string;
    postponed_by: string | null; postponed_at: string | null; status: string;
}
/** /api/maintenance_workflows?status=… (routes/maintenance/workflow.py): rândul workflow-ului + numele utilajului și
 *  acțiunea intervalului (LEFT JOIN). Tabelul n-are linie: `line_id` nu vine niciodată */
export interface LucrareInCurs extends RandMaintenanceWorkflows {
    machine_name: string | null; action_ro: string | null;
    line_id?: undefined;
}
/** /api/machine_issues și …/<id>/details (routes/maintenance/avarii.py): rândul problemei + numele utilajului (LEFT JOIN) */
export interface ProblemaUtilaj extends RandMachineIssues { machine_name: string | null; }
/** un rând din machine_issue_events, cum îl trimit …/<id>/timeline și …/<id>/details */
interface EvenimentProblema {
    id: number; event_type: string; actor: string | null; from_status: string | null; to_status: string | null;
    note: string | null; created_at: string | null;
}
/** /api/mytasks/badges (routes/maintenance/taskuri.py): contoarele din capul „Lucrărilor mele” */
interface InsigneLucrari {
    overdue: number; in_progress: number; paused: number; total_active: number; today: number;
    workflows_in_progress: number; workflows_paused: number;
}
/** o lucrare din /api/mytasks/due_soon (routes/maintenance/taskuri.py): lucrare alocată (coloanele de mai jos) sau
 *  ocurență din plan (`source` 'calendar', `id` null), cu aceleași nume de chei */
interface LucrareScadenta extends Pick<RandOperatorTasks, 'machine_id' | 'line_id' | 'description' | 'due_date' | 'status'
        | 'frequency' | 'interval_id' | 'task_type'> {
    id: number | null; source?: string;
}
/** /api/mytasks/due_soon: fereastra cerută și lucrările scadente în ea */
interface ScadenteInCurand { window_hours: number; from: string; to: string; count: number; tasks: LucrareScadenta[]; }
/** /api/maintenance_intervals/<id>/matched_parts (routes/maintenance/piese.py): piesa din catalog + potrivirea. Cheile
 *  legăturii (`confidence`, `mapping_id`…) vin doar pentru piesele legate în interval_parts, nu la potrivirea pe cuvinte */
interface PiesaPotrivita extends RandParts {
    match_score: number; match_source: string; verified: boolean;
    mapped_qty?: number | null; confidence?: number | null; verified_by?: string | null;
    mapping_notes?: string | null; mapping_id?: number | null;
}
/** /api/parts/search (routes/other.py): câteva coloane din `parts` */
type PiesaCautata = Pick<RandParts, 'id' | 'machine_id' | 'part_number' | 'description_ro' | 'description_en' | 'category' | 'wear_type'>;
/** POST /api/maintenance_log (routes/maintenance/jurnal.py): lucrarea scrisă; la un workflow încheiat, rândul lui
 *  completat (`completat_workflow`, fără `task_closed` / `parts_deducted`) */
interface LucrareScrisa {
    status: string; id: number; registration_number: string | null; validation_status: string | null;
    /** id-ul lucrării alocate închise odată cu scrierea, sau null */
    task_closed?: number | null;
    parts_deducted?: { part_id: number; deducted: boolean; reason?: string; stock_before?: number; stock_after?: number; below_min?: boolean }[];
    completat_workflow?: boolean;
    /** verdictul colegului AI: in_citire | validat_ai | la_inginer | neatins | null */
    coleg_decizie?: string | null;
}
/** corpul amânării: POST /api/operator_tasks/<id>/reschedule, sau POST /api/operator_tasks cu cheile ocurenței din plan */
interface CorpAmanare {
    reason_code: string; reason_text: string | null; postponed_until: string; due_date: string;
    waiting_part_id?: number; waiting_part_number?: string;
    machine_id?: string; interval_id?: number | null; task_type?: string; description?: string; assigned_by?: string;
    status?: string; replaces_calendar_date?: string; postpone_reason?: string;
}
/** un eveniment din /api/operator_tasks/<id>/timeline sau /api/mytasks/calendar_timeline (routes/maintenance/taskuri.py);
 *  cheile amânării vin doar la evenimentele `postpone_*` */
interface EvenimentIstoric {
    type: string; actor: string; ts: string | null; details: string;
    reason_code?: string; reason_text?: string; postponed_until?: string;
}
/** răspunsul celor două rute de istoric (`task_id` doar la prima) */
interface IstoricLucrare { task_id?: number; events: EvenimentIstoric[]; }
/** /api/parts/<id>/stock_check (routes/other.py): `stock_qty` e null când nu s-a setat stoc (`stock_status` 'unknown') */
interface StocPiesa {
    part_id: number; part_number: string | null; description: string | null; stock_qty: number | null; min_stock: number | null;
    stock_status: string; source: string; message?: string;
    location?: string | null; unit_cost?: number | null; currency?: string | null; last_updated?: string | null;
}
/** /api/maintenance_intervals (routes/maintenance/intervale.py:_enrich_interval_dict): rândul intervalului + ultima lucrare
 *  potrivită și starea mașinii; `workflow_id` doar când mașina e oprită pentru mentenanță */
export interface IntervalPlan extends RandMaintenanceIntervals {
    last_log_hours: number | null; last_log_date: string | null; last_log_synthetic: number;
    machine_state: string; should_hide_alert: boolean; workflow_id?: number | null;
    /** 1 nov 2026: starea din sursa unică (routes/maintenance/_stare.py) */
    stare?: StareLucrare;
}
/** intervalul cum îl cere `intervalHoursProgress` (core.ts), care nu primește `interval_hours` null */
type ProgresOre = Parameters<typeof intervalHoursProgress>[0];
/** /api/operator_tasks?status=… (routes/maintenance/taskuri.py): rândul din operator_tasks. Tabelul n-are
 *  `interval_text`: cardul din calendar cade mereu pe `frequency` */
export interface LucrareAlocata extends RandOperatorTasks { interval_text?: undefined; }
/** o linie din /api/factory_structure (routes/machines.py), cu orele ei */
export interface LinieStructura extends ProductionLine { operating_hours: number; }
/** /api/factory_structure: liniile de producție și grupurile de utilități. Cheia grupului e id-ul liniei (UTIL_AER, TRAFO,
 *  RACITOARE_EXTERNE), parcurse toate în `loadCalendar` */
export interface StructuraFabrica extends FactoryStructure {
    production_lines: LinieStructura[];
    utilities: Record<string, { label: string; icon: string; machines: EquipmentItem[] }>;
}
/** /api/maintenance_intervals/conditional (routes/maintenance/intervale.py): intervalele „la nevoie” + linia și numele utilajului */
interface IntervalConditional extends RandMaintenanceIntervals { line_id: string | null; machine_name: string | null; }
/** /api/maintenance_intervals/stats (routes/maintenance/intervale.py): câte intervale, pe tip și pe utilaj */
interface StatisticiIntervale {
    total: number; scheduled: number; conditional: number; disabled: number;
    per_machine: { machine_id: string | null; line: string | null; name: string | null; total: number; scheduled: number;
        conditional: number; critical: number }[];
}
/** o ocurență proiectată de `buildCalendarEvents` dintr-un interval */
export interface OcurentaProiectata extends Pick<RandMaintenanceIntervals, 'machine_id' | 'action_ro' | 'category' | 'critical'
        | 'interval_text' | 'document_id' | 'page_number' | 'needs_parts'> {
    interval_id: number; interval_hours: number; line_id: string;
}
/** o lucrare efectuată, cum o arată panoul zilei (din /api/maintenance_log) */
export type LucrareFacuta = Pick<RandMaintenanceLog, 'machine_id' | 'action' | 'date' | 'technician'>;
/** un pas al fișei (intervention_procedure_steps). Codul citește și `description` / `duration_minutes` / `photo_url`,
 *  pe care serverul nu le trimite — coloanele sunt `estimated_minutes` și `photo_reference` */
interface PasFisa extends RandInterventionProcedureSteps { description?: undefined; duration_minutes?: undefined; photo_url?: undefined; }
/** /api/procedures/for-interval/<id> (routes/maintenance/proceduri.py): fișa ACTIVĂ a intervalului cu pașii ei (sau null) */
interface FisaInterval extends RandInterventionProcedures {
    steps: PasFisa[];
    previous_photos: { step: number | null; photo: string; operator: string | null; date: string | null }[];
    avg_duration: number | null;
    execution_count: number;
    /** interogarea cere `cause` / `status`, coloane pe care diagnostic_cases nu le are — vine mereu [] */
    lessons: { symptom: string; cause: string | null; actions_taken: string | null; status: string | null }[];
}
/** POST /api/procedures/generate-from-ai (routes/maintenance/proceduri.py): fișa nouă (draft) sau eroarea */
interface FisaGenerata { id?: number; status?: string; step_count?: number; error?: string; raw?: string; }

/* ═══ LUCRĂRILE MELE (OPERATOR TASKS) ══════════════════════════ */

/** Ce spune ceasul mașinii despre lucrarea asta, plus ce trebuie apăsat pe HMI. */
function _ceasMasinaHtml(t: { ceas_masina?: CeasMasina }): string {
    const c = t && t.ceas_masina;
    if (!c) return '';
    // Contor necitit de ore (PLC fără legătură sau linie oprită): cifra e de atunci, nu de acum (9 oct 2026)
    const vechi = !!c.vechi;
    const depasit = !!c.depasit && !vechi;
    let cifre = depasit
        ? `mașina numără <b>${c.scurse_h}h</b> din ${c.cadenta_h}h — <b>depășit cu ${c.scurse_h - c.cadenta_h}h</b>`
        : `mașina numără ${c.scurse_h}h din ${c.cadenta_h}h`;
    if (vechi) {
        const cand = c.citit_la
            ? new Date(c.citit_la).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
            : '—';
        cifre = `contorul mașinii nu s-a mai citit din ${cand} — atunci număra ${c.scurse_h}h din ${c.cadenta_h}h`;
    }
    return `<div class="mytask-ceas ${depasit ? 'mytask-ceas-rosu' : ''}">
        <div>⏱ ${cifre}</div>
        <div class="mytask-ceas-hmi">Apasă <b>Start</b> pe ecranul mașinii când începi și <b>Stop</b> când termini —
        altfel utilajul nu-și resetează contorul și alarma rămâne aprinsă.</div>
    </div>`;
}

export let _myTasksTab: string | undefined = 'date';
export let _myTasksSelectedDate = todayStr();
/** Secțiunea „Oprite — se pot face cât stă mașina" pornește deschisă?
 *  Se ridică la click pe chip-ul „⏸ N sărite" din banda de zile (5 aug 2026). */
let _myTasksShowSkipped = false;
export let _myTasksDailyCache: Record<string, LucrarePlan[]> = {};
let _myTasksFacuteAzi: FacutAzi[] = [];
export let _myTasksOverdue: LucrareRestanta[] = [];
export let _myTasksPostponed: LucrareAmanata[] = [];
export let _myTasksInProgress: RandOperatorTasks[] = [];
export let _myTasksPaused: RandOperatorTasks[] = [];
export let _myTasksActiveWfs: LucrareInCurs[] = [];
export let _myTasksOpenIssues: ProblemaUtilaj[] = [];
export let _myTasksDateCounts: Record<string, number> = {};
export let _myTasksFiltersReady = false;

/* Refactor 2026-05-11: orice task se poate amâna cu motiv obligatoriu.
 * Limita implicită = intervalul task-ului (zilnic+1 / săpt+7 / lunar+30).
 * Depășirea limitei e permisă DOAR cu reason_code='other' + reason_text ≥20 chars.
 * Conceptul "Recurentă" a fost ELIMINAT din UI. */
function _intervalTextToDays(intervalText: string | null | undefined): number {
    if (!intervalText) return 30;
    const t = (intervalText || '').toLowerCase().trim();
    const hints: Array<[string, number]> = [
        ['zilnic', 1], ['daily', 1],
        ['săptămânal', 7], ['saptamanal', 7], ['weekly', 7],
        ['lunar', 30], ['monthly', 30],
        ['trimestrial', 90], ['quarterly', 90],
        ['semestrial', 180], ['biannual', 180],
        ['anual', 365], ['yearly', 365], ['annual', 365],
    ];
    for (const [kw, days] of hints) {
        if (t.includes(kw)) return days;
    }
    if (t.includes('24h') || t.includes('8h')) return 1;
    if (t.includes('7 z') || t.includes('7zile') || t.includes('7 zi')) return 7;
    if (t.includes('30 z') || t.includes('30zile')) return 30;
    const mZ = t.match(/(\d+)\s*z[ai]/);
    if (mZ) return Math.max(1, parseInt(mZ[1], 10));
    const mH = t.match(/(\d+)\s*h/);
    if (mH) return Math.max(1, Math.floor(parseInt(mH[1], 10) / 24));
    return 30;
}

const POSTPONE_REASONS: Array<{ code: string; label: string }> = [
    { code: 'no_production_stop', label: 'Nu s-a aprobat oprirea producției' },
    { code: 'missing_part', label: 'Lipsă piesă de schimb' },
    { code: 'missing_tool', label: 'Lipsă unealtă/echipament' },
    { code: 'shift_change', label: 'Schimb de tură' },
    { code: 'other', label: 'Alt motiv (Altele)' },
];

function _postponeReasonLabel(code: string | null | undefined): string {
    if (!code) return '';
    const r = POSTPONE_REASONS.find(x => x.code === code);
    return r ? r.label : code;
}

function _issueSeverityMeta(sev: string | null): { icon: string; label: string; color: string } {
    switch (sev) {
        case 'critical': return { icon: '🔴', label: 'CRITIC', color: '#ef4444' };
        case 'high': return { icon: '🟠', label: 'RIDICAT', color: '#f59e0b' };
        case 'medium': return { icon: '🔵', label: 'MEDIU', color: '#3b82f6' };
        case 'low': return { icon: '⚪', label: 'SCĂZUT', color: '#94a3b8' };
        default: return { icon: '🔵', label: 'MEDIU', color: '#3b82f6' };
    }
}

/* Delegated click router for issue-card buttons.
 * Attached ONCE on document.body — works regardless of when/where issue cards
 * are inserted, survives `listEl.innerHTML = ''` in _renderMyTasks, and is
 * idempotent (guarded by window-level flag). This fixes the recurring bug
 * where per-card querySelectorAll().forEach(addEventListener) was attaching
 * 0 listeners because cards were still in a detached DocumentFragment. */
function _ensureIssueClickDelegate(): void {
    if (window._issueClickDelegateBound) return;
    window._issueClickDelegateBound = true;
    document.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const start = target.closest('.issue-start-btn') as HTMLElement | null;
        if (start) {
            e.preventDefault();
            const id = start.dataset.issueid;
            if (id) await _showIssueWorkPanel(id);
            return;
        }
        const tl = target.closest('.issue-timeline-btn') as HTMLElement | null;
        if (tl) {
            e.preventDefault();
            const id = tl.dataset.issueid;
            if (id) await _showIssueTimeline(id);
            return;
        }
        const res = target.closest('.issue-resolve-btn') as HTMLElement | null;
        if (res) {
            e.preventDefault();
            const id = res.dataset.issueid;
            if (!id) return;
            const scris = await customPrompt('Cum s-a rezolvat problema?', '', 'Rezolvare problemă');
            if (scris === null) return;
            const notes = scris ? await confirmaUnText('Cum s-a rezolvat', scris, 'problema-rezolvata') : scris;
            const operatorName = state.currentUser?.username || '';
            try {
                await api('/api/machine_issues/' + id + '/resolve', {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ resolved_by: operatorName, resolution_notes: notes || '' })
                });
                toast('Problemă marcată ca rezolvată — necesită validare', 'success');
                loadMyTasks();
            } catch (err) { toast('Eroare: ' + (err as Error).message, 'error'); }
            return;
        }
        const val = target.closest('.issue-validate-btn') as HTMLElement | null;
        if (val) {
            e.preventDefault();
            const id = val.dataset.issueid;
            if (!id) return;
            const notes = await customPrompt('Note validare:', '', 'Validare problemă');
            if (notes === null) return;
            try {
                await api('/api/machine_issues/' + id + '/validate', {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ validation_notes: notes || '' })
                });
                toast('Problemă validată și închisă', 'success');
                loadMyTasks();
            } catch (err) { toast('Eroare: ' + (err as Error).message, 'error'); }
            return;
        }
        const rej = target.closest('.issue-reject-btn') as HTMLElement | null;
        if (rej) {
            e.preventDefault();
            const id = rej.dataset.issueid;
            if (!id) return;
            const reason = await customPrompt('Motiv respingere:', '', 'Respingere');
            if (reason === null) return;
            try {
                await api('/api/machine_issues/' + id + '/reopen', {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reason: reason || '' })
                });
                toast('Problemă respinsă — revine la deschis', 'info');
                loadMyTasks();
            } catch (err) { toast('Eroare: ' + (err as Error).message, 'error'); }
            return;
        }
    }, { capture: false });
}

function _updateMyTasksHeaderBadges(badges: InsigneLucrari | null): void {
    const overdueEl = document.getElementById('mytasks-header-overdue');
    const inProgEl = document.getElementById('mytasks-header-inprogress');
    const pausedEl = document.getElementById('mytasks-header-paused');
    if (!badges) {
        if (overdueEl) overdueEl.style.display = 'none';
        if (inProgEl) inProgEl.style.display = 'none';
        if (pausedEl) pausedEl.style.display = 'none';
        return;
    }
    if (overdueEl) {
        if ((badges.overdue || 0) > 0) {
            overdueEl.textContent = '⚠ ' + badges.overdue + ' restante';
            overdueEl.style.display = '';
        } else {
            overdueEl.style.display = 'none';
        }
    }
    if (inProgEl) {
        if ((badges.in_progress || 0) > 0) {
            inProgEl.textContent = '▶ ' + badges.in_progress + ' în curs';
            inProgEl.style.display = '';
        } else {
            inProgEl.style.display = 'none';
        }
    }
    if (pausedEl) {
        if ((badges.paused || 0) > 0) {
            pausedEl.textContent = '⏸ ' + badges.paused + ' pauză';
            pausedEl.style.display = '';
        } else {
            pausedEl.style.display = 'none';
        }
    }
}

function _mapOperatorTaskToCard(t: RandOperatorTasks, source: string) {
    return {
        source,
        machine_id: t.machine_id,
        action_ro: t.description || '',
        line_id: t.line_id || '',
        interval_text: t.frequency || '',
        status: t.status,
        task_id: t.id,
        interval_id: t.interval_id,
        notes: t.notes,
        started_at: t.started_at,
        paused_at: t.paused_at,
        pause_reason: t.pause_reason,
        postpone_reason: t.postpone_reason,
        waiting_part_number: t.waiting_part_number,
        waiting_part_id: t.waiting_part_id,
        postponed_until: t.postponed_until,
    };
}

function _lineMatches(machineLineId: string, filterLine: string): boolean {
    if (!machineLineId || !filterLine) return false;
    if (machineLineId === filterLine) return true;
    return machineLineId.split(',').some(l => l.trim() === filterLine);
}

function _myTasksFilteredList<T extends FiltruLucrare>(tasks: T[]): T[] {
    const lineSel = document.getElementById('mytasks-line-filter');
    const machineSel = document.getElementById('mytasks-machine-filter');
    const cineSel = document.getElementById('mytasks-cine-filter');
    const lineVal = lineSel ? (lineSel as HTMLInputElement).value : '';
    const machineVal = machineSel ? (machineSel as HTMLInputElement).value : '';
    const cineVal = cineSel ? (cineSel as HTMLSelectElement).value : '';
    let filtered = tasks;
    // Part 2 (2026-05-15): exclude skipped (technical pause) din liste — apar separat ca info în calendar.
    filtered = filtered.filter(t => !t.skipped);
    if (machineVal) {
        filtered = filtered.filter(t => t.machine_id === machineVal);
    } else if (lineVal) {
        filtered = filtered.filter(t => {
            const ml = t.line_id || _getMachineLineId(t.machine_id);
            return _lineMatches(ml, lineVal);
        });
    }
    if (cineVal) {
        const eu = state.currentUser?.username || '';
        if (cineVal === 'alocate-de-mine') filtered = filtered.filter(t => t.assigned_by === eu);
        else if (cineVal === 'ale-mele') filtered = filtered.filter(t => t.assigned_to === eu);
    }
    return filtered;
}

/** Comută mașina din «pauză tehnică» în «oprire pentru mentenanță».
 *  Efectul e cel din services/technical_pause: starea 'maintenance' NU mai sare
 *  lucrările, deci tot ce era ascuns revine în planul zilei. (5 aug 2026) */
async function _markMaintenanceFromTasks(btn: HTMLElement): Promise<void> {
    const machineId = btn.dataset.machine || '';
    if (!machineId) return;
    const motiv = await askText('Fac mentenanță pe ' + machineId, {
        label: 'Ce lucrezi cât e oprită? (opțional)',
        multiline: true, required: false, okText: 'Marchează',
    });
    if (motiv === null) return;   // anulat
    const el = btn as HTMLButtonElement;
    el.disabled = true;
    const orig = el.textContent;
    el.textContent = '⏳ Se marchează…';
    try {
        const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/mark_maintenance_stop`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            credentials: 'include', body: JSON.stringify({ reason: motiv.trim() }),
        });
        if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error || 'Eroare');
        toast(`${machineId}: oprire pentru mentenanță — lucrările au revenit în plan`, 'success');
        _myTasksShowSkipped = false;
        loadMyTasks();
    } catch (e) {
        toast('Nu s-a putut marca: ' + (e as Error).message, 'error');
        el.disabled = false;
        el.textContent = orig;
    }
}

function _wireSkippedSectionButtons(listEl: HTMLElement, alsoOneTap = false): void {
    listEl.querySelectorAll('.mytask-mark-maint-btn').forEach(btn => {
        const el = btn as HTMLElement;
        if (el.dataset.wired === '1') return;
        el.dataset.wired = '1';
        el.addEventListener('click', () => _markMaintenanceFromTasks(el));
    });
    // Butoanele „✓ Am făcut" din secțiune sunt legate de bucla generală de mai
    // jos; le legăm noi doar când aceasta nu mai rulează (zi fără alte lucrări).
    if (!alsoOneTap) return;
    listEl.querySelectorAll('#mytasks-skipped-section .mytask-onetap-btn').forEach(btn => {
        const el = btn as HTMLElement;
        if (el.dataset.wired === '1') return;
        el.dataset.wired = '1';
        el.addEventListener('click', () => _myTaskOneTapComplete(el));
    });
}

// Part 2 (2026-05-15): listă SKIPPED-only pentru badge-uri "X sărite" pe calendar.
function _myTasksSkippedList(tasks: LucrarePlan[]) {
    const lineSel = document.getElementById('mytasks-line-filter');
    const machineSel = document.getElementById('mytasks-machine-filter');
    const lineVal = lineSel ? (lineSel as HTMLInputElement).value : '';
    const machineVal = machineSel ? (machineSel as HTMLInputElement).value : '';
    let skipped = (tasks || []).filter(t => t.skipped);
    if (machineVal) {
        skipped = skipped.filter(t => t.machine_id === machineVal);
    } else if (lineVal) {
        skipped = skipped.filter(t => {
            const ml = t.line_id || _getMachineLineId(t.machine_id);
            return _lineMatches(ml, lineVal);
        });
    }
    return skipped;
}

function _renderLoadingWithRetry(host: HTMLElement, msg: string = 'Se încarcă...'): void {
    host.innerHTML = `
        <div class="alarm-empty" style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:24px">
            <span class="icon">⏳</span>
            <span id="mytasks-loading-msg">${escHtml(msg)}</span>
            <button id="mytasks-loading-retry" class="btn btn-sm" style="display:none;padding:6px 14px;background:#6366f1;color:#fff;border:none;border-radius:8px;font-weight:600;cursor:pointer">🔄 Reîncearcă</button>
        </div>`;
}

function _withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('Timeout după ' + (ms / 1000) + 's: ' + label)), ms);
        promise.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
    });
}

export async function loadMyTasks() {
    const listEl = document.getElementById('mytasks-list');
    if (!listEl) return;
    _ensureIssueClickDelegate();
    const infoEl = document.getElementById('mytasks-user-info');
    const user = state.currentUser;
    if (infoEl && user) {
        const today = new Date().toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        infoEl.textContent = `${user.username} — ${today}`;
    }
    _renderLoadingWithRetry(listEl, 'Se încarcă lucrările tale...');

    if (!_myTasksFiltersReady) {
        await _ensureMachineCache();
        const machineSel = document.getElementById('mytasks-machine-filter');
        const lineSel = document.getElementById('mytasks-line-filter');
        if (machineSel && lineSel) {
            machineSel.innerHTML = '<option value="">Toate</option>';
            (_allMachinesCache || []).forEach(m => { machineSel.innerHTML += `<option value="${escHtml(m.id)}">${escHtml(m.id)}</option>`; });
            _populateLineSelect(lineSel);
            _linkLineToMachineSelect(lineSel, machineSel);
            lineSel.addEventListener('change', () => _renderMyTasks());
            machineSel.addEventListener('change', () => _renderMyTasks());
            _myTasksFiltersReady = true;
        }
        // audit UX 31 iul 2026: pentru inginer lista e de fapt planul fabricii —
        // titlu cinstit + filtru „cine" (alocate de mine / ale mele)
        const rol = state.currentUser?.role || 'operator';
        if ((rol === 'engineer' || rol === 'master') && machineSel && !document.getElementById('mytasks-cine-filter')) {
            const h2 = document.querySelector('#sec-mytasks .op-section-title');
            if (h2 && h2.childNodes.length) h2.childNodes[0].textContent = (temaD() ? '' : '🔧 ') + 'Plan zilnic fabrică ';
            const navLbl = document.querySelector('.nav-item[data-section="mytasks"] .nav-label');
            if (navLbl) navLbl.textContent = 'Plan zilnic fabrică';
            const cine = document.createElement('select');
            cine.id = 'mytasks-cine-filter';
            cine.className = (machineSel as HTMLSelectElement).className;
            cine.innerHTML = '<option value="">Toată fabrica</option>'
                + '<option value="alocate-de-mine">👤 Alocate de mine</option>'
                + '<option value="ale-mele">🔧 Ale mele</option>';
            machineSel.parentElement!.insertBefore(cine, machineSel.nextSibling);
            cine.addEventListener('change', () => _renderMyTasks());
        }
    }

    const retryBtn = document.getElementById('mytasks-loading-retry');
    const msgEl = document.getElementById('mytasks-loading-msg');
    const slowTimer = setTimeout(() => {
        if (msgEl) msgEl.textContent = 'Se încarcă... durează mai mult ca de obicei.';
    }, 8000);
    const offerRetry = () => {
        if (retryBtn) {
            retryBtn.style.display = '';
            retryBtn.addEventListener('click', () => loadMyTasks(), { once: true });
        }
        if (msgEl) msgEl.textContent = 'Încărcare prea lentă. Folosește butonul de mai jos.';
    };
    const offerTimer = setTimeout(offerRetry, 15000);

    try {
        const today = new Date();
        const startDate = localDateKey(today);
        const facutePromise: Promise<FacutAzi[]> = _withTimeout(api('/api/mytasks/facute_azi'), 15000, 'făcute azi').catch(() => []);
        const [allDays, overdue, postponed, inProgress, paused, activeWfs, openIssues, badges]: [Record<string, LucrarePlan[]>,
            LucrareRestanta[], LucrareAmanata[], RandOperatorTasks[], RandOperatorTasks[], LucrareInCurs[], ProblemaUtilaj[],
            InsigneLucrari | null] = await Promise.all([
            // Part 2 (2026-05-15): include_skipped=1 — primim TOATE inclusiv cele sărite (technical_pause)
            // pentru a putea afișa badge "X sărite" în calendar; _myTasksFilteredList le exclude din liste.
            _withTimeout(api('/api/mytasks/daily?start=' + startDate + '&days=10&include_skipped=1'), 15000, 'lucrări zilnice'),
            _withTimeout(api('/api/mytasks/overdue'), 15000, 'restante').catch(() => []),
            // Fix 11 mai 2026: counter mic „Amânate" arăta valoare diferită faţă de
            // banner-ul de jos. Cauza: counterul folosea `/api/operator_tasks?status=...`
            // (include și amânările EXPIRATE, fără filtru utilizator), iar banner-ul
            // folosea `/api/mytasks/postponed` (filtrat la postponed_until > azi + user).
            // Acum ambele consumă același endpoint → un singur număr.
            _withTimeout(api('/api/mytasks/postponed'), 15000, 'amânate').catch(() => []),
            _withTimeout(api('/api/operator_tasks?status=in_progress'), 15000, 'în lucru').catch(() => []),
            _withTimeout(api('/api/operator_tasks?status=paused'), 15000, 'pauză').catch(() => []),
            _withTimeout(Promise.all([
                api('/api/maintenance_workflows?status=paused').catch(() => []),
                api('/api/maintenance_workflows?status=in_progress').catch(() => []),
            ]).then(([p, c]) => [...(p || []), ...(c || [])]), 15000, 'workflow-uri deschise').catch(() => []),
            // și cele „în lucru” (12 oct 2026): „Începe” o trece în in_progress, iar fără ele dispărea din toată interfața
            // (M3 #10 și #18, butoanele de urgență, nevăzute din mai/iulie)
            _withTimeout(api('/api/machine_issues?status=open,in_progress,resolved'), 15000, 'probleme').catch(() => []),
            _withTimeout(api('/api/mytasks/badges'), 15000, 'badge-uri').catch(() => null),
        ]);
        clearTimeout(slowTimer);
        clearTimeout(offerTimer);
        _myTasksDailyCache = allDays || {};
        _myTasksDateCounts = {};
        for (const [ds, tasks] of Object.entries(_myTasksDailyCache)) {
            _myTasksDateCounts[ds] = (tasks || []).length;
        }
        _myTasksOverdue = overdue || [];
        _myTasksPostponed = postponed || [];
        _myTasksInProgress = inProgress || [];
        _myTasksPaused = paused || [];
        _myTasksActiveWfs = activeWfs || [];
        _myTasksOpenIssues = openIssues || [];
        _myTasksFacuteAzi = (await facutePromise) || [];
        _updateMyTasksHeaderBadges(badges);
        _renderMyTasks();
        _checkDueSoonAlert();
    } catch (e) {
        clearTimeout(slowTimer);
        clearTimeout(offerTimer);
        listEl.innerHTML = `
            <div class="alarm-empty" style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:24px">
                <span class="icon">⚠</span>
                <span>Eroare: ${escHtml((e as Error)?.message || String(e))}</span>
                <button class="btn btn-sm mytasks-retry-now" style="padding:6px 14px;background:#6366f1;color:#fff;border:none;border-radius:8px;font-weight:600;cursor:pointer">🔄 Reîncearcă</button>
            </div>`;
        listEl.querySelector('.mytasks-retry-now')?.addEventListener('click', () => loadMyTasks());
    }
}

export async function _showIssueTimeline(issueId: string): Promise<void> {
    let overlay = document.getElementById('issue-timeline-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'issue-timeline-overlay';
        overlay.className = 'detail-overlay';
        document.body.appendChild(overlay);
    }
    overlay.innerHTML = '<div class="detail-panel" style="max-width:640px"><div class="detail-header"><div class="detail-title-block"><span class="detail-line-id">Se încarcă...</span></div><button class="alarm-close" id="it-close">✕</button></div><div class="detail-content" id="it-body" style="padding:16px"><div class="typing-dots"><span></span><span></span><span></span></div></div></div>';
    overlay.classList.remove('hidden');
    document.getElementById('it-close')?.addEventListener('click', () => overlay!.classList.add('hidden'));
    try {
        const d: { issue: RandMachineIssues; events: EvenimentProblema[] } = await api('/api/machine_issues/' + issueId + '/timeline');
        const issue = d.issue || {};
        const events = d.events || [];
        const header = overlay.querySelector('.detail-title-block') as HTMLElement;
        if (header) header.innerHTML = `<span class="detail-line-id">Issue #${issue.id} — ${escHtml(issue.machine_id)}</span><span class="detail-line-product">${escHtml(issue.title || '')}</span>`;
        const colorByEvent: Record<string, string> = {
            'created': '#60a5fa', 'started': '#f59e0b', 'assigned': '#a78bfa',
            'resolved': '#00e676', 'validated': '#10b981', 'reopened': '#ef4444',
            'cancelled': '#6b7280',
        };
        const body = document.getElementById('it-body');
        if (!body) return;
        if (events.length === 0) {
            body.innerHTML = '<div style="color:var(--text-muted)">Nu există evenimente înregistrate.</div>';
            return;
        }
        body.innerHTML = '<div style="position:relative;padding-left:24px">' + events.map(ev => {
            const col = colorByEvent[ev.event_type] || '#888';
            const date = ev.created_at || '';
            return `<div style="position:relative;padding-bottom:14px;border-left:2px solid ${col};margin-left:8px;padding-left:14px">
                <div style="position:absolute;left:-7px;top:0;width:12px;height:12px;border-radius:50%;background:${col}"></div>
                <div style="font-size:.85rem;font-weight:700;color:${col}">${escHtml(ev.event_type.toUpperCase())}</div>
                <div style="font-size:.72rem;color:var(--text-muted)">${escHtml(date)} ${ev.actor ? '— ' + escHtml(ev.actor) : ''}</div>
                ${ev.from_status || ev.to_status ? `<div style="font-size:.75rem;color:var(--text-muted)">${escHtml(ev.from_status || '?')} → <strong>${escHtml(ev.to_status || '?')}</strong></div>` : ''}
                ${ev.note ? `<div style="font-size:.82rem;margin-top:4px;padding:6px 8px;background:var(--panel);border-radius:6px">${escHtml(ev.note)}</div>` : ''}
            </div>`;
        }).join('') + '</div>';
    } catch (e) {
        const body = document.getElementById('it-body');
        if (body) body.innerHTML = '<div style="color:#ef4444">Eroare: ' + escHtml((e as Error)?.message || String(e)) + '</div>';
    }
}


/* Work panel for resuming an in-progress issue.
 * Auto-promotes status from open → in_progress on first open, then displays:
 *   - issue header (machine, severity, reporter, date)
 *   - full description
 *   - chronological activity log (status changes + previously added notes)
 *   - inline form to append a new note
 *   - actions: Save note, Pause (in_progress → open), Resolve, Close.
 * Reuses the existing machine_issue_events table; notes are stored with event_type='note'. */
export async function _showIssueWorkPanel(issueId: string): Promise<void> {
    let overlay = document.getElementById('issue-work-overlay') as HTMLElement | null;
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'issue-work-overlay';
        overlay.className = 'detail-overlay';
        document.body.appendChild(overlay);
    }
    overlay.innerHTML = '<div class="detail-panel" style="max-width:760px"><div class="detail-header"><div class="detail-title-block"><span class="detail-line-id">Se încarcă...</span></div><button class="alarm-close" id="iw-close">✕</button></div><div class="detail-content" id="iw-body" style="padding:16px"><div class="typing-dots"><span></span><span></span><span></span></div></div></div>';
    overlay.classList.remove('hidden');
    const closeOverlay = () => overlay!.classList.add('hidden');
    document.getElementById('iw-close')?.addEventListener('click', closeOverlay);

    const fetchAndRender = async () => {
        const d: { issue: ProblemaUtilaj; events: EvenimentProblema[] } = await api('/api/machine_issues/' + issueId + '/details');
        const issue = d.issue || {};
        const events = d.events || [];
        const sevMeta = _issueSeverityMeta(issue.severity);
        const isInProgress = issue.status === 'in_progress';
        const reported = formatDateTimeRO(issue.reported_at);
        const startedLine = issue.started_at
            ? `<div style="font-size:.78rem;color:var(--text-muted)">Început: ${escHtml(formatDateTimeRO(issue.started_at))}${issue.assigned_to ? ' de ' + escHtml(issue.assigned_to) : ''}</div>`
            : '';
        const header = overlay!.querySelector('.detail-title-block') as HTMLElement;
        if (header) header.innerHTML = `<span class="detail-line-id">Lucrare #${issue.id} — ${escHtml(issue.machine_id)}${issue.machine_name ? ' • ' + escHtml(issue.machine_name) : ''}</span><span class="detail-line-product">${escHtml(issue.title || '')}</span>`;

        const colorByEvent: Record<string, string> = {
            'created': '#60a5fa', 'started': '#f59e0b', 'assigned': '#a78bfa',
            'note': '#94a3b8', 'paused': '#6366f1',
            'resolved': '#00e676', 'validated': '#10b981', 'reopened': '#ef4444',
            'cancelled': '#6b7280',
        };
        const labelByEvent: Record<string, string> = {
            'created': 'RAPORTAT', 'started': 'ÎNCEPUT', 'assigned': 'ATRIBUIT',
            'note': 'NOTĂ', 'paused': 'PAUZĂ',
            'resolved': 'REZOLVAT', 'validated': 'VALIDAT', 'reopened': 'REDESCHIS',
            'cancelled': 'ANULAT',
        };

        const timelineHtml = events.length === 0
            ? '<div style="color:var(--text-muted);font-size:.85rem">Nu există încă activitate înregistrată.</div>'
            : '<div style="position:relative;padding-left:8px">' + events.map(ev => {
                const col = colorByEvent[ev.event_type] || '#888';
                const lbl = labelByEvent[ev.event_type] || (ev.event_type || '').toUpperCase();
                const date = formatDateTimeRO(ev.created_at);
                return `<div style="position:relative;padding-bottom:10px;border-left:2px solid ${col};margin-left:6px;padding-left:14px">
                    <div style="position:absolute;left:-7px;top:0;width:12px;height:12px;border-radius:50%;background:${col}"></div>
                    <div style="font-size:.78rem;font-weight:700;color:${col}">${escHtml(lbl)}</div>
                    <div style="font-size:.7rem;color:var(--text-muted)">${escHtml(date)}${ev.actor ? ' — ' + escHtml(ev.actor) : ''}</div>
                    ${(ev.from_status || ev.to_status) && ev.event_type !== 'note' ? `<div style="font-size:.72rem;color:var(--text-muted)">${escHtml(ev.from_status || '?')} → <strong>${escHtml(ev.to_status || '?')}</strong></div>` : ''}
                    ${ev.note ? `<div style="font-size:.85rem;margin-top:4px;padding:6px 8px;background:var(--panel,rgba(255,255,255,.04));border-radius:6px;white-space:pre-wrap">${escHtml(ev.note)}</div>` : ''}
                </div>`;
            }).join('') + '</div>';

        const pauseBtnHtml = isInProgress
            ? `<button type="button" id="iw-pause" style="font-size:.85rem;padding:8px 14px;background:rgba(99,102,241,.15);color:#818cf8;border:1px solid rgba(99,102,241,.35);border-radius:8px;cursor:pointer;font-weight:600">⏸ Pauză</button>`
            : '';

        const body = document.getElementById('iw-body');
        if (!body) return;
        body.innerHTML = `
            <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:12px">
                <span class="issue-severity-badge issue-severity-${escHtml(issue.severity || 'medium')}" style="font-size:.78rem">${sevMeta.icon} ${sevMeta.label}</span>
                ${isInProgress ? '<span style="display:inline-block;padding:2px 8px;border-radius:8px;font-size:.72rem;font-weight:700;background:#f59e0b33;color:#f59e0b">▶ ÎN LUCRU</span>' : '<span style="display:inline-block;padding:2px 8px;border-radius:8px;font-size:.72rem;font-weight:700;background:#94a3b833;color:#94a3b8">DESCHIS</span>'}
                <span style="font-size:.78rem;color:var(--text-muted)">Raportat: ${escHtml(reported)}${issue.reported_by ? ' de ' + escHtml(issue.reported_by) : ''}</span>
            </div>
            ${startedLine}
            ${issue.description ? `<div style="margin:12px 0 16px;padding:10px 12px;background:var(--panel,rgba(255,255,255,.04));border-radius:8px;font-size:.9rem;line-height:1.5;white-space:pre-wrap">${escHtml(issue.description)}</div>` : ''}

            <div style="font-size:.85rem;font-weight:700;margin:14px 0 8px;color:var(--text-main);display:flex;align-items:center;gap:6px">
                <span>📜</span> Istoric activitate (${events.length})
            </div>
            <div id="iw-timeline" style="max-height:280px;overflow-y:auto;padding-right:6px">${timelineHtml}</div>

            <div style="font-size:.85rem;font-weight:700;margin:18px 0 6px;color:var(--text-main);display:flex;align-items:center;gap:6px">
                <span>✏️</span> Adaugă notă / progres
            </div>
            <textarea id="iw-note-input" rows="3" placeholder="Ce ai făcut acum? Ce-ai observat? Ce piesă lipsește?"
                style="width:100%;padding:10px 12px;font-size:.92rem;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.06);color:var(--text-main,#fff);resize:vertical;font-family:inherit;min-height:70px"></textarea>

            <div style="display:flex;flex-wrap:wrap;gap:8px;justify-content:flex-end;margin-top:14px">
                <button type="button" id="iw-cancel" style="font-size:.85rem;padding:8px 14px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border,#334155);border-radius:8px;cursor:pointer">Închide</button>
                ${pauseBtnHtml}
                <button type="button" id="iw-save-note" style="font-size:.9rem;padding:8px 16px;background:#3b82f6;color:#fff;border:none;border-radius:8px;cursor:pointer;font-weight:700">💾 Salvează nota</button>
                <button type="button" id="iw-resolve" style="font-size:.9rem;padding:8px 18px;background:#00e676;color:#000;border:none;border-radius:8px;cursor:pointer;font-weight:700">✓ Am rezolvat</button>
            </div>
        `;

        document.getElementById('iw-cancel')?.addEventListener('click', () => { closeOverlay(); loadMyTasks(); });
        document.getElementById('iw-save-note')?.addEventListener('click', async () => {
            const ta = document.getElementById('iw-note-input') as HTMLTextAreaElement | null;
            const scris = (ta?.value || '').trim();
            if (!scris) { toast('Scrie ceva în câmpul notă înainte de a salva.', 'info'); return; }
            const txt = await confirmaUnText('Nota ta', scris, 'problema-nota');
            try {
                await api('/api/machine_issues/' + issueId + '/notes', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note: txt })
                });
                toast('Notă salvată', 'success');
                if (ta) ta.value = '';
                await fetchAndRender();
            } catch (e) { toast('Eroare la salvare: ' + (e as Error).message, 'error'); }
        });
        document.getElementById('iw-pause')?.addEventListener('click', async () => {
            if (!await customConfirm('Pui lucrarea pe pauză? Va putea fi reluată oricând cu butonul ▶ Începe.', '⏸ Pauză lucrare')) return;
            try {
                await api('/api/machine_issues/' + issueId + '/pause', {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note: 'Lucru întrerupt manual' })
                });
                toast('Lucrare pe pauză', 'info');
                closeOverlay();
                loadMyTasks();
            } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
        document.getElementById('iw-resolve')?.addEventListener('click', async () => {
            const ta = document.getElementById('iw-note-input') as HTMLTextAreaElement | null;
            const draft = (ta?.value || '').trim();
            const scris = await customPrompt('Cum s-a rezolvat problema?', draft, 'Rezolvare problemă');
            if (scris === null) return;
            const notes = scris ? await confirmaUnText('Cum s-a rezolvat', scris, 'problema-rezolvata') : scris;
            const operatorName = state.currentUser?.username || '';
            try {
                await api('/api/machine_issues/' + issueId + '/resolve', {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ resolved_by: operatorName, resolution_notes: notes || '' })
                });
                toast('Problemă marcată ca rezolvată — necesită validare', 'success');
                closeOverlay();
                loadMyTasks();
            } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    };

    try {
        // Auto-promote open → in_progress on first open. If already in_progress, /start
        // returns 400 ("Nu se poate începe dintr-un status in_progress") which we ignore.
        try {
            await api('/api/machine_issues/' + issueId + '/start', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}'
            });
        } catch (e) { /* expected when status != open */ }
        await fetchAndRender();
    } catch (e) {
        const body = document.getElementById('iw-body');
        if (body) body.innerHTML = '<div style="color:#ef4444">Eroare: ' + escHtml((e as Error)?.message || String(e)) + '</div>';
    }
}


export async function _checkDueSoonAlert(): Promise<void> {
    try {
        const r: ScadenteInCurand = await api('/api/mytasks/due_soon?hours=24');
        const count = r?.count || 0;
        const existing = document.getElementById('due-soon-banner');
        if (existing) existing.remove();
        if (count === 0) return;
        // 29 sep 2026 (Dan): bannerul reapărea la fiecare intrare în Lucrările mele. Acum o dată
        // pe sesiune — din nou doar dacă numărul lucrărilor scadente a crescut.
        try {
            const vazut = parseInt(sessionStorage.getItem('fm_scadente24_vazut') || '0', 10);
            if (count <= vazut) return;
            sessionStorage.setItem('fm_scadente24_vazut', String(count));
        } catch (_e) { /* fără stocare: îl arătăm */ }
        const tasks = r.tasks || [];
        const machinesList = [...new Set(tasks.map(t => t.machine_id))].slice(0, 5).join(', ');
        const bar = document.createElement('div');
        bar.id = 'due-soon-banner';
        /* 4 sep 2026 (audit, mecanic simulat pe Pixel 7): la 480px lățime și
           top:10px bannerul acoperea TOATĂ bara de sus, inclusiv ☰, și înghițea
           atingerile — meniul nu se putea deschide până nu apăsai ×. Pe ecran
           îngust stă SUB bară, pe toată lățimea; oriunde, se închide singur după
           12 s (informația rămâne în insigna «restante»). */
        const ingust = window.innerWidth < 640;
        bar.style.cssText = (ingust
            ? 'position:fixed;top:64px;left:8px;right:8px;max-width:none;'
            : 'position:fixed;top:10px;right:16px;max-width:480px;animation:slideDown .4s ease-out;')   // slideDown are translateX(-50%) — pe lățime întreagă îl împingea în stânga
            + 'z-index:9999;padding:12px 18px;border-radius:10px;background:linear-gradient(135deg,#f59e0b,#d97706);color:#000;font-weight:600;font-size:.9rem;box-shadow:0 4px 20px rgba(0,0,0,.3);display:flex;align-items:center;gap:10px';
        setTimeout(() => { if (bar.isConnected) bar.remove(); }, 12000);
        bar.innerHTML = `
            <span style="font-size:1.3rem">⏰</span>
            <span style="flex:1">
                <strong>${count} ${count === 1 ? 'lucrare scadentă' : 'lucrări scadente'} în 24h</strong><br>
                <span style="font-size:.8rem;font-weight:500">${escHtml(machinesList)}${tasks.length > 5 ? '...' : ''}</span>
            </span>
            <button style="padding:6px 14px;min-height:44px;min-width:44px;border-radius:6px;border:2px solid #000;background:rgba(0,0,0,.15);color:#000;font-weight:700;font-size:.85rem;cursor:pointer" id="due-soon-goto">Vezi</button>
            <button style="padding:4px 8px;min-height:44px;min-width:44px;border:none;background:transparent;color:rgba(0,0,0,.6);font-size:1.3rem;cursor:pointer;line-height:1" id="due-soon-close" aria-label="Închide">&times;</button>`;
        document.body.appendChild(bar);
        document.getElementById('due-soon-close')?.addEventListener('click', () => bar.remove());
        // „Vezi" (29 sep 2026, Dan: „apăs și nu funcționează"): până acum doar reîncărca
        // lista pe care omul o avea deja în față. Acum arată CARE sunt lucrările din 24 h.
        document.getElementById('due-soon-goto')?.addEventListener('click', () => {
            bar.remove();
            _arataScadente24h(tasks);
        });
    } catch (e) { /* silent — nu blocăm încărcarea */ }
}

/** Lista lucrărilor scadente în 24 h, pe zile, cu drum spre ziua respectivă din plan. */
function _arataScadente24h(tasks: LucrareScadenta[]): void {
    document.getElementById('scadente24-dialog')?.remove();
    const peZi = new Map<string, LucrareScadenta[]>();
    tasks.forEach(t => {
        const zi = String(t.due_date || '').slice(0, 10) || todayStr();
        if (!peZi.has(zi)) peZi.set(zi, []);
        peZi.get(zi)!.push(t);
    });
    const numeZi = (zi: string) => zi === todayStr() ? 'Azi' : (zi === localDateKey(new Date(Date.now() + 86400000)) ? 'Mâine' : formatDateRO(zi));
    let corp = '';
    Array.from(peZi.keys()).sort().forEach(zi => {
        const lista = peZi.get(zi)!;
        corp += `<div class="scad24-zi"><div class="scad24-zi-cap"><strong>${escHtml(numeZi(zi))}</strong> · ${lista.length} ${lista.length === 1 ? 'lucrare' : 'lucrări'}
            <button type="button" class="btn btn-sm scad24-mergi" data-zi="${escHtml(zi)}">Deschide ziua ›</button></div><ul class="scad24-lista">`
            + lista.map(t => `<li><strong>${escHtml(t.machine_id || '')}</strong> — ${escHtml(truncate(t.description || '', 110))}</li>`).join('')
            + '</ul></div>';
    });
    const ov = document.createElement('div');
    ov.id = 'scadente24-dialog';
    ov.className = 'proc-overlay';
    ov.innerHTML = `<div class="proc-container scad24-cutie" role="dialog" aria-modal="true" aria-labelledby="scad24-titlu">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title" id="scad24-titlu">${tasks.length} ${tasks.length === 1 ? 'lucrare scadentă' : 'lucrări scadente'} în 24 h</h2>
            <button type="button" class="proc-close" data-scad24-inchide aria-label="Închide">&times;</button></div></div>
        <div class="scad24-corp">${corp}</div></div>`;
    document.body.appendChild(ov);
    const inchide = () => ov.remove();
    ov.addEventListener('click', e => {
        const t = e.target as HTMLElement;
        if (t === ov || t.closest('[data-scad24-inchide]')) { inchide(); return; }
        const b = t.closest('.scad24-mergi') as HTMLElement | null;
        if (!b) return;
        inchide();
        _myTasksTab = 'date';
        _myTasksSelectedDate = b.dataset.zi || todayStr();
        document.querySelectorAll('[data-mytasks-tab]').forEach(x => x.classList.remove('btn-active'));
        const sec = document.getElementById('sec-mytasks');
        if (sec && sec.classList.contains('active')) _renderMyTasks();
        else (document.querySelector('.nav-links a[data-section="mytasks"]') as HTMLElement | null)?.click();
    });
}

export function _buildDateStrip() {
    const strip = document.getElementById('mytasks-date-strip');
    if (!strip) return;
    strip.innerHTML = '';
    const today = new Date();
    const dayNames = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];
    for (let i = 0; i < 10; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        const dateStr = localDateKey(d);
        const dayNum = d.getDate();
        const dayName = i === 0 ? 'Azi' : dayNames[d.getDay()];
        const dayTasks = _myTasksDailyCache[dateStr] || [];
        const count = _myTasksFilteredList(dayTasks).length;
        const skippedCount = _myTasksSkippedList(dayTasks).length;
        const isActive = _myTasksSelectedDate === dateStr && _myTasksTab === 'date';
        const btn = document.createElement('button');
        btn.className = 'btn btn-sm mt-zi' + (isActive ? ' mt-zi-on' : '');
        // Part 2 — zilele cu DOAR task-uri sărite primesc clasa skipped-day (fundal gri estompat)
        const onlySkipped = count === 0 && skippedCount > 0;
        if (onlySkipped) btn.classList.add('skipped-day');
        btn.style.cssText = `min-width:56px;padding:6px 8px;border-radius:10px;text-align:center;flex-shrink:0;border:2px solid ${isActive ? 'var(--accent)' : 'var(--border)'};background:${isActive ? 'var(--accent)' : (onlySkipped ? 'rgba(120,120,120,.18)' : 'var(--surface)')};color:${isActive ? '#fff' : (onlySkipped ? 'var(--text-muted)' : 'var(--text-main)')};cursor:pointer;transition:all .2s`;
        // 5 aug 2026: chip-ul e clicabil — deschide direct lista lucrărilor sărite
        // a zilei, în loc să rămână un număr informativ într-o fundătură.
        const skippedHint = skippedCount > 0
            ? `<div class="mytasks-skipped-chip" data-date="${dateStr}" role="button" tabindex="0"
                 style="font-size:.6rem;color:${isActive ? '#fff' : '#f59e0b'};opacity:.95;text-decoration:underline;cursor:pointer;padding:2px 0"
                 title="Mașini oprite — apasă ca să vezi lucrările și să le poți face">⏸ ${skippedCount} săr.</div>`
            : '';
        btn.title = skippedCount > 0
            ? `${count} lucrări programate · ${skippedCount} sărite (mașini oprite) — apasă pe «${skippedCount} săr.» ca să le vezi`
            : `${count} lucrări programate`;
        btn.innerHTML = `<div style="font-size:.7rem;font-weight:600;text-transform:uppercase;opacity:.7">${dayName}</div>
            <div style="font-size:1.1rem;font-weight:700;line-height:1.2">${dayNum}</div>
            ${count > 0 ? `<div style="font-size:.65rem;font-weight:700;color:${isActive ? '#fff' : 'var(--accent)'}">${count}</div>` : (skippedCount > 0 ? '' : '<div style="font-size:.65rem;opacity:.4">—</div>')}
            ${skippedHint}`;
        btn.addEventListener('click', (ev) => {
            _myTasksTab = 'date';
            _myTasksSelectedDate = dateStr;
            // click pe „⏸ N săr." → deschide secțiunea lucrărilor oprite
            _myTasksShowSkipped = !!(ev.target as HTMLElement)?.closest('.mytasks-skipped-chip');
            document.querySelectorAll('[data-mytasks-tab]').forEach(b => b.classList.remove('btn-active'));
            _renderMyTasks();
            if (_myTasksShowSkipped) {
                document.getElementById('mytasks-skipped-section')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
        strip.appendChild(btn);
    }
}

export function _renderMyTasks() {
    const listEl = document.getElementById('mytasks-list');
    if (!listEl) return;
    // 1 nov 2026 (Dan): „Ce urmează pe linii” — starea mentenanțelor pe liniile alese, ca să le facă cât linia stă
    if (_myTasksTab === 'urmeaza') {
        _buildDateStrip();
        void randeazaCeUrmeaza(listEl);
        return;
    }

    const filteredOverdue = _myTasksFilteredList(_myTasksOverdue);
    const postponedMapped = _myTasksPostponed.map(t => ({
        source: 'postponed', machine_id: t.machine_id, action_ro: t.description || '',
        line_id: t.line_id || '', interval_text: t.frequency || '', status: t.status, task_id: t.id,
        postpone_reason: t.postpone_reason, waiting_part_number: t.waiting_part_number,
        waiting_part_id: t.waiting_part_id, postponed_until: t.postponed_until,
        interval_id: t.interval_id, notes: t.notes,
    }));
    const filteredPostponed = _myTasksFilteredList(postponedMapped);

    _buildDateStrip();

    const inProgressMapped = _myTasksInProgress.map(t => _mapOperatorTaskToCard(t, 'in_progress'));
    const filteredInProgress = _myTasksFilteredList(inProgressMapped);
    const pausedMapped = _myTasksPaused.map(t => _mapOperatorTaskToCard(t, 'paused'));
    const filteredPaused = _myTasksFilteredList(pausedMapped);

    // „Azi" (2026-07-14, cerut de user): programatele ZILEI fără restanțe.
    // După regula restanțelor 24h, o lunară ratată apare și la Restante (14 iun)
    // și în ziua curentă (aniversarea, 14 iul) — chip-ul separă cele două liste.
    const _overdueKeys = new Set(filteredOverdue.map(t => `${t.machine_id}|${t.interval_id || ''}`));
    const _todayNonOverdue = _myTasksFilteredList(_myTasksDailyCache[todayStr()] || [])
        .filter(t => !_overdueKeys.has(`${t.machine_id}|${t.interval_id || ''}`));
    // 26 oct 2026 (Dan: „lucrările făcute azi nu mai apar ca făcute”): ce s-a închis azi stă în «Făcute azi», sub
    // listă, cu cine și când — din jurnal, nu din lista zilei (după închidere scadența pleacă pe altă zi)
    const _esteAzi = _myTasksTab === 'today' || (_myTasksTab === 'date' && _myTasksSelectedDate === todayStr());
    const facuteAzi = _myTasksFilteredList(_myTasksFacuteAzi.map(f => ({ ...f, skipped: false, assigned_to: f.technician })));
    const _deFacutAzi = _todayNonOverdue.filter(t => !t.completed_today);

    let tasks: CardLucrare[], emptyMsg;
    if (_myTasksTab === 'overdue') {
        tasks = filteredOverdue;
        emptyMsg = 'Nicio lucrare restantă.';
    } else if (_myTasksTab === 'today') {
        tasks = _deFacutAzi;
        emptyMsg = 'Nicio lucrare programată azi (restanțele au tabul lor).';
    } else if (_myTasksTab === 'waiting') {
        tasks = filteredPostponed;
        emptyMsg = 'Nicio lucrare amânată.';
    } else if (_myTasksTab === 'inprogress') {
        tasks = filteredInProgress;
        emptyMsg = 'Nicio lucrare în curs.';
    } else if (_myTasksTab === 'paused') {
        tasks = filteredPaused;
        emptyMsg = 'Nicio lucrare în pauză.';
    } else {
        tasks = _ziuaFaraRestante(_myTasksSelectedDate);
        if (_esteAzi) tasks = tasks.filter(t => !(t as LucrarePlan).completed_today);
        emptyMsg = 'Nicio lucrare programată în această zi.';
    }

    // 11 sep 2026 (verificarea strictă cerută de Dan: «nu vreau lucrări dublate»).
    // Tabul «Azi» scotea restanțele din ziua curentă, dar vederea implicită pe
    // zi (cea cu care se deschide pagina, contorul «Lucrări: N») NU le scotea:
    // 16 lucrări pe ore, deja restante, apăreau și la «Restante», și în ziua de
    // azi — aceeași lucrare, două carduri, două contoare. Regula fabricii: o
    // lucrare neefectuată RĂMÂNE restantă până se face; nu se adună a doua.
    // Deci o lucrare aflată în restanțe nu mai apare și ca «programată» în
    // nicio zi — la închidere iese din restanțe și ciclul reia de la zero.
    function _ziuaFaraRestante(zi: string): LucrarePlan[] {
        return _myTasksFilteredList(_myTasksDailyCache[zi] || [])
            .filter(t => !_overdueKeys.has(`${t.machine_id}|${t.interval_id || ''}`));
    }

    const dailyTasks = _ziuaFaraRestante(_myTasksSelectedDate);
    const countDone = _myTasksSelectedDate === todayStr() ? facuteAzi.length : dailyTasks.filter(t => !!t.completed_today).length;
    const elDaily = document.getElementById('mytasks-count-daily');
    const elDone = document.getElementById('mytasks-count-done');
    const elOverdue = document.getElementById('mytasks-count-overdue');
    const elWaiting = document.getElementById('mytasks-count-waiting');
    const elInProgress = document.getElementById('mytasks-count-inprogress');
    const elPaused = document.getElementById('mytasks-count-paused');
    if (elDaily) elDaily.textContent = String(dailyTasks.length);
    if (elDone) elDone.textContent = String(countDone);
    const elToday = document.getElementById('mytasks-count-today');
    if (elToday) elToday.textContent = String(_deFacutAzi.length);
    if (elOverdue) elOverdue.textContent = String(filteredOverdue.length);
    if (elWaiting) elWaiting.textContent = String(filteredPostponed.length);
    listEl.innerHTML = '';

    const filteredActiveWfs = _myTasksActiveWfs.filter(wf =>
        _myTasksFilteredList([{ machine_id: wf.machine_id, line_id: wf.line_id || '', skipped: false }]).length > 0
    );
    // 29 sep 2026: fila „În pauză" arăta 0 cu trei fișe de lucru în pauză afișate chiar
    // dedesubt — număra doar sarcinile puse pe pauză, nu și fișele (workflow-urile).
    // Acum numără ambele, iar filele „În pauză" / „În curs" le și arată.
    const wfPauza = filteredActiveWfs.filter(wf => wf.status === 'paused');
    const wfInCurs = filteredActiveWfs.filter(wf => wf.status !== 'paused');
    if (elInProgress) elInProgress.textContent = String(filteredInProgress.length + wfInCurs.length);
    if (elPaused) elPaused.textContent = String(filteredPaused.length + wfPauza.length);
    _dProgresZi(_deFacutAzi.length, facuteAzi.length, filteredOverdue.length);

    const wfDeAratat = _myTasksTab === 'date' ? filteredActiveWfs
        : _myTasksTab === 'paused' ? wfPauza
        : _myTasksTab === 'inprogress' ? wfInCurs : [];
    if (wfDeAratat.length > 0) {
        const wfSection = document.createElement('div');
        wfSection.className = 'mt-sectiune mt-sectiune-reluare';
        wfSection.style.cssText = 'margin-bottom:16px';
        wfSection.innerHTML = `<div class="mt-sect-titlu" style="font-size:.9rem;font-weight:700;color:#f59e0b;margin-bottom:8px;display:flex;align-items:center;gap:6px">
            ${temaD() ? `Continuă de unde ai rămas <span class="mt-sect-nr">${wfDeAratat.length}</span>` : `<span>⏸</span> Lucrări în pauză (${wfDeAratat.length})`}
        </div>`;
        wfDeAratat.forEach(wf => {
            const card = document.createElement('div');
            card.className = 'mt-wf-card';
            card.style.cssText = 'padding:12px 16px;border-radius:10px;border:1px solid rgba(245,158,11,.4);background:rgba(245,158,11,.06);margin-bottom:8px;border-left:4px solid #f59e0b';
            // coloanele pot fi null: null > 0 e fals, deci rămâne ''
            const progress = (wf.steps_total as number) > 0 ? `${wf.steps_completed}/${wf.steps_total} pași` : '';
            const duration = (wf.duration_minutes as number) > 0 ? `${wf.duration_minutes} min` : '';
            const meta = [progress, duration].filter(Boolean).join(' · ');
            const statusLabel = wf.status === 'paused' ? `${sim('pause', '⏸')} ÎN PAUZĂ` : `${sim('play', '▶')} ÎN CURS`;
            const statusColor = wf.status === 'paused' ? '#f59e0b' : '#00e676';
            // pe tema D: poza utilajului și eticheta „În pauză · N zile" (machetele aprobate de Dan)
            const zile = wf.started_at ? Math.max(0, Math.floor((Date.now() - new Date(String(wf.started_at).replace(' ', 'T')).getTime()) / 86400000)) : 0;
            const etichetaD = wf.status === 'paused' ? `În pauză${zile ? ' · ' + zile + (zile === 1 ? ' zi' : ' zile') : ''}` : 'În curs';
            card.innerHTML = `
                <div class="mt-wf-rand" style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">
                    ${temaD() ? `<span class="task-photo-thumb mytask-photo-thumb mt-wf-poza" data-mid="${escHtml(wf.machine_id || '')}" data-needs-load="1" aria-label="Poză utilaj ${escHtml(wf.machine_id || '')}"></span>` : ''}
                    <div class="mt-wf-corp" style="flex:1;min-width:200px">
                        <div class="mt-wf-meta" style="font-size:.8rem;color:var(--text-muted)"><strong>${escHtml(wf.machine_id)}</strong>${wf.machine_name ? ' — ' + escHtml(wf.machine_name) : ''}
                            <span class="mt-b ${wf.status === 'paused' ? 'mt-b-pauza' : 'mt-b-incurs'}" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(245,158,11,.15);color:${statusColor};margin-left:6px">${temaD() ? sim(wf.status === 'paused' ? 'pause' : 'play', '') + ' ' + etichetaD : statusLabel}</span>
                        </div>
                        <div class="mt-wf-titlu" style="font-weight:600;font-size:.95rem;margin:4px 0">${escHtml((wf.action_ro || 'Lucrare mentenanță').substring(0, 120))}</div>
                        ${meta ? `<div class="mt-wf-sub" style="font-size:.8rem;color:var(--text-muted)">${escHtml(meta)}</div>` : ''}
                        ${wf.started_at ? `<div class="mt-wf-sub" style="font-size:.75rem;color:var(--text-muted)">Început: ${escHtml(formatDateTimeRO(wf.started_at))}</div>` : ''}
                    </div>
                    <div class="mt-wf-actiuni" style="display:flex;gap:6px;flex-shrink:0">
                        <button type="button" class="btn btn-sm wf-resume-btn mt-btn-primar" data-wfid="${wf.id}" data-mid="${escHtml(wf.machine_id)}" data-ivid="${wf.interval_id || ''}"
                            style="font-size:.9rem;padding:8px 18px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:8px;cursor:pointer">${temaD() ? sim('play', '') + ' Reia lucrarea' : '▶ Redeschide lucrarea'}</button>
                        <button type="button" class="btn btn-sm wf-cancel-btn mt-btn-anuleaza" data-wfid="${wf.id}" ${temaD() ? 'aria-label="Anulează lucrarea" title="Anulează lucrarea"' : ''}
                            style="font-size:.85rem;padding:8px 14px;background:rgba(239,68,68,.12);color:#ef4444;border:1px solid rgba(239,68,68,.3);border-radius:8px;cursor:pointer">${temaD() ? sim('x', '') : '✕ Anulează'}</button>
                    </div>
                </div>`;
            wfSection.appendChild(card);
        });
        listEl.appendChild(wfSection);

        listEl.querySelectorAll('.wf-resume-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const machineId = (btn as HTMLElement).dataset.mid;
                const ivId = parseInt((btn as HTMLElement).dataset.ivid || '0', 10);
                const wfId = parseInt((btn as HTMLElement).dataset.wfid || '0', 10);
                if (!ivId || !machineId) { toast('Date lipsă pentru reluare', 'error'); return; }
                try {
                    await _ensureMachineCache();
                    const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(machineId));
                    const iv = (intervals || []).find(x => Number(x.id) === ivId);
                    if (!iv) { toast('Intervalul nu a fost găsit', 'error'); return; }
                    const m = (_allMachinesCache || []).find(x => x.id === machineId);
                    const machineName = m ? (m.name || machineId) : machineId;
                    toast('Se redeschide procedura…');
                    openRevisionDetailView(iv, machineId, machineName, wfId);
                } catch (e) { toast('Eroare la reluare: ' + (e as Error).message, 'error'); }
            });
        });

        listEl.querySelectorAll('.wf-cancel-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const wfId = (btn as HTMLElement).dataset.wfid;
                if (!wfId) return;
                if (!await customConfirm('Sigur anulezi această lucrare în pauză?', 'Anulare lucrare')) return;
                try {
                    await api('/api/maintenance_workflow/' + wfId + '/cancel', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
                    toast('Lucrare anulată', 'info');
                    loadMyTasks();
                } catch (e) { toast('Eroare', 'error'); }
            });
        });
    }

    // Open issues section
    const filteredIssues = _myTasksFilteredList(
        _myTasksOpenIssues.map(i => ({ ...i, machine_id: i.machine_id, line_id: '' }))
    ).map(i => _myTasksOpenIssues.find(o => o.id === i.id) || i);
    // „în lucru” stă tot aici, cu eticheta „▶ ÎN LUCRU” și butonul „Continuă”
    const openIssues = filteredIssues.filter(i => i.status === 'open' || i.status === 'in_progress');
    const toValidate = filteredIssues.filter(i => i.status === 'resolved');
    const userRole = state.currentUser?.role || 'operator';
    const canValidate = userRole === 'master' || userRole === 'engineer';

    if (openIssues.length > 0 && _myTasksTab === 'date') {
        const issueSection = document.createElement('div');
        issueSection.className = 'mt-sectiune mt-sectiune-probleme';
        issueSection.style.cssText = 'margin-bottom:16px';
        issueSection.innerHTML = `<div class="mt-sect-titlu" style="font-size:.9rem;font-weight:700;color:#ef4444;margin-bottom:8px;display:flex;align-items:center;gap:6px">
            <span>${sim('octagon', '⚠')}</span> Probleme deschise (${openIssues.length})
        </div>`;
        openIssues.forEach(issue => {
            const sevMeta = _issueSeverityMeta(issue.severity);
            const card = document.createElement('div');
            card.className = 'issue-card';
            const reported = formatDateRO(issue.reported_at);
            const isInProgress = issue.status === 'in_progress';
            const assignedLabel = issue.assigned_to ? ' • asignat: ' + escHtml(issue.assigned_to) : '';
            const statusBadge = isInProgress
                ? '<span style="display:inline-block;padding:2px 8px;border-radius:8px;font-size:.72rem;font-weight:700;background:#f59e0b33;color:#f59e0b;margin-left:4px">▶ ÎN LUCRU</span>'
                : '';
            // Dynamic primary action: green "Continuă" if work already started, else amber "Începe"
            const primaryBtn = isInProgress
                ? `<button type="button" class="btn btn-sm issue-start-btn mt-btn-primar" data-issueid="${issue.id}"
                       style="font-size:.85rem;padding:6px 12px;background:#10b981;color:#000;font-weight:700;border:none;border-radius:8px;cursor:pointer">${sim('play', '▶')} Continuă</button>`
                : `<button type="button" class="btn btn-sm issue-start-btn mt-btn-primar" data-issueid="${issue.id}"
                       style="font-size:.85rem;padding:6px 12px;background:#f59e0b;color:#000;font-weight:700;border:none;border-radius:8px;cursor:pointer">${sim('play', '▶')} Începe</button>`;
            card.innerHTML = `
                <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">
                    <div style="flex:1;min-width:200px">
                        <div style="font-size:.8rem;color:var(--text-muted)">
                            <strong>${escHtml(issue.machine_id)}</strong>${issue.machine_name ? ' — ' + escHtml(issue.machine_name) : ''}
                            <span class="issue-severity-badge issue-severity-${issue.severity}">${sevMeta.icon} ${sevMeta.label}</span>
                            ${statusBadge}
                        </div>
                        <div style="font-weight:600;font-size:.95rem;margin:4px 0">${escHtml(issue.title)}</div>
                        ${issue.description ? `<div style="font-size:.85rem;color:var(--text-muted);margin-bottom:4px">${escHtml(issue.description).substring(0, 150)}</div>` : ''}
                        <div style="font-size:.75rem;color:var(--text-muted)">Raportat: ${escHtml(reported)}${issue.reported_by ? ' de ' + escHtml(issue.reported_by) : ''}${assignedLabel}</div>
                    </div>
                    <div style="display:flex;gap:6px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end">
                        ${primaryBtn}
                        <button type="button" class="btn btn-sm issue-resolve-btn mt-btn-secundar" data-issueid="${issue.id}"
                            style="font-size:.9rem;padding:8px 18px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:8px;cursor:pointer">${sim('check', '✓')} Am rezolvat</button>
                        <button type="button" class="btn btn-sm issue-timeline-btn mt-btn-secundar" data-issueid="${issue.id}"
                            style="font-size:.8rem;padding:6px 10px;background:rgba(96,165,250,.12);color:#60a5fa;border:1px solid rgba(96,165,250,.35);border-radius:8px;cursor:pointer">${sim('history', '📜')} Istoric</button>
                    </div>
                </div>`;
            issueSection.appendChild(card);
        });
        // Click handlers for .issue-start-btn / .issue-timeline-btn / .issue-resolve-btn
        // are wired via the document-level delegate (_ensureIssueClickDelegate),
        // so we only need to insert the cards into the DOM.
        listEl.appendChild(issueSection);
    }

    if (toValidate.length > 0 && canValidate && _myTasksTab === 'date') {
        const valSection = document.createElement('div');
        valSection.style.cssText = 'margin-bottom:16px';
        valSection.innerHTML = `<div style="font-size:.9rem;font-weight:700;color:#f59e0b;margin-bottom:8px;display:flex;align-items:center;gap:6px">
            <span>🔍</span> De validat (${toValidate.length})
        </div>`;
        toValidate.forEach(issue => {
            const card = document.createElement('div');
            card.className = 'issue-card resolved';
            card.innerHTML = `
                <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">
                    <div style="flex:1;min-width:200px">
                        <div style="font-size:.8rem;color:var(--text-muted)">
                            <strong>${escHtml(issue.machine_id)}</strong>${issue.machine_name ? ' — ' + escHtml(issue.machine_name) : ''}
                            <span style="display:inline-block;padding:2px 8px;border-radius:8px;font-size:.72rem;font-weight:700;background:#f59e0b33;color:#f59e0b">DE VALIDAT</span>
                        </div>
                        <div style="font-weight:600;font-size:.95rem;margin:4px 0">${escHtml(issue.title)}</div>
                        ${issue.resolution_notes ? `<div style="font-size:.85rem;color:var(--text-muted)">Rezolvare: ${escHtml(issue.resolution_notes).substring(0, 150)}</div>` : ''}
                        <div style="font-size:.75rem;color:var(--text-muted)">Rezolvat de: ${escHtml(issue.resolved_by || '?')}</div>
                    </div>
                    <div style="display:flex;gap:6px;flex-shrink:0">
                        <button type="button" class="btn btn-sm issue-validate-btn" data-issueid="${issue.id}"
                            style="font-size:.85rem;padding:8px 14px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:8px;cursor:pointer">✓ Validez</button>
                        <button type="button" class="btn btn-sm issue-reject-btn" data-issueid="${issue.id}"
                            style="font-size:.85rem;padding:8px 14px;background:rgba(239,68,68,.12);color:#ef4444;border:1px solid rgba(239,68,68,.3);border-radius:8px;cursor:pointer">✕ Respinge</button>
                    </div>
                </div>`;
            valSection.appendChild(card);
        });
        // Validate / reject buttons handled by document-level delegate.
        listEl.appendChild(valSection);
    }

    // ── Lucrări sărite pentru că mașina stă (5 aug 2026, cerut de Dan) ───────
    // Serverul le trimitea deja (include_skipped=1), dar erau doar NUMĂRATE pe
    // banda de zile — mecanicul nu putea intra în ele. Întrebarea lui Dan:
    // «mașina stă 3 zile, vreau să profit și să fac mentenanțe, dar nu apar».
    // Acum: secțiune pliabilă, cu execuție pe loc și scurtătura care comută
    // mașina din «pauză tehnică» în «oprire pentru mentenanță» (starea aia
    // readuce TOATE lucrările în plan, nu doar cele critice).
    const skippedForDay = _myTasksTab === 'date'
        ? _myTasksSkippedList(_myTasksDailyCache[_myTasksSelectedDate] || [])
        : [];
    if (skippedForDay.length > 0) {
        const byMachine = new Map<string, LucrarePlan[]>();
        skippedForDay.forEach(t => {
            const k = t.machine_id || '—';
            if (!byMachine.has(k)) byMachine.set(k, []);
            byMachine.get(k)!.push(t);
        });
        const sec = document.createElement('div');
        sec.id = 'mytasks-skipped-section';
        sec.style.cssText = 'margin-bottom:16px';
        const canMark = ['engineer', 'master'].includes(state.currentUser?.role || '');
        let html = `<details ${_myTasksShowSkipped ? 'open' : ''} style="border:1px solid rgba(245,158,11,.35);border-radius:10px;background:rgba(245,158,11,.05)">
            <summary style="cursor:pointer;padding:10px 14px;font-size:.9rem;font-weight:700;color:#f59e0b;min-height:44px;display:flex;align-items:center;gap:8px">
                ⏸ Oprite — se pot face cât stă mașina (${skippedForDay.length})
            </summary>
            <div style="padding:0 14px 12px">
                <div style="font-size:.82rem;color:var(--text-muted);margin:0 0 10px">
                    Mașinile de mai jos sunt oprite, așa că lucrările lor nu intră în planul zilei și nu devin restanțe.
                    Dacă te apuci de ele acum, le poți bifa direct de aici.
                </div>`;
        byMachine.forEach((lista, mid) => {
            const st = lista[0].skip_reason === 'inactive' ? 'linie nepornită' : 'pauză tehnică';
            const ore = lista[0].pause_info?.offline_duration_hours || 0;
            const durata = ore >= 24 ? `${Math.floor(ore / 24)} zile` : `${Math.round(ore)} h`;
            html += `<div style="margin-bottom:10px;padding:10px;border-radius:8px;background:rgba(255,255,255,.03);border:1px solid var(--border)">
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px">
                    <strong style="font-size:.92rem">${escHtml(mid)}</strong>
                    <span style="font-size:.75rem;padding:2px 8px;border-radius:10px;background:rgba(245,158,11,.15);color:#f59e0b">⏸ ${escHtml(st)}${ore ? ' · ' + escHtml(durata) : ''}</span>
                    <span style="font-size:.78rem;color:var(--text-muted)">${lista.length} lucrări</span>
                    ${canMark && lista[0].skip_reason !== 'inactive'
                        ? `<button type="button" class="btn btn-sm mytask-mark-maint-btn" data-machine="${escHtml(mid)}"
                             title="Marchează oprirea ca fiind pentru mentenanță — toate lucrările mașinii revin în planul zilnic"
                             style="margin-left:auto;background:#6366f1;color:#fff;border:none">🔧 Fac mentenanță acum</button>`
                        : ''}
                </div>`;
            lista.forEach(t => {
                const uid = `sk-${t.machine_id}-${t.interval_id || ''}`;
                html += `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--border)">
                    <div style="flex:1;min-width:180px">
                        <div style="font-size:.88rem">${escHtml(truncate(t.action_ro || '', 90))}</div>
                        <div style="font-size:.74rem;color:var(--text-muted)">${escHtml(t.interval_text || '')}${t.critical ? ' · <span style="color:#ef4444;font-weight:700">CRITIC</span>' : ''}</div>
                    </div>
                    <button type="button" class="btn mytask-onetap-btn" data-uid="${uid}" data-mid="${escHtml(t.machine_id)}"
                        data-ivid="${t.interval_id || ''}" data-action="${escHtml(t.action_ro || '')}" data-ih="${t.interval_hours || 0}"
                        data-taskid="${t.task_id || ''}" data-critical="${t.critical ? 1 : 0}" data-cat="${escHtml(t.category || '')}"
                        style="font-size:.9rem;font-weight:700;padding:10px 18px;min-height:44px;background:#00e676;color:#000;border:none;border-radius:10px">✓ Am făcut</button>
                </div>`;
            });
            html += '</div>';
        });
        html += '</div></details>';
        sec.innerHTML = html;
        listEl.appendChild(sec);
    }

    if (tasks.length === 0 && _myTasksActiveWfs.length === 0 && openIssues.length === 0
        && skippedForDay.length === 0) {
        listEl.innerHTML += `<div class="alarm-empty"><span class="icon">✅</span>${emptyMsg}</div>`;
        if (_esteAzi) _sectiuneFacuteAzi(listEl, facuteAzi);
        return;
    }
    if (tasks.length === 0) {
        _wireSkippedSectionButtons(listEl);
        if (_esteAzi) _sectiuneFacuteAzi(listEl, facuteAzi);
        return;
    }

    tasks.forEach(t => {
        const lineId = t.line_id || _getMachineLineId(t.machine_id);
        const lineLabel = lineId ? (_lineLabels[lineId] || lineId) : '—';
        const machineState = typeof getSensorValue === 'function' ? getSensorValue(t.machine_id as string, 'machine_state') : null;
        const isProducing = parseInt(String(machineState)) === 7;
        const isCritical = t.critical === 1;
        const isEngineer = t.source === 'engineer';

        const isCompleted = !!t.completed_today;

        let statusBadge = '';
        if (isCompleted) {
            statusBadge = `<span class="mt-b mt-b-facut" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#00e676;color:#000">${sim('checkCircle', '✅')} TERMINAT</span>`;
        } else if (t.status === 'waiting_parts') {
            statusBadge = '<span class="mt-b mt-b-atentie" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#f59e0b;color:#000">LIPSA PIESA</span>';
        } else if (t.status === 'postponed') {
            statusBadge = '<span class="mt-b mt-b-neutru" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#6366f1;color:#fff">AMÂNATĂ</span>';
        } else if ((t.overdue_days as number) > 0) {   // lipsește în afara restanțelor: undefined > 0 e fals
            const label = (t.overdue_days as number) >= 1 ? t.overdue_days + ' zile întârziere' : t.overdue_hours + 'h întârziere';
            statusBadge = `<span class="mt-b mt-b-intarziat" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:var(--red);color:#fff">${temaD() ? sim('clock', '') + ' ' : ''}${label}</span>`;
        }
        if (isEngineer) {
            statusBadge += ` <span class="mt-b mt-b-neutru" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#8b5cf6;color:#fff">Alocat de ${escHtml(t.assigned_by || 'inginer')}</span>`;
            if (t.priority === 'high') {
                statusBadge += ` <span class="mt-b mt-b-intarziat" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:var(--red);color:#fff">${sim('warn', '⚠')} URGENTĂ</span>`;
            }
        }
        // 8 aug 2026 (P8) — lucrare rămasă din momentul opririi. Cât mașina stă
        // nu mai apar altele; astea trebuie închise. Unele însă NU se pot face
        // cât mașina e oprită, iar mecanicul trebuie să știe ÎNAINTE să se ducă.
        if (t.inghetat) {
            statusBadge += ` <span title="Datorată la momentul opririi (${escHtml(t.inghetat_din || '')}). Cât mașina stă, nu apar lucrări noi." class="mt-b mt-b-info" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#0ea5e9;color:#fff">${sim('snow', '❄')} RĂMASĂ DE LA OPRIRE</span>`;
        }
        if (t.cere_masina_pornita) {
            statusBadge += ` <span title="${escHtml(t.cere_masina_pornita_motiv || 'Nu se poate executa pe mașina oprită')}" class="mt-b mt-b-atentie" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#f59e0b;color:#000">${sim('warn', '⚠')} CERE MAȘINA PORNITĂ</span>`;
        }
        if (t.amanata) {
            statusBadge += ` <span title="${escHtml(t.amanare_motiv || '')}${t.amanata_de ? ' — ' + escHtml(t.amanata_de) : ''}" class="mt-b mt-b-neutru" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#6366f1;color:#fff">AMÂNATĂ până la ${escHtml(t.amanata_pana_la || '')}</span>`;
        }

        let prodBanner = '';
        if (isProducing) {
            prodBanner = `<div class="mt-banda mt-banda-atentie" style="padding:6px 10px;border-radius:6px;background:rgba(249,115,22,.12);border:1px solid rgba(249,115,22,.3);font-size:.85rem;color:#f97316;margin-bottom:8px">
                <strong>${sim('warn', '⚠')} Linia în producție</strong> — solicită aprobare oprire de la șeful de producție</div>`;
        }

        let extraInfo = '';
        if (t.status === 'waiting_parts' && t.waiting_part_number) {
            extraInfo = `<div style="font-size:.85rem;color:var(--text-muted);margin-top:4px">Piesă așteptată: <strong>${escHtml(t.waiting_part_number)}</strong></div>`;
        }
        if (t.status === 'postponed' && t.postpone_reason) {
            extraInfo = `<div style="font-size:.85rem;color:var(--text-muted);margin-top:4px">Motiv: <em>${escHtml(t.postpone_reason)}</em>${t.postponed_until ? ' — reprogramat: ' + escHtml(formatDateRO(t.postponed_until)) : ''}</div>`;
        }
        if (t.last_service_date) {
            extraInfo += `<div style="font-size:.8rem;color:var(--text-muted);margin-top:2px">Ultimul serviciu: ${escHtml(formatDateRO(t.last_service_date))}</div>`;
        }
        // Ceasul propriu al mașinii (7 sep 2026). Utilajele SACMI numără singure
        // orele pe cadența asta și își resetează contorul DOAR când mecanicul
        // apasă Start/Stop pe HMI-ul mașinii. Verificat pe datele colectate:
        // nimeni nu apasă, deci mașina crede că nu s-a făcut nimic. Nu putem
        // apăsa noi — interfața SACMI e read-only — deci o cerem aici, unde
        // mecanicul citește lucrarea înainte să înceapă.
        extraInfo += _ceasMasinaHtml(t);
        // notele inginerului („piesa e în magazie, raftul 3") — se salvau, dar nu le vedea nimeni
        if (isEngineer && t.notes && String(t.notes).trim()) {
            extraInfo += `<div class="mytask-instructiuni" style="font-size:.88rem;margin-top:6px;padding:6px 10px;border-radius:8px;background:rgba(139,92,246,.08);border:1px solid rgba(139,92,246,.25)"><strong>Instrucțiuni${t.assigned_by ? ' de la ' + escHtml(t.assigned_by) : ''}:</strong> ${escHtml(String(t.notes).trim())}</div>`;
        }

        const partsInfo = [t.part_number, t.lubricant].filter(Boolean).join(' | ');
        const uid = t.task_id ? 'task-' + t.task_id : 'iv-' + (t.interval_id || 0);

        const needsParts = t.needs_parts === 1;
        const hasLinkedParts = t.has_linked_parts === true;
        let typeBadge = '';
        // o intervenție programată de inginer (fără interval din plan) nu e o „Verificare" —
        // pe 30 sep cardul „înlocuire garnitură" apărea marcat 👁 Verificare
        const esteInterventie = isEngineer && !t.interval_id;
        if (esteInterventie) {
            typeBadge = `<span class="mt-cat" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(139,92,246,.14);color:#a78bfa;border:1px solid rgba(139,92,246,.35)">${sim('pin', '📌')} Intervenție programată</span>`;
        } else if (needsParts) {
            typeBadge = `<span class="mt-cat" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(251,146,60,.15);color:#fb923c;border:1px solid rgba(251,146,60,.3)">${sim('swap', '🔧')} Schimb piese</span>`;
        } else {
            typeBadge = `<span class="mt-cat" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(96,165,250,.12);color:#60a5fa;border:1px solid rgba(96,165,250,.3)">${sim('eye', '👁')} Verificare</span>`;
        }

        const card = document.createElement('div');
        // Class-driven styling — vezi `#sec-mytasks .mytask-card` în style.css
        // (Task 3 — 11 mai 2026: carduri ~dublu pe tabletă)
        let cardCls = 'mytask-card';
        if (isCompleted) cardCls += ' mytask-card-completed';
        else if (!isCompleted && (t.overdue_days as number) > 0) cardCls += ' mytask-card-overdue';
        else if (isCritical) cardCls += ' mytask-card-critical';
        card.className = cardCls;

        const partsContainerId = 'parts-' + (t.interval_id || Math.random().toString(36).slice(2));

        card.innerHTML = `
            ${prodBanner}
            <div class="mytask-card-row">
                <div class="mytask-card-photo">
                    <span class="task-photo-thumb mytask-photo-thumb" data-mid="${escHtml(t.machine_id || '')}" data-needs-load="1" title="Se încarcă poza utilajului…" aria-label="Poză utilaj ${escHtml(t.machine_id || '')}">${sim('camera', '📷')}</span>
                </div>
                <div class="mytask-card-main">
                    <div class="mytask-card-meta">${escHtml(lineLabel)} — <strong>${escHtml(t.machine_id)}</strong> ${typeBadge} ${statusBadge} ${isCritical ? '<span class="mt-b mt-b-critic" style="color:var(--red);font-weight:700">CRITICĂ</span>' : ''}${t.respinsa ? ` <span class="mt-b mt-b-respinsa" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.75rem;font-weight:700;background:#7c3aed;color:#fff" title="Lucrarea a fost respinsă la validare — trebuie refăcută">↩ RESPINSĂ</span>` : ''}</div>
                    ${t.respinsa ? `<div class="mytask-respinsa" style="font-size:.82rem;color:#a78bfa;margin-top:2px">↩ Respinsă${t.respinsa.de ? ' de ' + escHtml(t.respinsa.de) : ''}${t.respinsa.la || t.respinsa.data ? ' pe ' + escHtml(formatDateRO(String(t.respinsa.la || t.respinsa.data).slice(0, 10))) : ''}${t.respinsa.motiv ? ': „' + escHtml(t.respinsa.motiv) + '”' : ''} — se reface</div>` : ''}
                    <div class="mytask-card-title">${escHtml(t.action_ro || 'Lucrare mentenanță')}</div>
                    ${t.interval_text && !esteInterventie ? `<div style="font-size:.85rem;color:var(--text-muted)">Interval: ${escHtml(t.interval_text)}${t.interval_hours ? ' (' + t.interval_hours + 'h)' : ''}</div>` : ''}
                    ${partsInfo ? `<div style="font-size:.85rem;color:var(--text-muted)">Piese: ${escHtml(partsInfo)}${t.quantity ? ' — Cantitate: ' + escHtml(t.quantity) : ''}</div>` : ''}
                    ${needsParts && t.interval_id ? `<div id="${partsContainerId}" class="mytask-parts-zone" style="margin-top:6px;font-size:.85rem" data-ivid="${t.interval_id}" data-mid="${escHtml(t.machine_id)}" data-loaded="false"></div>` : ''}
                    ${extraInfo}
                </div>
                <div class="mytask-card-actions" style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
                    ${t.document_id && t.page_number ? `<a href="/api/documents/${t.document_id}/view?page=${t.page_number}" target="_blank" class="btn btn-sm mt-btn-manual" style="font-size:.85rem;padding:6px 14px;background:rgba(0,180,255,.12);border:1px solid rgba(0,180,255,.35);color:#00b4ff;text-decoration:none;border-radius:8px">${sim('book', '📖')} Manual p.${t.page_number}</a>` : (t.document_id ? `<a href="/api/documents/${t.document_id}/view" target="_blank" class="btn btn-sm mt-btn-manual" style="font-size:.85rem;padding:6px 14px;background:rgba(0,180,255,.08);border:1px solid rgba(0,180,255,.25);color:#00b4ff;text-decoration:none;border-radius:8px">${sim('book', '📖')} Manual</a>` : '')}
                    ${isCompleted ? '' : `<button type="button" class="btn mytask-onetap-btn" data-uid="${uid}" data-mid="${escHtml(t.machine_id)}" data-ivid="${t.interval_id || ''}" data-action="${escHtml(t.action_ro || '')}" data-ih="${t.interval_hours || 0}" data-taskid="${t.task_id || ''}" data-critical="${isCritical ? 1 : 0}" data-cat="${escHtml(t.category || '')}" style="font-size:1.05rem;font-weight:800;padding:14px 26px;min-height:52px;background:#00e676;color:#000;border:none;border-radius:12px;box-shadow:0 2px 8px rgba(0,230,118,.35)">${sim('check', '✓')} Am făcut</button>`}
                    ${(!isCompleted && t.task_id && t.status === 'paused') ? `<button type="button" class="btn btn-sm mytask-resume-btn" data-taskid="${t.task_id}" style="font-size:.9rem;padding:10px 16px;min-height:44px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:8px">${sim('play', '▶')} ${temaD() ? 'Reia lucrarea' : 'Redeschide lucrarea'}</button>` : ''}
                    ${isCompleted ? '' : `<button type="button" class="btn btn-sm mytask-more-btn" aria-expanded="false" style="font-size:.9rem;padding:10px 14px;min-height:44px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border);border-radius:8px">${sim('more', '⋯')} Mai multe</button>`}
                    ${isCompleted ? '' : `<div class="mytask-more-actions" style="display:none;gap:8px;flex-wrap:wrap;width:100%;margin-top:8px;padding-top:8px;border-top:1px dashed var(--border)">
                        ${needsParts && !hasLinkedParts && t.document_id && t.page_number ? `<a href="/api/documents/${t.document_id}/view?page=${t.page_number}" target="_blank" class="btn btn-sm" style="font-size:.85rem;padding:8px 14px;min-height:44px;background:rgba(251,146,60,.1);border:1px solid rgba(251,146,60,.3);color:#fb923c;text-decoration:none;border-radius:8px">${sim('search', '🔍')} Caută piese în manual</a>` : ''}
                        <button type="button" class="btn btn-green btn-sm mytask-start-btn" data-uid="${uid}" data-mid="${escHtml(t.machine_id)}" data-ivid="${t.interval_id || ''}" data-action="${escHtml(t.action_ro || '')}" data-ih="${t.interval_hours || 0}" data-taskid="${t.task_id || ''}" style="font-size:.9rem;padding:10px 16px;min-height:44px">${sim('play', '▶')} Încep (procedură)</button>
                        <button type="button" class="btn btn-sm mytask-done-btn" data-uid="${uid}" data-mid="${escHtml(t.machine_id)}" data-ivid="${t.interval_id || ''}" data-action="${escHtml(t.action_ro || '')}" data-ih="${t.interval_hours || 0}" data-taskid="${t.task_id || ''}" data-critical="${isCritical ? 1 : 0}" data-cat="${escHtml(t.category || '')}" style="font-size:.9rem;padding:10px 16px;min-height:44px;background:rgba(0,230,118,.15);color:var(--text-main);border:1px solid rgba(0,230,118,.4);border-radius:8px">${sim('check', '✓')} Am făcut + notă</button>
                        ${(t.task_id && t.status === 'in_progress') ? `<button type="button" class="btn btn-sm mytask-pause-btn" data-taskid="${t.task_id}" style="font-size:.9rem;padding:10px 16px;min-height:44px;background:#6366f1;color:#fff;border:none;border-radius:8px">${sim('pause', '⏸')} Pauză (continuă mâine)</button>` : ''}
                        <button type="button" class="btn btn-sm mytask-postpone-btn" data-uid="${uid}" data-mid="${escHtml(t.machine_id)}" data-ivid="${t.interval_id || ''}" data-itext="${escHtml(t.interval_text || '')}" data-action="${escHtml(t.action_ro || '')}" data-origdate="${escHtml(t.due_date || _myTasksSelectedDate || todayStr())}" data-taskid="${t.task_id || ''}" style="font-size:.9rem;padding:10px 16px;min-height:44px;background:#6366f1;color:#fff;border:none;border-radius:8px">${sim('clock', '⏸')} Amân</button>
                        ${t.task_id ? `<button type="button" class="btn btn-sm mytask-handover-btn" data-taskid="${t.task_id}" data-mid="${escHtml(t.machine_id || '')}" title="Predă lucrarea unui coleg — se scrie în jurnal cine, cui și ce observații" style="font-size:.9rem;padding:10px 16px;min-height:44px;background:rgba(245,158,11,.15);color:#f59e0b;border:1px solid rgba(245,158,11,.4)">${sim('handover', '🔄')} Predă către…</button>` : ''}
                        ${t.task_id || t.interval_id ? `<button type="button" class="btn btn-sm mytask-timeline-btn" data-taskid="${t.task_id || ''}" data-mid="${escHtml(t.machine_id || '')}" data-ivid="${t.interval_id || ''}" title="Istoric amânări și activitate" style="font-size:.9rem;padding:10px 14px;min-height:44px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border);border-radius:8px">${sim('history', '🕐')} Istoric</button>` : ''}
                    </div>`}
                </div>
            </div>`;
        listEl.appendChild(card);
    });

    if (_esteAzi) _sectiuneFacuteAzi(listEl, facuteAzi);
    listEl.querySelectorAll('.mytask-onetap-btn').forEach(btn => {
        (btn as HTMLElement).dataset.wired = '1';
        btn.addEventListener('click', () => _myTaskOneTapComplete(btn as HTMLElement));
    });
    _wireSkippedSectionButtons(listEl);
    listEl.querySelectorAll('.mytask-more-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const el = btn as HTMLElement;
            const wrap = el.closest('.mytask-card-actions')?.querySelector('.mytask-more-actions') as HTMLElement | null;
            if (!wrap) return;
            const open = wrap.style.display !== 'none' && wrap.style.display !== '';
            wrap.style.display = open ? 'none' : 'flex';
            el.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
    });
    listEl.querySelectorAll('.mytask-start-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskStartFromCalendar(btn));
    });
    listEl.querySelectorAll('.mytask-done-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskQuickComplete(btn));
    });
    listEl.querySelectorAll('.mytask-postpone-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskPostponeFromCalendar(btn as HTMLElement));
    });
    listEl.querySelectorAll('.mytask-handover-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskHandover(btn as HTMLElement));
    });
    listEl.querySelectorAll('.mytask-pause-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskPauseQuick(btn as HTMLElement));
    });
    listEl.querySelectorAll('.mytask-resume-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskResumeQuick(btn as HTMLElement));
    });
    listEl.querySelectorAll('.mytask-timeline-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const el = btn as HTMLElement;
            const tid = parseInt(el.dataset.taskid || '0') || 0;
            const mid = el.dataset.mid || '';
            const ivid = parseInt(el.dataset.ivid || '0') || 0;
            _showPostponeTimeline(tid, mid, ivid);
        });
    });
    _loadPartsForVisibleCards(listEl);
    _loadMachinePhotosForVisibleCards(listEl);
    _checkPostponedPartsStock();
    _renderPostponedBanner();
}

/** «Făcute azi» (26 oct 2026, Dan): lucrările din plan închise azi, cu cine și când; «Anulează» pentru o apăsare
 *  greșită — lucrarea rămâne în jurnal ca anulată și se întoarce unde era (ex. la «Restante»). */
function _sectiuneFacuteAzi(listEl: HTMLElement, facute: FacutAzi[]): void {
    const sec = document.createElement('div');
    sec.className = 'mt-sectiune mt-sectiune-facute';
    sec.innerHTML = `<div class="mt-sectiune-titlu" style="display:flex;align-items:center;gap:8px;font-weight:700;margin:18px 0 8px;color:var(--text)">
            <span style="color:var(--green)">✓</span> Făcute azi <span class="op-tab-badge" style="background:var(--green);color:#fff">${facute.length}</span></div>
        ${facute.length ? facute.map(f => `<div class="mt-facut" data-log="${f.id}" style="display:flex;flex-wrap:wrap;align-items:center;gap:10px;justify-content:space-between;
                padding:10px 12px;border:1px solid var(--border);border-left:4px solid var(--green);border-radius:8px;margin-bottom:6px;background:var(--card-bg)">
            <div style="min-width:0;flex:1 1 260px">
                <div style="font-size:.85rem;color:var(--text-muted)">${escHtml(f.machine_name || f.machine_id)}${f.ora ? ` · ${escHtml(f.ora)}` : ''}${f.technician ? ` · ${escHtml(f.technician)}` : ''}${f.validation_status === 'pending' ? ' · la validare' : ''}</div>
                <div style="color:var(--text);text-decoration:none">${escHtml(truncate(f.action || '', 140))}</div>
            </div>
            ${f.poate_anula ? `<button type="button" class="btn btn-sm mt-anuleaza" data-log="${f.id}" style="min-height:44px;padding:8px 14px;background:transparent;border:1px solid var(--border);color:var(--text)">↶ Anulează</button>` : ''}
        </div>`).join('') : '<div style="color:var(--text-muted);font-size:.9rem;padding:4px 2px">Nimic închis azi încă.</div>'}`;
    listEl.appendChild(sec);
    sec.querySelectorAll<HTMLButtonElement>('.mt-anuleaza').forEach(b => b.addEventListener('click', async () => {
        const id = Number(b.dataset.log);
        const f = facute.find(x => x.id === id);
        if (!f) return;
        const motiv = await askText('Anulezi lucrarea închisă?', {
            label: `${f.machine_name || f.machine_id} — ${truncate(f.action || '', 120)}\nRămâne în jurnal ca anulată și se întoarce la lucrările de făcut.`,
            value: 'Am apăsat din greșeală', required: true, okText: 'Anulează lucrarea',
        });
        if (motiv == null) return;
        b.disabled = true;
        try {
            await api(`/api/mytasks/anuleaza/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ motiv }) });
            toast('↶ Anulată — lucrarea e din nou de făcut');
            loadMyTasks();
        } catch (e: unknown) {
            b.disabled = false;
            toast('Nu s-a anulat: ' + (e instanceof Error ? e.message : String(e)), 'error');
        }
    }));
}

/** Tema D: „Azi ai făcut X din Y" + restanțele, în capul ecranului (machetele aprobate pe 29 sep). */
function _dProgresZi(deFacut: number, facute: number, restante: number): void {
    const el = document.getElementById('d-progres-zi');
    if (!el || !temaD()) return;
    const total = deFacut + facute;          // 26 oct 2026: făcute = din jurnal (înainte: doar cele rămase în lista zilei)
    const pct = total ? Math.round(facute * 100 / total) : 0;
    el.innerHTML = `<div class="d-pz-rand">
            <span class="d-pz-et">Azi ai făcut</span><span class="d-pz-nr">${facute}</span><span class="d-pz-din">din ${total}</span>
            ${restante ? `<button type="button" class="d-pz-rest" data-mytasks-tab-link="overdue">${sim('clock', '')} ${restante} restante</button>` : ''}
        </div>
        <div class="d-pz-bara" role="progressbar" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${facute}" aria-label="Lucrări făcute azi"><div style="width:${pct}%"></div></div>`;
    el.querySelector('.d-pz-rest')?.addEventListener('click', () => {
        (document.getElementById('mytasks-tab-overdue') as HTMLElement | null)?.click();
    });
}

/* Încarcă pozele pentru toate `data-needs-load="1"` thumb-uri din container.
 * Folosește BATCH endpoint (`/api/config/machines/photos_batch`) ca să evite N+1.
 * 2026-05-11: adăugat pentru cardurile „Lucrările mele".
 */
async function _loadMachinePhotosForVisibleCards(container: HTMLElement): Promise<void> {
    const thumbs = Array.from(container.querySelectorAll('.mytask-photo-thumb[data-needs-load="1"]')) as HTMLElement[];
    if (thumbs.length === 0) return;
    const machineIds = Array.from(new Set(thumbs.map(t => t.dataset.mid || '').filter(Boolean)));
    if (machineIds.length === 0) return;
    let photosByMid: Record<string, LightboxPhoto[]> = {};
    try {
        photosByMid = await fetchMachinePhotosBatch(machineIds);
    } catch (e) {
        console.warn('[mytasks-photos] eroare batch:', e);
        return;
    }
    thumbs.forEach((thumb: HTMLElement) => {
        const mid = thumb.dataset.mid || '';
        thumb.dataset.needsLoad = '0';
        const photos = (photosByMid[mid] || []) as LightboxPhoto[];
        if (!photos || photos.length === 0) {
            thumb.classList.add('no-photo-placeholder');
            thumb.title = 'Fără poze încă';
            if (temaD()) thumb.innerHTML = sim('camera', '📷'); else thumb.textContent = '📷';
            // Click pe placeholder = no-op (vezi `attachPhotoThumbHandlers`)
            return;
        }
        thumb.classList.add('has-photo');
        thumb.title = `Deschide galerie (${photos.length} ${photos.length === 1 ? 'poză' : 'poze'})`;
        // pe tema D: poza de ansamblu a utilajului, nu plăcuța — după ea îl recunoaște operatorul
        const first = (temaD() && photos.find(p => p.photo_type === 'general')) || photos[0];
        const extra = photos.length > 1 ? `<span class="task-photo-count">+${photos.length - 1}</span>` : '';
        thumb.innerHTML = `<img src="${escHtml(first.thumb_url || first.url)}" alt="${escHtml(first.description || mid)}" loading="lazy" />${extra}`;
        thumb.addEventListener('click', (e) => {
            e.stopPropagation();
            openMachineLightbox(photos, 0);
        });
    });
}

async function _loadPartsForVisibleCards(container: HTMLElement) {
    const zones = container.querySelectorAll<HTMLElement>('.mytask-parts-zone[data-loaded="false"]');
    zones.forEach(async (zone: HTMLElement) => {
        const ivId = zone.dataset.ivid;
        const machineId = zone.dataset.mid;
        if (!ivId) return;
        zone.dataset.loaded = 'true';
        try {
            const parts: PiesaPotrivita[] = await api('/api/maintenance_intervals/' + ivId + '/matched_parts');
            if (!parts || parts.length === 0) {
                zone.innerHTML = `<div style="color:var(--text-muted);font-size:.8rem;padding:4px 0">
                    Fără piese asociate.
                    <button type="button" class="mytask-add-part-btn" data-ivid="${ivId}" data-mid="${escHtml(machineId || '')}" style="margin-left:6px;font-size:.78rem;padding:2px 10px;border-radius:6px;border:1px solid rgba(251,146,60,.4);background:rgba(251,146,60,.08);color:#fb923c;cursor:pointer">+ Adaugă piesă</button>
                </div>`;
            } else {
                // produsul echivalent (alt furnizor) stă pe rândul produsului pe care îl înlocuiește
                const grupuri = grupeazaEchivalente(parts);
                const topParts = grupuri.slice(0, 3);
                let html = '<div style="display:flex;flex-direction:column;gap:3px;padding:4px 0">';
                const linkPiesa = (x: PiesaPotrivita) => `<a href="#" class="mytask-part-link" data-pn="${escHtml(x.part_number || '')}" data-mid="${escHtml(machineId || '')}"${x.echivalent_conditie ? ` title="${escHtml(x.echivalent_conditie)}"` : ''} style="font-weight:600;color:#fb923c;text-decoration:underline;cursor:pointer">${escHtml(x.part_number || '—')}</a>`;
                topParts.forEach(({ p, alternative }: { p: PiesaPotrivita; alternative: PiesaPotrivita[] }) => {
                    const conf = p.confidence ? ` <span style="opacity:.5;font-size:.75rem">(${Math.round((p.confidence || 0) * 100)}%)</span>` : '';
                    const sau = alternative.map(a => ` <span style="color:var(--text-muted)">sau</span> ${linkPiesa(a)}`).join('');
                    html += `<div style="display:flex;align-items:center;flex-wrap:wrap;gap:6px;font-size:.82rem">
                        <span style="color:#fb923c">🔩</span>
                        ${linkPiesa(p)}
                        <span style="color:var(--text-muted)">${escHtml((p.description_ro || p.description_en || '').substring(0, 50))}</span>${conf}${sau}
                    </div>`;
                });
                if (grupuri.length > 3) {
                    html += `<div style="font-size:.78rem;color:var(--text-muted)">+${grupuri.length - 3} piese</div>`;
                }
                html += `<button type="button" class="mytask-add-part-btn" data-ivid="${ivId}" data-mid="${escHtml(machineId || '')}" style="align-self:flex-start;margin-top:2px;font-size:.78rem;padding:2px 10px;border-radius:6px;border:1px solid rgba(251,146,60,.4);background:rgba(251,146,60,.08);color:#fb923c;cursor:pointer">+ Adaugă piesă</button>`;
                html += '</div>';
                zone.innerHTML = html;
            }
            zone.querySelectorAll('.mytask-part-link').forEach(link => {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    _navigateToPartsCatalog((link as HTMLElement).dataset.pn!, (link as HTMLElement).dataset.mid!);
                });
            });
            zone.querySelectorAll('.mytask-add-part-btn').forEach(btn => {
                btn.addEventListener('click', () => _openAddPartDialog((btn as HTMLElement).dataset.ivid!, (btn as HTMLElement).dataset.mid!));
            });
        } catch (_e) {
            zone.innerHTML = '';
        }
    });
}

function _navigateToPartsCatalog(partNumber: string, machineId: string) {
    const partsNavLink = document.querySelector('.nav-links a[data-section="parts"]') as HTMLElement;
    if (partsNavLink) partsNavLink.click();

    const doSearch = () => {
        const tabBtn = document.querySelector('[data-tab="parts-tab-catalog"]');
        if (tabBtn) (tabBtn as HTMLElement).click();

        setTimeout(() => {
            const searchEl = $('#parts-search') as HTMLInputElement;
            const machineEl = $('#parts-machine') as HTMLSelectElement;
            if (machineEl) machineEl.value = machineId || '';
            if (searchEl && partNumber) { searchEl.value = partNumber; }
            state.partsPage = 1;
            const loadBtn = $('#parts-btn') as HTMLButtonElement;
            if (loadBtn) loadBtn.click();
        }, 150);
    };

    setTimeout(doSearch, 300);
}

async function _openAddPartDialog(intervalId: string, machineId: string) {
    let existing = document.getElementById('mytask-addpart-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'mytask-addpart-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
    <div class="proc-container" style="max-width:500px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">+ Adaugă piesă la mentenanță</h2>
            <button class="proc-close" id="addpart-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:16px">
            <div style="margin-bottom:10px;font-size:.9rem;color:var(--text-muted)">
                Mașina: <strong>${escHtml(machineId)}</strong>
            </div>
            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Caută piesă:</label>
                <input type="text" id="addpart-search" placeholder="Caută după cod sau descriere..." style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
            </div>
            <div id="addpart-results" style="max-height:300px;overflow-y:auto;border:1px solid var(--border);border-radius:8px;font-size:.85rem"></div>
            <div id="addpart-status" style="margin-top:8px;font-size:.85rem;text-align:center"></div>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    document.getElementById('addpart-close')!.addEventListener('click', () => overlay.remove());

    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    const searchInput = document.getElementById('addpart-search') as HTMLInputElement;
    const resultsDiv = document.getElementById('addpart-results')!;

    searchInput.addEventListener('input', () => {
        clearTimeout(searchTimer as ReturnType<typeof setTimeout>);   // null la prima tastă: clearTimeout(null) nu face nimic
        searchTimer = setTimeout(async () => {
            const q = searchInput.value.trim();
            if (q.length < 2) { resultsDiv.innerHTML = '<div style="padding:10px;color:var(--text-muted)">Introdu minim 2 caractere...</div>'; return; }
            resultsDiv.innerHTML = '<div style="padding:10px;color:var(--text-muted)">Se caută...</div>';
            try {
                const parts: PiesaCautata[] = await api('/api/parts/search?machine_id=' + encodeURIComponent(machineId) + '&q=' + encodeURIComponent(q) + '&limit=20');
                if (!parts || parts.length === 0) {
                    resultsDiv.innerHTML = '<div style="padding:10px;color:var(--text-muted)">Nicio piesă găsită.</div>';
                    return;
                }
                resultsDiv.innerHTML = '';
                parts.forEach(p => {
                    const row = document.createElement('div');
                    row.style.cssText = 'padding:8px 10px;border-bottom:1px solid var(--border);cursor:pointer;display:flex;align-items:center;gap:8px;transition:background .15s';
                    row.innerHTML = `<div style="flex:1"><strong>${escHtml(p.part_number || '—')}</strong> — ${escHtml((p.description_ro || p.description_en || '').substring(0, 60))}</div>
                        <button class="btn btn-sm" style="font-size:.78rem;padding:3px 10px;background:#22c55e;color:#fff;border:none;border-radius:6px;flex-shrink:0">+ Adaugă</button>`;
                    row.querySelector('button')!.addEventListener('click', async (e) => {
                        e.stopPropagation();
                        const statusDiv = document.getElementById('addpart-status')!;
                        try {
                            await api('/api/maintenance_intervals/' + intervalId + '/add_part_operator', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ part_id: p.id, notes: 'Adăugat de operator din interfață' })
                            });
                            statusDiv.innerHTML = '<span style="color:#22c55e">✅ Piesa a fost adăugată!</span>';
                            toast('Piesa ' + (p.part_number || '') + ' adăugată la mentenanță.');
                        } catch (err) {
                            statusDiv.innerHTML = '<span style="color:var(--red)">Eroare: ' + escHtml((err as Error).message || '') + '</span>';
                        }
                    });
                    resultsDiv.appendChild(row);
                });
            } catch (err) {
                resultsDiv.innerHTML = '<div style="padding:10px;color:var(--red)">Eroare: ' + escHtml((err as Error).message || '') + '</div>';
            }
        }, 400);
    });

    searchInput.focus();
}

export async function _myTaskStartFromCalendar(btn: Element) {
    const machineId = (btn as HTMLElement).dataset.mid;
    const intervalId = parseInt((btn as HTMLElement).dataset.ivid!, 10) || 0;
    const machineState = typeof getSensorValue === 'function' ? getSensorValue(machineId!, 'machine_state') : null;
    const isProducing = parseInt(String(machineState)) === 7;

    if (isProducing) {
        const approved = await askConfirm(
            '⚠ Linia este în PRODUCȚIE!',
            'Ai primit aprobare de la șeful de producție pentru oprire?\n\n'
            + 'Dacă nu, lucrarea se amână automat.',
            'Am aprobare');
        if (!approved) {
            _myTaskPostponeFromCalendar(btn as HTMLElement);
            return;
        }
    }

    if (intervalId) {
        try {
            await _ensureMachineCache();
            const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(machineId!));
            const iv = (intervals || []).find(x => Number(x.id) === intervalId);
            if (!iv) {
                toast('Nu s-a găsit intervalul de mentenanță pentru acest utilaj. Reîncărcați pagina sau contactați administratorul.', 'error');
                return;
            }
            const m = (_allMachinesCache || []).find(x => x.id === machineId);
            const machineName = m ? (m.name || machineId) : machineId;
            toast('Se deschide procedura reviziei…');
            openRevisionDetailView(iv, machineId!, machineName!);
        } catch (e) {
            toast('Eroare: ' + (e as Error).message, 'error');
        }
    } else {
        const fakeBtn = { dataset: { machine: machineId, action: (btn as HTMLElement).dataset.action, interval: (btn as HTMLElement).dataset.ih, ivid: (btn as HTMLElement).dataset.ivid || '', taskid: (btn as HTMLElement).dataset.taskid || '' } };
        openModal(fakeBtn);
    }
}

/**
 * B1 (2026-07-07): one-tap "✓ Am făcut" directly on the card — no overlay.
 * Writes maintenance_log immediately (advances the clock so the task doesn't
 * reappear). Non-critical routine work is auto-validated server-side. The full
 * note/photo overlay stays available under "⋯ Mai multe" (_myTaskQuickComplete).
 */
export async function _myTaskOneTapComplete(btn: HTMLElement): Promise<void> {
    const el = btn as HTMLButtonElement;
    if (el.dataset.busy === '1') return;
    const machineId = el.dataset.mid || '';
    const action = el.dataset.action || '';
    const intervalH = parseInt(el.dataset.ih || '0') || 0;
    const taskId = el.dataset.taskid || '';
    const isCritical = el.dataset.critical === '1';
    const category = el.dataset.cat || '';

    // 26 oct 2026 (Dan): „când dai «Am făcut» direct să te întrebe dacă ești sigur, iar confirmarea să nu fie direct
    // bifată” — un mecanic a închis din greșeală o lucrare care a dispărut de pe ecranul lui
    const numeUtilaj = (_allMachinesCache || []).find(m => m.id === machineId)?.name || machineId;
    el.dataset.busy = '1';
    const sigur = await askConfirmCuBifa('Ai făcut lucrarea?', `${numeUtilaj}\n${action}`,
        'Da, am făcut-o acum', 'Am făcut');
    if (!sigur) { el.dataset.busy = '0'; return; }
    // Colegul AI (31 oct 2026): „Am făcut” fără nimic scris — o singură întrebare scurtă despre lucrare
    const ivid = parseInt(el.dataset.ivid || '', 10) || null;
    const coleg = await colegIntreaba({ interval_id: ivid, machine_id: machineId, notes: '' });
    const origLabel = el.textContent;
    el.disabled = true;
    el.textContent = '⏳ Se salvează…';
    const hoursNow = state.machineHours[machineId] || 0;
    const operator = state.currentUser?.username || 'operator';
    // Corectura scrierii (1 nov 2026, Dan): răspunsul la întrebarea colegului se vede corectat și se aprobă
    const notaOneTap = await confirmaUnText('Ce ai răspuns', notaCuRaspuns('', coleg), 'am-facut');

    try {
        const rez: LucrareScrisa = await api('/api/maintenance_log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                machine_id: machineId,
                action: action,
                date: todayStr(),
                technician: operator,
                hours_at_service: hoursNow,
                next_due_hours: intervalH > 0 ? hoursNow + intervalH : null,
                notes: notaOneTap,
                coleg: coleg,
                parts_used: '',
                category: category,
                critical: isCritical ? 1 : 0,
                // 3 aug 2026: one-tap NU trimitea interval_id (deși butonul îl
                // avea) — contorul se reseta doar prin potrivirea de text, care
                // ratează intervalele cu același prefix dar cadențe diferite,
                // iar lucrarea apărea «nelegată» în compliance
                interval_id: ivid,
                // 8 aug 2026 (P5): task_id merge ÎN ACEEAȘI cerere — serverul
                // scrie logul și închide task-ul într-o singură tranzacție.
                // Înainte se trimitea un al doilea PUT „trimite și uită": dacă
                // pica, lucrarea era în jurnal iar task-ul rămânea deschis, deci
                // mecanicul o făcea a doua oară. Și log_id nu se scria niciodată.
                task_id: taskId ? parseInt(taskId, 10) : null,
                source: 'quick_log',
                status: 'completed',   // consemnare «am făcut» = lucrare terminată (4 sep 2026)
            })
        });
        toast('✅ Marcat ca făcut' + colegDupaInchidere(rez, isCritical)
            + ' — îl vezi la «Azi → Făcute azi», de unde îl poți anula');
        loadMyTasks();
    } catch (e: unknown) {
        toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
        el.dataset.busy = '0';
        el.disabled = false;
        el.textContent = origLabel;
    }
}

export async function _myTaskQuickComplete(btn: Element): Promise<void> {
    const machineId = (btn as HTMLElement).dataset.mid;
    const action = (btn as HTMLElement).dataset.action || '';
    const intervalH = parseInt((btn as HTMLElement).dataset.ih!) || 0;
    const taskId = (btn as HTMLElement).dataset.taskid || '';
    const isCritical = (btn as HTMLElement).dataset.critical === '1';
    const category = (btn as HTMLElement).dataset.cat || '';

    let overlay = document.getElementById('quick-complete-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'quick-complete-overlay';
        overlay.className = 'detail-overlay';
        overlay.innerHTML = `
            <div class="detail-panel" style="max-width:500px">
                <div class="detail-header">
                    <div class="detail-title-block">
                        <span class="detail-line-id" id="qc-machine"></span>
                        <span class="detail-line-product" id="qc-action"></span>
                    </div>
                    <button class="alarm-close" id="qc-close">✕</button>
                </div>
                <div class="detail-content" style="padding:20px">
                    <div style="text-align:center;font-size:2.5rem;margin-bottom:10px">✅</div>
                    <h3 style="text-align:center;margin-bottom:16px;color:var(--green)">Marchează ca terminată</h3>
                    <div class="urg-field">
                        <label class="urg-label">Note (opțional)</label>
                        <textarea id="qc-notes" rows="2" class="urg-textarea" placeholder="Ce ai observat, ce ai făcut..."></textarea>
                    </div>
                    <div style="display:flex;gap:10px;margin-top:16px;justify-content:center">
                        <button class="btn btn-green" id="qc-confirm" style="font-size:1rem;padding:10px 30px;font-weight:700">✓ GATA, AM TERMINAT</button>
                        <button class="btn btn-outline" id="qc-cancel" style="font-size:.9rem;padding:10px 20px">Anulează</button>
                    </div>
                    <div id="qc-status" style="margin-top:10px;text-align:center"></div>
                </div>
            </div>`;
        document.body.appendChild(overlay);
        overlay.querySelector('#qc-close')?.addEventListener('click', () => overlay!.classList.add('hidden'));
        overlay.querySelector('#qc-cancel')?.addEventListener('click', () => overlay!.classList.add('hidden'));
    }

    overlay.querySelector('#qc-machine')!.textContent = machineId as string;
    overlay.querySelector('#qc-action')!.textContent = action;
    (overlay.querySelector('#qc-notes') as HTMLTextAreaElement).value = '';
    overlay.querySelector('#qc-status')!.innerHTML = '';
    overlay.classList.remove('hidden');

    const confirmBtn = overlay.querySelector('#qc-confirm') as HTMLButtonElement;
    const newBtn = confirmBtn.cloneNode(true) as HTMLButtonElement;
    confirmBtn.parentNode!.replaceChild(newBtn, confirmBtn);

    newBtn.addEventListener('click', async () => {
        newBtn.disabled = true;
        newBtn.textContent = '⏳ Se salvează...';
        const notaScrisa = (overlay!.querySelector('#qc-notes') as HTMLTextAreaElement)?.value?.trim() || '';
        const ivid = parseInt((btn as HTMLElement).dataset.ivid || '', 10) || null;
        // Colegul AI (31 oct 2026): dacă nota nu spune nimic despre lucrare, o singură întrebare
        const coleg = await colegIntreaba({ interval_id: ivid, machine_id: machineId, notes: notaScrisa });
        // Corectura scrierii (1 nov 2026, Dan): nota (cu răspunsul colegului) se vede corectată și se aprobă
        const notes = await confirmaUnText('Ce ai scris la lucrare', notaCuRaspuns(notaScrisa, coleg), 'am-facut-nota');
        const hoursNow = state.machineHours[machineId!] || 0;
        const operator = state.currentUser?.username || 'operator';

        try {
            const qcRes: LucrareScrisa = await api('/api/maintenance_log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    machine_id: machineId,
                    action: action,
                    date: todayStr(),
                    technician: operator,
                    hours_at_service: hoursNow,
                    next_due_hours: intervalH > 0 ? hoursNow + intervalH : null,
                    interval_id: ivid,
                    notes: notes,
                    coleg: coleg,
                    parts_used: '',
                    category: category,
                    critical: isCritical ? 1 : 0,
                    // vezi P5 mai sus — o singură cerere, o singură tranzacție
                    task_id: taskId ? parseInt(taskId, 10) : null,
                    source: 'quick_log',
                })
            });

            toast('Lucrare marcată ca terminată! ✅' + colegDupaInchidere(qcRes));
            overlay!.classList.add('hidden');
            loadMyTasks();
            // Coleg AI: if the mechanic wrote observations, they're worth a
            // lesson — offer to save one (fire-and-forget, no flow blocking).
            if (notes) {
                colleagueOfferLesson({
                    context_type: 'log', log_id: (qcRes && qcRes.id) || 0,
                    machine_id: machineId, actions: action, notes: notes,
                }).catch(() => {});
            }
        } catch (e: unknown) {
            toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
            newBtn.disabled = false;
            newBtn.textContent = '✓ GATA, AM TERMINAT';
        }
    });
}

/** `dataTinta` — data propusă din capul locului (ex. ziua pe care s-a tras cardul). */
export function _myTaskPostponeFromCalendar(btn: { dataset: Record<string, string | undefined> }, dataTinta?: string) {
    const machineId = btn.dataset.mid || '';
    const intervalId = parseInt(btn.dataset.ivid as string) || 0;   // lipsă → NaN → 0
    const intervalText = btn.dataset.itext || '';
    const actionRo = btn.dataset.action || '';
    const originalDate = btn.dataset.origdate || _myTasksSelectedDate || todayStr();
    const taskId = parseInt(btn.dataset.taskid as string) || 0;

    const maxDefaultDays = _intervalTextToDays(intervalText);

    let existing = document.getElementById('mytask-postpone-overlay');
    if (existing) existing.remove();

    const today = new Date();
    const defaultUntil = new Date(today.getTime() + maxDefaultDays * 86400000);
    // 🌙 Ziua LOCALĂ, nu UTC (audit 2026-08-19, constatarea 2.7): aceeași capcană ca la
    // înregistrarea urgențelor — în tura de noapte (00:00–03:00 local) data propusă pentru amânare
    // ieșea cu o zi mai devreme decât intervalul calculat.
    const defaultUntilStr = dataTinta || localDateKey(defaultUntil);

    const reasonOptions = POSTPONE_REASONS.map(r =>
        `<option value="${escHtml(r.code)}">${escHtml(r.label)}</option>`
    ).join('');

    const overlay = document.createElement('div');
    overlay.id = 'mytask-postpone-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
    <div class="proc-container" style="max-width:520px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">📅 Amânare mentenanță</h2>
            <button class="proc-close" id="mytask-pp-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:16px">
            <div class="task-context" style="margin-bottom:12px;font-size:.9rem;color:var(--text-muted);padding:8px 10px;background:rgba(99,102,241,.06);border-left:3px solid #6366f1;border-radius:6px">
                <strong>${escHtml(machineId)}</strong> — ${escHtml(actionRo)}
                ${intervalText ? `<div style="font-size:.78rem;margin-top:2px;opacity:.85">Interval: ${escHtml(intervalText)}</div>` : ''}
            </div>

            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Motiv amânare <span style="color:#ef4444">*</span></label>
                <select id="pp-reason-code" required style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                    <option value="">— Selectează motivul —</option>
                    ${reasonOptions}
                </select>
            </div>

            <div id="pp-other-block" style="display:none;margin-bottom:12px;padding:10px;background:rgba(245,158,11,.08);border-left:3px solid #f59e0b;border-radius:6px">
                <label style="font-weight:600;font-size:.95rem;display:block">
                    Detalii motiv <span style="color:#ef4444">*</span>
                    <span class="hint" style="color:#f59e0b;font-weight:500;font-size:.82rem;display:block;margin-top:2px">⚠ Te rog descrie în minimum 20 de caractere motivul amânării.</span>
                </label>
                <textarea id="pp-reason-text" maxlength="500" rows="3" placeholder="Descrie în minimum 20 caractere de ce amâni..." style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px;resize:vertical"></textarea>
                <div class="char-counter" style="font-size:.78rem;margin-top:4px;color:var(--text-muted)">
                    <span id="pp-char-count" style="font-weight:700">0</span> / 20 minim caractere
                </div>
            </div>

            <div id="pp-missing-part-block" style="display:none;margin-bottom:12px">
                <label style="font-weight:600;font-size:.9rem">Piesa lipsă (opțional):</label>
                <select id="pp-part" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                    <option value="">— Selectează piesa —</option>
                </select>
            </div>

            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Reluare la data <span style="color:#ef4444">*</span></label>
                <input type="date" id="pp-until" required value="${defaultUntilStr}" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                <div class="date-hint" id="pp-date-hint" style="font-size:.8rem;color:var(--text-muted);margin-top:4px"></div>
            </div>

            <div id="pp-warn" class="warn-banner" style="display:none;padding:8px 12px;background:rgba(239,68,68,.1);border-left:3px solid #ef4444;border-radius:6px;margin-bottom:12px;font-size:.85rem;color:#fca5a5">
                ⚠ Acest task poate fi amânat max <b id="pp-max-days">${maxDefaultDays}</b> zile. Pentru depășire, alege "Alt motiv" și descrie ≥20 caractere.
            </div>

            <div class="modal-actions" style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
                <button id="pp-cancel" class="btn btn-secondary" style="padding:8px 14px;background:rgba(255,255,255,.06);border:1px solid var(--border);color:var(--text-main);border-radius:8px">Anulează</button>
                <button id="pp-save" class="btn btn-primary" style="padding:8px 16px;background:#6366f1;color:#fff;border:none;border-radius:8px;font-weight:700">💾 Salvează amânarea</button>
            </div>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    const reasonSel = document.getElementById('pp-reason-code') as HTMLSelectElement;
    const otherBlock = document.getElementById('pp-other-block') as HTMLElement;
    const otherText = document.getElementById('pp-reason-text') as HTMLTextAreaElement;
    const charCount = document.getElementById('pp-char-count') as HTMLElement;
    const missingPartBlock = document.getElementById('pp-missing-part-block') as HTMLElement;
    const partSel = document.getElementById('pp-part') as HTMLSelectElement;
    const dateInput = document.getElementById('pp-until') as HTMLInputElement;
    const dateHint = document.getElementById('pp-date-hint') as HTMLElement;
    const warnBanner = document.getElementById('pp-warn') as HTMLElement;
    const maxDaysEl = document.getElementById('pp-max-days') as HTMLElement;

    document.getElementById('mytask-pp-close')?.addEventListener('click', () => overlay.remove());
    document.getElementById('pp-cancel')?.addEventListener('click', () => overlay.remove());

    const updateWarn = () => {
        const val = dateInput.value;
        if (!val) { dateHint.textContent = ''; warnBanner.style.display = 'none'; return; }
        const target = new Date(val + 'T00:00:00');
        const todayMs = new Date(todayStr() + 'T00:00:00').getTime();
        const days = Math.round((target.getTime() - todayMs) / 86400000);
        dateHint.textContent = '→ ' + formatDateRO(val) + ' (în ' + days + ' ' + (days === 1 ? 'zi' : 'zile') + ')';
        if (days > maxDefaultDays && reasonSel.value !== 'other') {
            warnBanner.style.display = '';
            maxDaysEl.textContent = String(maxDefaultDays);
        } else {
            warnBanner.style.display = 'none';
        }
    };

    reasonSel.addEventListener('change', async () => {
        const code = reasonSel.value;
        otherBlock.style.display = code === 'other' ? '' : 'none';
        missingPartBlock.style.display = code === 'missing_part' && intervalId ? '' : 'none';
        if (code === 'missing_part' && intervalId && partSel.children.length <= 1) {
            try {
                const parts: PiesaPotrivita[] = await api('/api/maintenance_intervals/' + intervalId + '/matched_parts');
                partSel.innerHTML = '<option value="">— Selectează piesa —</option>';
                ordoneazaCuEchivalente(parts || []).forEach((p: PiesaPotrivita & { _sau?: boolean }) => {
                    const opt = document.createElement('option');
                    opt.value = String(p.id || '');
                    opt.dataset.pn = p.part_number || '';
                    opt.textContent = (p._sau ? '   sau ' : '') + (p.part_number || '') + ' — ' + (p.description_ro || '').slice(0, 60);
                    partSel.appendChild(opt);
                });
            } catch (_e) { /* ignore */ }
        }
        updateWarn();
    });

    otherText.addEventListener('input', () => {
        const n = otherText.value.trim().length;
        charCount.textContent = String(n);
        charCount.style.color = n >= 20 ? '#22c55e' : '#ef4444';
    });

    dateInput.addEventListener('change', updateWarn);
    updateWarn();

    document.getElementById('pp-save')?.addEventListener('click', async () => {
        const reasonCode = reasonSel.value;
        const reasonText = otherText.value.trim();
        const untilDate = dateInput.value;
        if (!reasonCode) { toast('Selectează motivul amânării!', 'warning'); return; }
        if (reasonCode === 'other' && reasonText.length < 20) {
            toast('Pentru "Alt motiv" descrierea trebuie să aibă minimum 20 caractere.', 'warning');
            otherText.focus();
            return;
        }
        if (!untilDate) { toast('Selectează data de reluare!', 'warning'); return; }

        const todayMs = new Date(todayStr() + 'T00:00:00').getTime();
        const targetMs = new Date(untilDate + 'T00:00:00').getTime();
        const days = Math.round((targetMs - todayMs) / 86400000);
        if (days <= 0) { toast('Data de reluare trebuie să fie în viitor.', 'warning'); return; }
        if (days > maxDefaultDays && reasonCode !== 'other') {
            toast('Depășire limită (' + maxDefaultDays + ' zile). Alege "Alt motiv" și descrie ≥20 caractere.', 'warning');
            return;
        }

        const partId = partSel.value || '';
        const partNumber = partSel.selectedOptions?.[0]?.dataset?.pn || '';

        const payload: CorpAmanare = {
            reason_code: reasonCode,
            reason_text: reasonText || null,
            postponed_until: untilDate,
            due_date: untilDate,
        };
        if (reasonCode === 'missing_part' && partId) {
            payload.waiting_part_id = parseInt(partId);
            payload.waiting_part_number = partNumber;
        }

        try {
            if (taskId) {
                await api('/api/operator_tasks/' + taskId + '/reschedule', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
            } else {
                payload.machine_id = machineId;
                payload.interval_id = intervalId || null;
                payload.task_type = 'assigned';
                payload.description = actionRo;
                payload.assigned_by = 'sistem';
                payload.status = (reasonCode === 'missing_part' && partId) ? 'waiting_parts' : 'postponed';
                payload.replaces_calendar_date = originalDate;
                payload.postpone_reason = reasonText || _postponeReasonLabel(reasonCode);
                await api('/api/operator_tasks', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
            }
            toast('Amânare salvată.');
            overlay.remove();
            try { delete _myTasksDailyCache[originalDate]; } catch (_e) { /* ignore */ }
            loadMyTasks();
            /* Amânarea se poate porni din panoul unei zile din calendar, iar acolo
               `loadMyTasks()` singur nu atinge grila: insigna ⏸ de pe zi rămânea
               la numărul vechi până reîncărcai secțiunea. Reîmprospătăm grila doar
               dacă e chiar pe ecran — din „Lucrările mele" n-are ce reîmprospăta,
               iar `loadCalendar()` trage cinci cereri de fiecare dată. */
            if (document.getElementById('cal-grid')) loadCalendar();
        } catch (e) {
            toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
        }
    });
}

/* ═══ POSTPONED BANNER + TIMELINE ════════════════════════════ */

async function _renderPostponedBanner(): Promise<void> {
    const host = document.getElementById('mytasks-list');
    if (!host) return;
    try {
        const items: LucrareAmanata[] = await api('/api/mytasks/postponed');
        const existing = document.getElementById('postponed-banner-wrap');
        if (existing) existing.remove();
        if (!items || items.length === 0) return;
        const wrap = document.createElement('div');
        wrap.id = 'postponed-banner-wrap';
        wrap.style.cssText = 'margin-bottom:14px';
        wrap.innerHTML = `
            <div id="postponed-banner" class="info-banner" style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;background:rgba(99,102,241,.1);border:1px solid rgba(99,102,241,.3);color:#c7d2fe">
                <span style="font-size:1.1rem">⏸</span>
                <span style="flex:1;font-size:.92rem;font-weight:600">Amânate: <b id="postponed-count">${items.length}</b></span>
                <button id="postponed-toggle" class="btn-link" style="padding:4px 10px;background:transparent;border:1px solid rgba(99,102,241,.45);color:#a5b4fc;border-radius:6px;cursor:pointer;font-size:.85rem;font-weight:600">Vezi listă →</button>
            </div>
            <div id="postponed-list" class="hidden" style="margin-top:8px;display:flex;flex-direction:column;gap:8px"></div>
        `;
        host.parentElement?.insertBefore(wrap, host);

        const listEl = document.getElementById('postponed-list') as HTMLElement;
        const toggleBtn = document.getElementById('postponed-toggle') as HTMLButtonElement;
        listEl.innerHTML = items.map(it => `
            <div class="postponed-card" style="padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--surface);display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap">
                <div style="flex:1;min-width:200px">
                    <div style="font-size:.85rem;color:var(--text-muted)">
                        <strong>${escHtml(it.machine_id || '')}</strong>${it.line ? ' — linia ' + escHtml(it.line) : ''}
                    </div>
                    <div style="font-weight:600;font-size:.92rem;margin:2px 0">${escHtml(it.task_name || 'Lucrare')}</div>
                    <div style="font-size:.8rem;color:var(--text-muted)">
                        Motiv: <em>${escHtml(_postponeReasonLabel(it.reason_code) || it.reason_text || '—')}</em>
                        ${it.reason_text && it.reason_code === 'other' ? '<br>' + escHtml(it.reason_text) : ''}
                    </div>
                    <div style="font-size:.78rem;color:var(--text-muted)">
                        Reluare: <strong>${escHtml(formatDateRO(it.postponed_until || ''))}</strong>
                        ${it.postponed_by ? ' • Amânat de: ' + escHtml(it.postponed_by) : ''}
                    </div>
                </div>
                <div style="display:flex;gap:6px;align-self:center">
                    <button class="postponed-clear-btn" data-taskid="${it.task_id}" style="padding:6px 12px;background:#00e676;color:#000;border:none;border-radius:8px;font-weight:700;font-size:.85rem;cursor:pointer">▶ Închide amânare</button>
                    <button class="postponed-tl-btn" data-taskid="${it.task_id}" title="Istoric" style="padding:6px 10px;background:rgba(255,255,255,.06);border:1px solid var(--border);color:var(--text-main);border-radius:8px;cursor:pointer">🕐</button>
                </div>
            </div>
        `).join('');

        toggleBtn.addEventListener('click', () => {
            listEl.classList.toggle('hidden');
            toggleBtn.textContent = listEl.classList.contains('hidden') ? 'Vezi listă →' : 'Ascunde ↑';
        });

        listEl.querySelectorAll('.postponed-clear-btn').forEach(b => {
            b.addEventListener('click', async () => {
                const tid = (b as HTMLElement).dataset.taskid;
                if (!tid) return;
                try {
                    await api('/api/operator_tasks/' + tid + '/clear_postpone', { method: 'POST' });
                    toast('Amânare închisă. Task-ul a revenit în lista zilei.');
                    loadMyTasks();
                } catch (e) {
                    toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
                }
            });
        });
        listEl.querySelectorAll('.postponed-tl-btn').forEach(b => {
            b.addEventListener('click', () => {
                const tid = (b as HTMLElement).dataset.taskid;
                if (tid) _showPostponeTimeline(parseInt(tid), '', 0);
            });
        });
    } catch (_e) { /* silent */ }
}

export async function _showPostponeTimeline(taskId: number, machineId: string, intervalId: number): Promise<void> {
    let overlay = document.getElementById('postpone-timeline-overlay');
    if (overlay) overlay.remove();
    overlay = document.createElement('div');
    overlay.id = 'postpone-timeline-overlay';
    overlay.className = 'detail-overlay';
    overlay.innerHTML = '<div class="detail-panel" style="max-width:640px"><div class="detail-header"><div class="detail-title-block"><span class="detail-line-id">Istoric activitate</span></div><button class="alarm-close" id="ppt-close">✕</button></div><div class="detail-content" id="ppt-body" style="padding:16px"><div class="typing-dots"><span></span><span></span><span></span></div></div></div>';
    document.body.appendChild(overlay);
    overlay.classList.remove('hidden');
    document.getElementById('ppt-close')?.addEventListener('click', () => overlay!.remove());

    try {
        const url = taskId
            ? '/api/operator_tasks/' + taskId + '/timeline'
            : '/api/mytasks/calendar_timeline?machine_id=' + encodeURIComponent(machineId) + '&interval_id=' + intervalId;
        const r: IstoricLucrare = await api(url);
        const events = r.events || [];
        const body = document.getElementById('ppt-body');
        if (!body) return;
        if (events.length === 0) {
            body.innerHTML = '<div style="color:var(--text-muted)">Nu există încă activitate înregistrată.</div>';
            return;
        }
        const colorByType: Record<string, string> = {
            'created': '#60a5fa', 'started': '#f59e0b', 'paused': '#6366f1',
            'completed': '#00e676', 'log_entry': '#10b981',
            'postpone_postponed': '#a78bfa',
            'postpone_cleared': '#22d3ee',
            'postpone_auto_reactivated': '#22c55e',
        };
        const iconByType: Record<string, string> = {
            'created': '📝', 'started': '▶', 'paused': '⏸',
            'completed': '✓', 'log_entry': '📋',
            'postpone_postponed': '📅',
            'postpone_cleared': '🔓',
            'postpone_auto_reactivated': '🔄',
        };
        const labelByType: Record<string, string> = {
            'created': 'CREAT', 'started': 'ÎNCEPUT', 'paused': 'PAUZĂ',
            'completed': 'TERMINAT', 'log_entry': 'LUCRARE',
            'postpone_postponed': 'AMÂNAT',
            'postpone_cleared': 'AMÂNARE ÎNCHISĂ',
            'postpone_auto_reactivated': 'REACTIVAT AUTO',
        };
        body.innerHTML = '<div style="position:relative;padding-left:8px">' + events.map(ev => {
            const col = colorByType[ev.type] || '#888';
            const lbl = labelByType[ev.type] || (ev.type || '').toUpperCase();
            const ic = iconByType[ev.type] || '•';
            const date = formatDateTimeRO(ev.ts || '');
            const reasonLine = ev.reason_code
                ? `<div style="font-size:.78rem;color:var(--text-muted)">Motiv: ${escHtml(_postponeReasonLabel(ev.reason_code))}${ev.postponed_until ? ' → ' + escHtml(formatDateRO(ev.postponed_until)) : ''}</div>`
                : '';
            return `<div style="position:relative;padding-bottom:12px;border-left:2px solid ${col};margin-left:6px;padding-left:14px">
                <div style="position:absolute;left:-7px;top:0;width:12px;height:12px;border-radius:50%;background:${col}"></div>
                <div style="font-size:.82rem;font-weight:700;color:${col}">${ic} ${escHtml(lbl)}</div>
                <div style="font-size:.72rem;color:var(--text-muted)">${escHtml(date)}${ev.actor ? ' — ' + escHtml(ev.actor) : ''}</div>
                ${reasonLine}
                ${ev.details && ev.type !== 'postpone_postponed' ? `<div style="font-size:.82rem;margin-top:3px;padding:5px 8px;background:rgba(255,255,255,.04);border-radius:6px;white-space:pre-wrap">${escHtml(ev.details)}</div>` : ''}
                ${ev.reason_text && ev.type === 'postpone_postponed' ? `<div style="font-size:.82rem;margin-top:3px;padding:5px 8px;background:rgba(255,255,255,.04);border-radius:6px;white-space:pre-wrap">${escHtml(ev.reason_text)}</div>` : ''}
            </div>`;
        }).join('') + '</div>';
    } catch (e) {
        const body = document.getElementById('ppt-body');
        if (body) body.innerHTML = '<div style="color:#ef4444">Eroare: ' + escHtml((e as Error)?.message || String(e)) + '</div>';
    }
}

/** Predarea unei lucrări către un coleg (20 sep 2026, cerută de Dan după garda
 *  IDOR). Același tipar ca „Predare schimb" la workflow: cine → cui, cu
 *  observații, scris în jurnal. Primitorul se alege dintr-o listă de conturi
 *  reale, nu se tastează — un nume greșit ar fi lăsat lucrarea nimănui. */
export async function _myTaskHandover(btn: HTMLElement) {
    const taskId = (btn as HTMLButtonElement).dataset.taskid;
    if (!taskId) { toast('Task ID lipsă', 'error'); return; }
    let colegi: Array<{ username: string; display_name: string; role: string }> = [];
    try { colegi = await api('/api/operator_tasks/colegi') || []; } catch (e) {
        toast('Nu pot lua lista de colegi: ' + ((e as Error)?.message || e), 'error'); return;
    }
    if (!colegi.length) { toast('Nu există alt coleg căruia să-i predai lucrarea.', 'error'); return; }
    const etichete = colegi.map(c => c.display_name === c.username ? c.username
        : `${c.display_name} (${c.username})${c.role === 'engineer' ? ' — inginer' : ''}`);
    const idx = await customSelect('Cui predai lucrarea?', etichete, 'Predă către…');
    if (idx === null || idx < 0) return;
    const catre = colegi[idx];
    const observatii = await askText('Predare către ' + catre.display_name,
        { label: 'Observații (ce ai făcut, ce a rămas):', okText: 'Predă lucrarea' });
    if (observatii === null) return;
    try {
        await api('/api/operator_tasks/' + taskId + '/handover', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to: catre.username, notes: observatii || '' })
        });
        toast('Lucrarea a fost predată către ' + catre.display_name + ' — s-a scris în jurnal.', 'success');
        loadMyTasks();
    } catch (e) {
        toast('Eroare la predare: ' + ((e as Error)?.message || String(e)), 'error');
    }
}

export async function _myTaskPauseQuick(btn: HTMLElement) {
    const taskId = (btn as HTMLButtonElement).dataset.taskid;
    if (!taskId) { toast('Task ID lipsă', 'error'); return; }
    const reason = await askText('Pauză lucrare', { label: 'Motiv pauză (opțional):', okText: 'Pune pe pauză' });
    if (reason === null) return;
    try {
        await api('/api/operator_tasks/' + taskId + '/pause', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pause_reason: reason || '' })
        });
        toast('Lucrare în pauză.');
        loadMyTasks();
    } catch (e) {
        toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
    }
}

export async function _myTaskResumeQuick(btn: HTMLElement) {
    const taskId = (btn as HTMLButtonElement).dataset.taskid;
    if (!taskId) { toast('Task ID lipsă', 'error'); return; }
    try {
        await api('/api/operator_tasks/' + taskId + '/resume', { method: 'POST' });
        toast('Lucrare reluată.');
        loadMyTasks();
    } catch (e) {
        toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
    }
}

export async function _checkPostponedPartsStock() {
    const waitingTasks = _myTasksPostponed.filter(t => t.status === 'waiting_parts' && t.waiting_part_id);
    const stockAlerts = [];
    for (const t of waitingTasks) {
        try {
            const sc: StocPiesa = await api('/api/parts/' + t.waiting_part_id + '/stock_check');
            if ((sc.stock_qty as number) > 0) stockAlerts.push({ task: t, stock: sc });   // stoc nesetat: null > 0 e fals
        } catch (e) { /* ignore */ }
    }
    if (stockAlerts.length > 0) {
        let existing = document.getElementById('stock-available-notification');
        if (existing) existing.remove();
        const msg = stockAlerts.length === 1
            ? `Piesa ${stockAlerts[0].task.waiting_part_number} este disponibilă! Lucrarea pe ${stockAlerts[0].task.machine_id} poate continua.`
            : `${stockAlerts.length} piese au intrat în stoc! Verifică lucrările amânate.`;
        const bar = document.createElement('div');
        bar.id = 'stock-available-notification';
        bar.style.cssText = 'position:fixed;bottom:20px;left:170px;right:16px;z-index:10000;padding:14px 20px;border-radius:10px;background:linear-gradient(135deg,#22c55e,#16a34a);color:#fff;font-weight:600;font-size:.95rem;box-shadow:0 4px 20px rgba(0,0,0,.3);display:flex;align-items:center;gap:10px;animation:slideDown .4s ease-out';
        bar.innerHTML = `<span style="font-size:1.4rem">📦</span><span style="flex:1">${escHtml(msg)}</span>
            <button style="padding:6px 16px;border-radius:6px;border:2px solid #fff;background:rgba(255,255,255,.2);color:#fff;font-weight:700;font-size:.9rem;cursor:pointer" onclick="document.querySelectorAll('[data-mytasks-tab=waiting]').forEach(b=>b.click());this.closest('#stock-available-notification').remove()">Vezi</button>
            <button style="padding:4px 10px;border:none;background:transparent;color:rgba(255,255,255,.6);font-size:1.2rem;cursor:pointer;line-height:1" onclick="this.closest('#stock-available-notification').remove()">&times;</button>`;
        document.body.appendChild(bar);
    }
}

document.querySelectorAll('[data-mytasks-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
        _myTasksTab = (btn as HTMLElement).dataset.mytasksTab;
        document.querySelectorAll('[data-mytasks-tab]').forEach(b => b.classList.remove('btn-active'));
        btn.classList.add('btn-active');
        _renderMyTasks();
    });
});
document.getElementById('mytasks-refresh')?.addEventListener('click', () => loadMyTasks());

/** „📊 Ce urmează pe linii” (1 nov 2026): fila cu starea mentenanțelor pe linii; `linie` = deschisă de pe cardul liniei. */
export function arataCeUrmeaza(linie?: string): void {
    if (linie) alegeLinia(linie);
    _myTasksTab = 'urmeaza';
    document.querySelectorAll('[data-mytasks-tab]').forEach(b => b.classList.remove('btn-active'));
    _renderMyTasks();
}
legaActiunile({ incep: _myTaskStartFromCalendar, amFacut: _myTaskOneTapComplete, reincarca: () => loadMyTasks() });
document.getElementById('mytasks-urmeaza-btn')?.addEventListener('click', () => arataCeUrmeaza());
// de pe cardul liniei (line-panel.ts): duce în „Lucrările mele” și deschide fila pe linia aceea
(window as unknown as { deschideCeUrmeaza?: (l: string) => void }).deschideCeUrmeaza = (linie: string) => {
    const link = document.querySelector('.nav-item[data-section="mytasks"], .nav-links a[data-section="mytasks"]');
    if (link) (link as HTMLElement).click();
    setTimeout(() => arataCeUrmeaza(linie), 400);
};
document.getElementById('mytasks-adhoc-btn')?.addEventListener('click', () => _openAdHocRepair());

/**
 * B2 (2026-07-07): global "Am reparat ceva acum" — a minimal ad-hoc log for
 * unplanned work. One line of text + optional photo, NO interval selection.
 * Keeps scheduled maintenance separate from spontaneous fixes. Free-text →
 * validation_status stays 'pending' (engineer reviews ad-hoc interventions).
 */
export async function _openAdHocRepair(): Promise<void> {
    await _ensureMachineCache();
    let overlay = document.getElementById('adhoc-repair-overlay');
    if (overlay) overlay.remove();
    const machineOpts = (_allMachinesCache || [])
        .map(m => `<option value="${escHtml(m.id)}">${escHtml(m.id)}${m.name ? ' — ' + escHtml(m.name) : ''}</option>`)
        .join('');
    overlay = document.createElement('div');
    overlay.id = 'adhoc-repair-overlay';
    overlay.className = 'detail-overlay';
    overlay.innerHTML = `
        <div class="detail-panel" style="max-width:520px">
            <div class="detail-header">
                <div class="detail-title-block">
                    <span class="detail-line-id">🛠 Am reparat ceva acum</span>
                    <span class="detail-line-product">Intervenție neplanificată</span>
                </div>
                <button class="alarm-close" id="adhoc-close">✕</button>
            </div>
            <div class="detail-content" style="padding:20px">
                <div class="urg-field">
                    <label class="urg-label">Utilaj</label>
                    <select id="adhoc-machine" class="urg-select"><option value="">— Selectează utilajul —</option>${machineOpts}</select>
                </div>
                <div class="urg-field">
                    <label class="urg-label">Ce ai făcut / reparat?</label>
                    <input type="text" id="adhoc-action" class="urg-input" placeholder="Ex: am strâns un furtun care pierdea, am curățat filtrul...">
                </div>
                <div class="urg-field">
                    <label class="urg-label">Poză (opțional)</label>
                    <input type="file" id="adhoc-photo" accept="image/*" capture="environment" class="urg-input">
                </div>
                <div style="display:flex;gap:10px;margin-top:16px;justify-content:center">
                    <button class="btn btn-green" id="adhoc-save" style="font-size:1rem;padding:12px 30px;font-weight:700;min-height:48px">✓ Salvează intervenția</button>
                    <button class="btn btn-outline" id="adhoc-cancel" style="font-size:.9rem;padding:12px 20px;min-height:48px">Anulează</button>
                </div>
                <div id="adhoc-status" style="margin-top:10px;text-align:center"></div>
            </div>
        </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay!.remove();
    overlay.querySelector('#adhoc-close')?.addEventListener('click', close);
    overlay.querySelector('#adhoc-cancel')?.addEventListener('click', close);

    const saveBtn = overlay.querySelector('#adhoc-save') as HTMLButtonElement;
    saveBtn.addEventListener('click', async () => {
        const machineId = (overlay!.querySelector('#adhoc-machine') as HTMLSelectElement).value;
        const actionText = (overlay!.querySelector('#adhoc-action') as HTMLInputElement).value.trim();
        const fileInput = overlay!.querySelector('#adhoc-photo') as HTMLInputElement;
        const statusEl = overlay!.querySelector('#adhoc-status') as HTMLElement;
        if (!machineId) { statusEl.innerHTML = '<span style="color:var(--red)">Alege un utilaj</span>'; return; }
        if (!actionText) { statusEl.innerHTML = '<span style="color:var(--red)">Scrie ce ai făcut</span>'; return; }
        saveBtn.disabled = true;
        saveBtn.textContent = '⏳ Se salvează…';
        const operator = state.currentUser?.username || 'operator';
        const hoursNow = state.machineHours[machineId] || 0;
        const actionCorect = await confirmaUnText('Ce ai reparat', actionText, 'am-reparat-ceva');
        try {
            const res: LucrareScrisa = await api('/api/maintenance_log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    machine_id: machineId,
                    action: actionCorect,
                    date: todayStr(),
                    technician: operator,
                    hours_at_service: hoursNow,
                    category: 'ad-hoc',
                    notes: 'Intervenție neplanificată (raportată din „Am reparat ceva acum")',
                    source: 'adhoc',
                })
            });
            const newLogId = res && res.id;
            const file = fileInput.files && fileInput.files[0];
            if (newLogId && file) {
                try {
                    const fd = new FormData();
                    fd.append('file', file);
                    await fetch(`/api/maintenance_log/${newLogId}/recording`, {
                        method: 'POST', credentials: 'include', body: fd,
                    });
                } catch (_e) { /* photo optional — ignore upload errors */ }
            }
            toast('✅ Intervenție salvată');
            close();
            loadMyTasks();
            // Coleg AI: unplanned fixes are prime lesson material — offer to
            // save one (fire-and-forget, never blocks the flow).
            colleagueOfferLesson({
                context_type: 'log', log_id: newLogId || 0,
                machine_id: machineId, actions: actionText,
            }).catch(() => {});
        } catch (e: unknown) {
            statusEl.innerHTML = '<span style="color:var(--red)">Eroare: ' + escHtml(e instanceof Error ? e.message : String(e)) + '</span>';
            saveBtn.disabled = false;
            saveBtn.textContent = '✓ Salvează intervenția';
        }
    });
}


/* ═══ CALENDAR MENTENANȚĂ ══════════════════════════════════════ */
export const HOURS_PER_DAY = 24;
export let calendarDataLoaded = false;
export let calendarIntervals: IntervalPlan[] = [];
export let calendarLogs: RandMaintenanceLog[] = [];
export let calendarStructure: StructuraFabrica | null = null;
/** utilaj → linia lui („M3,M4,M6” la cele partajate). Cheile `machine_id as string` de mai jos: un id null caută „null” → '' */
export let machineToLine: Record<string, string> = {};
/** Sursă unică cu Lucrările mele: /api/mytasks/daily (ore + log agregat în backend). */
let _calDailyCache: Record<string, LucrarePlan[]> = {};

if (typeof state.calYear === 'undefined') state.calYear = new Date().getFullYear();
if (typeof state.calMonth === 'undefined') state.calMonth = new Date().getMonth();

export function ensureCalendarStructure() {
    const sec = document.getElementById('sec-calendar');
    if (!sec) return;
    if (document.getElementById('cal-grid')) return;
    var filterBar = sec.querySelector('.filter-bar');
    if (!filterBar) return;
    if (!filterBar.querySelector('#cal-category-filter')) {
        var catLabel = document.createElement('label');
        catLabel.textContent = 'Tip mentenanță: ';
        var catSel = document.createElement('select');
        catSel.id = 'cal-category-filter';
        catSel.innerHTML = '<option value="">Toate</option>';
        catLabel.appendChild(catSel);
        filterBar.appendChild(catLabel);
    }
    var table = sec.querySelector('#calendar-table');
    var tableWrapper = sec.querySelector('#cal-table-wrapper');
    if (table && !tableWrapper) {
        tableWrapper = (table.closest('.table-wrapper') || table.parentNode) as Element | null;
        if (tableWrapper && tableWrapper.id !== 'cal-table-wrapper') {
            tableWrapper.id = 'cal-table-wrapper';
            tableWrapper.classList.add('hidden');
            (tableWrapper as HTMLElement).style.display = 'none';
        }
    }
    if (tableWrapper) { tableWrapper.classList.add('hidden'); (tableWrapper as HTMLElement).style.display = 'none'; }
    var wrap = document.createElement('div');
    wrap.className = 'cal-view-wrap';
    wrap.style.cssText = '';
    wrap.innerHTML = '<div class="cal-left"><div class="cal-month-nav"><button type="button" id="cal-prev-month" class="btn btn-sm">‹</button><h2 id="cal-month-title" class="cal-month-title">' + new Date(state.calYear!, state.calMonth!).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' }) + '</h2><button type="button" id="cal-next-month" class="btn btn-sm">›</button></div><div id="cal-grid" class="cal-grid"></div></div><div id="cal-day-panel" class="cal-day-panel hidden"><div class="cal-day-panel-header"><h3 id="cal-day-panel-title">Ziua</h3><button type="button" id="cal-day-panel-close" class="alarm-close">✕</button></div><div class="cal-day-panel-body"><h4 class="cal-day-section">Programate în această zi</h4><div id="cal-day-scheduled" class="cal-day-list"></div><h4 class="cal-day-section">Mentenanțe efectuate</h4><div id="cal-day-performed" class="cal-day-list"></div></div></div>';
    var toggleWrap = sec.querySelector('.cal-list-toggle-wrap');
    if (!toggleWrap) {
        toggleWrap = document.createElement('div');
        toggleWrap.className = 'cal-list-toggle-wrap';
        toggleWrap.innerHTML = '<button type="button" id="cal-toggle-list" class="btn btn-sm btn-outline">Afișează listă completă</button>';
    }
    if (tableWrapper && tableWrapper.parentNode) {
        tableWrapper.parentNode.insertBefore(wrap, tableWrapper);
        if (!sec.querySelector('.cal-list-toggle-wrap')) tableWrapper.parentNode.insertBefore(toggleWrap, tableWrapper);
    } else {
        sec.insertBefore(wrap, filterBar.nextSibling);
        if (!sec.querySelector('.cal-list-toggle-wrap')) sec.appendChild(toggleWrap);
    }
}

function _calMonthStartKey(): string {
    return localDateKey(new Date(state.calYear!, state.calMonth!, 1));
}

function _calDaysInMonth(): number {
    return new Date(state.calYear!, state.calMonth! + 1, 0).getDate();
}

async function _fetchCalDailyForMonth(): Promise<void> {
    const start = _calMonthStartKey();
    const days = _calDaysInMonth();
    try {
        const data: Record<string, LucrarePlan[]> = await api(`/api/mytasks/daily?start=${start}&days=${days}&include_skipped=1`);
        _calDailyCache = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
    } catch (_e) {
        _calDailyCache = {};
    }
}

function _calApplyFilters(tasks: LucrarePlan[]): LucrarePlan[] {
    const filterMachine = ($('#cal-machine-filter') && ($('#cal-machine-filter') as HTMLSelectElement).value) || '';
    const filterLine = ($('#cal-line-filter') && ($('#cal-line-filter') as HTMLSelectElement).value) || '';
    const filterCategory = ($('#cal-category-filter') && ($('#cal-category-filter') as HTMLSelectElement).value) || '';
    const criticalOnly = ($('#cal-critical-only') && ($('#cal-critical-only') as HTMLInputElement).checked) || false;

    let filtered = (tasks || []).filter(t => !t.skipped);
    if (filterMachine) {
        filtered = filtered.filter(t => t.machine_id === filterMachine);
    } else if (filterLine) {
        filtered = filtered.filter(t => {
            const ml = t.line_id || machineToLine[t.machine_id as string] || '';
            return _lineMatches(ml, filterLine);
        });
    }
    if (filterCategory) filtered = filtered.filter(t => (t.category || '') === filterCategory);
    if (criticalOnly) filtered = filtered.filter(t => t.critical);

    const seen = new Set<string>();
    return filtered.filter(t => {
        const key = t.task_id ? `eng:${t.task_id}` : `cal:${t.machine_id}:${t.interval_id}:${t.due_date || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function _calScheduledByDate(): Record<string, LucrarePlan[]> {
    const out: Record<string, LucrarePlan[]> = {};
    for (const [dateKey, tasks] of Object.entries(_calDailyCache)) {
        const filtered = _calApplyFilters(tasks);
        if (filtered.length) out[dateKey] = filtered;
    }
    return out;
}

function _calMonthEndKey(): string {
    const d = new Date(state.calYear!, state.calMonth! + 1, 0);
    return localDateKey(d);
}

async function _fetchCalLogsForMonth(): Promise<RandMaintenanceLog[]> {
    const from = _calMonthStartKey();
    const to = _calMonthEndKey();
    try {
        return await api(`/api/maintenance_log?from=${from}&to=${to}`);
    } catch (_e) {
        return [];
    }
}

/* ── Vedere săptămânală + încărcare pe tehnician (audit UX, 31 iul 2026) ── */
let _calViewMode: 'luna' | 'saptamana' = 'luna';
let _calWeekStart: Date | null = null;   // mereu o zi de luni
let _calWeekCache: Record<string, LucrarePlan[]> = {};

function _calMondayOf(d: Date): Date {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const wd = (x.getDay() + 6) % 7;   // 0 = luni
    x.setDate(x.getDate() - wd);
    return x;
}

function _calWeekTitle(): string {
    if (!_calWeekStart) return '';
    const fin = new Date(_calWeekStart);
    fin.setDate(fin.getDate() + 6);
    const f = (d: Date) => d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
    return `Săptămâna ${f(_calWeekStart)} – ${f(fin)}`;
}

function _calEnsureWeekUI(): void {
    const nav = document.querySelector('.cal-month-nav');
    if (nav && !document.getElementById('cal-view-toggle')) {
        const tg = document.createElement('div');
        tg.id = 'cal-view-toggle';
        tg.className = 'cal-view-toggle';
        tg.innerHTML = '<button type="button" class="btn btn-sm btn-primary" id="cal-view-luna">Lună</button>'
            + '<button type="button" class="btn btn-sm btn-outline" id="cal-view-sapt">Săptămână</button>';
        nav.appendChild(tg);
        document.getElementById('cal-view-luna')!.addEventListener('click', () => _calSetView('luna'));
        document.getElementById('cal-view-sapt')!.addEventListener('click', () => _calSetView('saptamana'));
    }
    const grid = document.getElementById('cal-grid');
    if (grid && !document.getElementById('cal-week-wrap')) {
        const w = document.createElement('div');
        w.id = 'cal-week-wrap';
        w.style.display = 'none';
        grid.parentElement!.appendChild(w);
    }
}

function _calSetView(mode: 'luna' | 'saptamana'): void {
    _calViewMode = mode;
    const bl = document.getElementById('cal-view-luna');
    const bs = document.getElementById('cal-view-sapt');
    if (bl && bs) {
        bl.className = 'btn btn-sm ' + (mode === 'luna' ? 'btn-primary' : 'btn-outline');
        bs.className = 'btn btn-sm ' + (mode === 'saptamana' ? 'btn-primary' : 'btn-outline');
    }
    const grid = document.getElementById('cal-grid');
    const week = document.getElementById('cal-week-wrap');
    if (grid) grid.style.display = mode === 'luna' ? '' : 'none';
    if (week) week.style.display = mode === 'saptamana' ? '' : 'none';
    if (mode === 'saptamana') {
        if (!_calWeekStart) _calWeekStart = _calMondayOf(new Date());
        renderCalendarWeek();
    } else {
        const titleEl = $('#cal-month-title');
        if (titleEl) titleEl.textContent = new Date(state.calYear!, state.calMonth!).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });
        renderCalendarGrid();
    }
}

export async function renderCalendarWeek(): Promise<void> {
    if (!_calWeekStart) _calWeekStart = _calMondayOf(new Date());
    const wrap = document.getElementById('cal-week-wrap');
    if (!wrap) return;
    const titleEl = $('#cal-month-title');
    if (titleEl) titleEl.textContent = _calWeekTitle();
    wrap.innerHTML = '<div class="cal-week-more" style="padding:20px">⏳ Se încarcă săptămâna...</div>';

    const startKey = localDateKey(_calWeekStart);
    try {
        const data: Record<string, LucrarePlan[]> = await api(`/api/mytasks/daily?start=${startKey}&days=7&include_skipped=1`);
        _calWeekCache = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
    } catch (_e) { _calWeekCache = {}; }

    const azi = todayStr();
    const zile = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
    // încărcarea pe tehnician pe toată săptămâna (după filtrele active)
    const workload: Record<string, number> = {};
    let nealocate = 0;
    let html = '<div class="cal-week-grid">';
    for (let i = 0; i < 7; i++) {
        const d = new Date(_calWeekStart);
        d.setDate(d.getDate() + i);
        const key = localDateKey(d);
        const tasks = _calApplyFilters(_calWeekCache[key] || []);
        tasks.forEach(t => {
            if (t.source === 'engineer' && t.assigned_to) workload[t.assigned_to] = (workload[t.assigned_to] || 0) + 1;
            else nealocate++;
        });
        const primele = tasks.slice(0, 6);
        html += `<div class="cal-week-day${key === azi ? ' azi' : ''}" data-datekey="${key}">
            <div class="cal-week-day-head">${zile[i]} ${d.getDate()}</div>`;
        primele.forEach(t => {
            const cls = t.critical ? ' crit' : (t.source === 'engineer' ? ' eng' : '');
            const cine = t.assigned_to ? ` · ${t.assigned_to}` : '';
            html += `<div class="cal-week-chip${cls}" title="${escHtml((t.machine_id || '') + ': ' + (t.action_ro || ''))}">${escHtml(t.machine_id || '')}${escHtml(cine)} — ${escHtml(truncate(t.action_ro || '', 40))}</div>`;
        });
        if (tasks.length > primele.length) html += `<div class="cal-week-more">+${tasks.length - primele.length} altele</div>`;
        if (!tasks.length) html += '<div class="cal-week-more">—</div>';
        html += '</div>';
    }
    html += '</div>';

    const chips = Object.entries(workload).sort((a, b) => b[1] - a[1])
        .map(([cine, n]) => `<span>👤 ${escHtml(cine)}: <strong>${n}</strong></span>`).join('');
    html += `<div class="cal-week-workload"><span style="color:var(--text-muted)">Încărcare săptămână:</span>${chips || '<span style="color:var(--text-muted)">niciun task alocat</span>'}<span>📋 fără mecanic: <strong>${nealocate}</strong></span></div>`;

    wrap.innerHTML = html;
    wrap.querySelectorAll('.cal-week-day').forEach(cell => {
        (cell as HTMLElement).addEventListener('click', () => {
            const key = (cell as HTMLElement).dataset.datekey || '';
            const sched = _calApplyFilters(_calWeekCache[key] || []);
            const perf = (calendarLogs || []).filter(l => (l.date || '').slice(0, 10) === key);
            openCalDayPanel(key, sched, perf);
        });
    });
}

export async function loadCalendar() {
    ensureCalendarStructure();
    _calEnsureWeekUI();
    const monthFrom = _calMonthStartKey();
    const monthTo = _calMonthEndKey();
    const [intervals, logs, structure, pausedWfs, opTasks]: [IntervalPlan[], RandMaintenanceLog[], StructuraFabrica | null,
        LucrareInCurs[], LucrareAlocata[]] = await Promise.all([
        api('/api/maintenance_intervals'),
        api(`/api/maintenance_log?from=${monthFrom}&to=${monthTo}`),
        api('/api/factory_structure').catch(() => null),
        // două cereri, nu o listă: ruta face egalitate strictă pe status și
        // răspunde 400 la „paused,in_progress" (audit 2.15)
        Promise.all([
            api('/api/maintenance_workflows?status=paused').catch(() => []),
            api('/api/maintenance_workflows?status=in_progress').catch(() => []),
        ]).then(([p, c]) => [...(p || []), ...(c || [])]),
        api('/api/operator_tasks?status=pending,assigned,in_progress,paused,postponed,waiting_parts').catch(() => []),
    ]);
    window._calPausedWfs = {};
    (pausedWfs || []).forEach(wf => { if (wf.interval_id) window._calPausedWfs[wf.interval_id] = wf; });
    calendarIntervals = intervals || [];
    calendarLogs = logs || [];
    window._calOperatorTasks = opTasks || [];
    calendarStructure = structure;
    machineToLine = {};
    if (structure && structure.production_lines) {
        structure.production_lines.forEach(pl => {
            // `main_machine` e rândul utilajului, nu id-ul (12 oct 2026: cheia ieșea "[object Object]")
            [pl.main_machine ? pl.main_machine.id : null].concat((pl.equipment || []).map(e => e.id)).forEach(mid => {
                if (!mid) return;
                if (machineToLine[mid]) {
                    if (!machineToLine[mid].split(',').includes(pl.id)) {
                        machineToLine[mid] += ',' + pl.id;
                    }
                } else {
                    machineToLine[mid] = pl.id;
                }
            });
        });
    }
    // Utilitățile (12 oct 2026): grupurile sunt UTIL_AER, TRAFO, RACITOARE_EXTERNE… — codul căuta `utilities.air`, care
    // n-a existat, deci utilitățile rămâneau fără linie: filtrul pe linia lor golea tabelul de intervale și efectuatele.
    // Linia se ia din `machines.line` (aceeași cheie ca în lista de linii); o mașină comună păstrează și liniile ei.
    if (structure && structure.utilities) {
        Object.entries(structure.utilities).forEach(([grup, u]) => {
            (u.machines || []).forEach(m => {
                if (!m.id) return;
                (m.line || grup).split(',').map(l => l.trim()).filter(Boolean).forEach(l => {
                    if (!machineToLine[m.id]) machineToLine[m.id] = l;
                    else if (!machineToLine[m.id].split(',').includes(l)) machineToLine[m.id] += ',' + l;
                });
            });
        });
    }
    const hoursFromStructure: Record<string, number> = {};
    if (structure && structure.production_lines) {
        structure.production_lines.forEach(pl => {
            const lineHours = parseFloat(String(pl.operating_hours)) || (parseFloat(String(state.machineHours[pl.id])) || 0);
            if (pl.main_machine) hoursFromStructure[pl.main_machine.id] = lineHours;
            (pl.equipment || []).forEach(eq => {
                const h = parseFloat(String(eq.operating_hours)) || (parseFloat(String(state.machineHours[eq.id])) || 0);
                hoursFromStructure[eq.id] = h;
            });
        });
    }
    Object.assign(state.machineHours, hoursFromStructure);

    if (!calendarDataLoaded) {
        await _ensureMachineCache();
        const machineIds = [...new Set((calendarIntervals || []).map(i => i.machine_id))].filter(Boolean).sort();
        const sel = $('#cal-machine-filter');
        if (sel) {
            sel.innerHTML = '<option value="">Toate</option>';
            machineIds.forEach(mid => { sel.innerHTML += `<option value="${mid}">${mid}</option>`; });
            _insertLineSelectBefore(sel, 'cal-line-filter', 'Toate liniile', 'Toate');
        }
        const categories = [...new Set((calendarIntervals || []).map(i => i.category).filter(Boolean))].sort();
        const catSel = $('#cal-category-filter');
        if (catSel) {
            catSel.innerHTML = '<option value="">Toate</option>';
            categories.forEach(c => { catSel.innerHTML += `<option value="${escHtml(c)}">${escHtml(c)}</option>`; });
        }
        calendarDataLoaded = true;
    }

    const gridEl = $('#cal-grid');
    const tableWrap = $('#cal-table-wrapper');
    const toggleListEl = $('#cal-toggle-list');
    if (gridEl) gridEl.style.display = '';
    if (tableWrap) { tableWrap.classList.add('hidden'); tableWrap.style.display = 'none'; }
    if (toggleListEl) toggleListEl.textContent = 'Afișează listă completă';

    await _fetchCalDailyForMonth();
    renderCalendarGrid();
    renderCalendarTable(calendarIntervals, calendarLogs);

    const calMachine = $('#cal-machine-filter');
    const calCat = $('#cal-category-filter');
    const calCrit = $('#cal-critical-only');
    const calLine = $('#cal-line-filter');
    const _rerender = () => {
        renderCalendarGrid();
        renderCalendarTable(calendarIntervals, calendarLogs);
        _refreshSubSections();
    };
    if (calMachine) calMachine.onchange = _rerender;
    if (calCat) calCat.onchange = _rerender;
    if (calCrit) calCrit.onchange = _rerender;
    if (calLine) {
        const origHandler = calLine.onchange;
        calLine.onchange = (e) => {
            if (origHandler) (origHandler as EventListener).call(calLine, e as Event);
            _rerender();
        };
    }
    const calReset = $('#cal-reset');
    if (calReset) calReset.onclick = () => {
        if (calLine) (calLine as HTMLInputElement).value = '';
        if (calMachine) (calMachine as HTMLInputElement).value = '';
        if (calCat) (calCat as HTMLInputElement).value = '';
        if (calCrit) (calCrit as HTMLInputElement).checked = false;
        _rerender();
    };

    const prevBtn = $('#cal-prev-month');
    const nextBtn = $('#cal-next-month');
    if (prevBtn) prevBtn.onclick = async () => {
        if (_calViewMode === 'saptamana') {
            _calWeekStart!.setDate(_calWeekStart!.getDate() - 7);
            await renderCalendarWeek();
            return;
        }
        state.calMonth!--;
        if (state.calMonth! < 0) { state.calMonth = 11; state.calYear!--; }
        calendarLogs = await _fetchCalLogsForMonth();
        await _fetchCalDailyForMonth();
        renderCalendarGrid();
        renderCalendarTable(calendarIntervals, calendarLogs);
    };
    if (nextBtn) nextBtn.onclick = async () => {
        if (_calViewMode === 'saptamana') {
            _calWeekStart!.setDate(_calWeekStart!.getDate() + 7);
            await renderCalendarWeek();
            return;
        }
        state.calMonth!++;
        if (state.calMonth! > 11) { state.calMonth = 0; state.calYear!++; }
        calendarLogs = await _fetchCalLogsForMonth();
        await _fetchCalDailyForMonth();
        renderCalendarGrid();
        renderCalendarTable(calendarIntervals, calendarLogs);
    };

    const titleEl = $('#cal-month-title');
    if (titleEl) titleEl.textContent = new Date(state.calYear!, state.calMonth!).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });

    const closePanel = $('#cal-day-panel-close');
    if (closePanel) closePanel.onclick = () => { _openPanelDateKey = null; const p = $('#cal-day-panel'); if (p) p.classList.add('hidden'); };
    const toggleList = $('#cal-toggle-list');
    if (toggleList && tableWrap) {
        toggleList.onclick = () => {
            const isHidden = tableWrap.classList.toggle('hidden');
            tableWrap.style.display = isHidden ? 'none' : '';
            toggleList.textContent = isHidden ? 'Afișează listă completă' : 'Ascunde listă';
        };
    }

    await _initConditionalSection();
    _initStatsSection();
}

let _condSectionOpen = false;
let _condAllData: IntervalConditional[] = [];
let _statsSectionOpen = false;
let _openPanelDateKey: string | null = null;

async function _initConditionalSection() {
    const toggleBtn = $('#cal-conditional-toggle');
    const body = $('#cal-conditional-body');
    if (!toggleBtn || !body) return;

    toggleBtn.onclick = async () => {
        _condSectionOpen = !_condSectionOpen;
        if (_condSectionOpen) {
            body.classList.remove('hidden');
            body.style.display = '';
            await _loadConditionalIntervals();
        } else {
            body.classList.add('hidden');
        }
    };

    const condCritical = $('#cond-critical-only');
    if (condCritical) condCritical.onchange = () => _refreshConditionalIfOpen();

    await _updateConditionalCount();
}

async function _updateConditionalCount() {
    try {
        const filterLine = ($('#cal-line-filter') as HTMLInputElement)?.value || '';
        const filterMachine = ($('#cal-machine-filter') as HTMLInputElement)?.value || '';
        let url = '/api/maintenance_intervals/conditional';
        const params: string[] = [];
        if (filterMachine) params.push('machine_id=' + encodeURIComponent(filterMachine));
        else if (filterLine) params.push('line=' + encodeURIComponent(filterLine));
        if (params.length) url += '?' + params.join('&');
        const data: IntervalConditional[] = await api(url);
        _condAllData = data || [];
        const countSpan = $('#cal-conditional-count');
        if (countSpan) countSpan.textContent = String(_condAllData.length);
    } catch { /* ignore */ }
}

function _refreshConditionalIfOpen() {
    if (_condSectionOpen) _loadConditionalIntervals();
    else _updateConditionalCount();
}

function _refreshSubSections() {
    _refreshConditionalIfOpen();
    if (_statsSectionOpen) {
        const body = $('#cal-stats-body');
        if (body) _loadStats(body as HTMLElement);
    }
}

async function _loadConditionalIntervals() {
    const body = $('#cal-conditional-body');
    if (!body) return;

    const filterLine = ($('#cal-line-filter') as HTMLInputElement)?.value || '';
    const filterMachine = ($('#cal-machine-filter') as HTMLInputElement)?.value || '';
    const filterCritical = ($('#cond-critical-only') as HTMLInputElement)?.checked || false;

    let url = '/api/maintenance_intervals/conditional';
    const params: string[] = [];
    if (filterMachine) params.push('machine_id=' + encodeURIComponent(filterMachine));
    else if (filterLine) params.push('line=' + encodeURIComponent(filterLine));
    if (params.length) url += '?' + params.join('&');

    const tbody = document.querySelector('#cal-conditional-table tbody');
    if (!tbody) return;
    tbody.innerHTML = '<tr><td colspan="5">Se încarcă...</td></tr>';

    try {
        let items: IntervalConditional[] = await api(url);
        if (filterCritical) items = (items || []).filter(i => i.critical);

        const countSpan = $('#cal-conditional-count');
        if (countSpan) countSpan.textContent = String((items || []).length);

        if (!items || items.length === 0) {
            tbody.innerHTML = '<tr><td colspan="5" class="empty-state">Nicio acțiune condițională' +
                (filterLine || filterMachine ? ' pentru filtrul selectat' : '') + '</td></tr>';
            return;
        }
        tbody.innerHTML = '';
        items.forEach(iv => {
            const tr = document.createElement('tr');
            const critBadge = iv.critical ? '<span style="color:var(--red);font-weight:700">⚠ CRITIC</span>' : '—';
            tr.innerHTML =
                `<td><strong>${escHtml(iv.machine_id)}</strong><br><small style="color:var(--text-muted)">${escHtml(iv.machine_name || '')}</small></td>` +
                `<td>${escHtml(iv.interval_text || 'La nevoie')}</td>` +
                `<td>${escHtml((iv.action_ro || '').substring(0, 200))}${(iv.action_ro || '').length > 200 ? '...' : ''}</td>` +
                `<td>${critBadge}</td>` +
                `<td>${escHtml(iv.part_number || '—')}</td>`;
            tbody.appendChild(tr);
        });
    } catch (e) {
        tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Eroare: ${escHtml((e as Error).message)}</td></tr>`;
    }
}

async function _initStatsSection() {
    const toggleBtn = $('#cal-stats-toggle');
    const body = $('#cal-stats-body');
    if (!toggleBtn || !body) return;

    toggleBtn.onclick = async () => {
        _statsSectionOpen = !_statsSectionOpen;
        if (_statsSectionOpen) {
            body.classList.remove('hidden');
            await _loadStats(body as HTMLElement);
        } else {
            body.classList.add('hidden');
        }
    };
}

async function _loadStats(container: HTMLElement) {
    container.innerHTML = '<p>Se încarcă...</p>';
    try {
        const filterLine = ($('#cal-line-filter') as HTMLInputElement)?.value || '';
        const filterMachine = ($('#cal-machine-filter') as HTMLInputElement)?.value || '';
        let statsUrl = '/api/maintenance_intervals/stats';
        const sp: string[] = [];
        if (filterMachine) sp.push('machine_id=' + encodeURIComponent(filterMachine));
        else if (filterLine) sp.push('line=' + encodeURIComponent(filterLine));
        if (sp.length) statsUrl += '?' + sp.join('&');
        const stats: StatisticiIntervale = await api(statsUrl);
        const pct = stats.total > 0 ? Math.round((stats.scheduled / stats.total) * 100) : 0;
        let html = `
            <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:16px">
                <div style="text-align:center;padding:12px;background:var(--bg);border-radius:8px">
                    <div style="font-size:1.8rem;font-weight:700;color:var(--accent)">${stats.total}</div>
                    <div style="font-size:0.85rem;color:var(--text-muted)">Total intervale</div>
                </div>
                <div style="text-align:center;padding:12px;background:var(--bg);border-radius:8px">
                    <div style="font-size:1.8rem;font-weight:700;color:var(--green)">${stats.scheduled}</div>
                    <div style="font-size:0.85rem;color:var(--text-muted)">Programate (${pct}%)</div>
                </div>
                <div style="text-align:center;padding:12px;background:var(--bg);border-radius:8px">
                    <div style="font-size:1.8rem;font-weight:700;color:var(--amber)">${stats.conditional}</div>
                    <div style="font-size:0.85rem;color:var(--text-muted)">Condiționale</div>
                </div>
                <div style="text-align:center;padding:12px;background:var(--bg);border-radius:8px">
                    <div style="font-size:1.8rem;font-weight:700;color:var(--text-muted)">${stats.disabled}</div>
                    <div style="font-size:0.85rem;color:var(--text-muted)">Dezactivate</div>
                </div>
            </div>
            <table style="width:100%;font-size:0.85rem">
                <thead><tr><th>Mașină</th><th>Linie</th><th>Total</th><th>Programate</th><th>Condiționale</th><th>Critice</th></tr></thead>
                <tbody>`;
        (stats.per_machine || []).forEach(m => {
            const bg = m.critical > 0 ? 'background:rgba(239,68,68,0.05)' : '';
            html += `<tr style="${bg}"><td><strong>${escHtml(m.machine_id)}</strong></td><td>${escHtml(m.line || '—')}</td><td>${m.total}</td><td>${m.scheduled}</td><td>${m.conditional}</td><td>${m.critical > 0 ? '<span style="color:var(--red);font-weight:700">' + m.critical + '</span>' : '0'}</td></tr>`;
        });
        html += '</tbody></table>';
        container.innerHTML = html;
    } catch (e) {
        container.innerHTML = `<p style="color:var(--red)">Eroare: ${escHtml((e as Error).message)}</p>`;
    }
}

export function buildCalendarEvents(intervals: IntervalPlan[], logs: RandMaintenanceLog[]) {
    const filterMachine = ($('#cal-machine-filter') && ($('#cal-machine-filter') as HTMLInputElement).value) || '';
    const filterLine = ($('#cal-line-filter') && ($('#cal-line-filter') as HTMLInputElement).value) || '';
    const filterCategory = ($('#cal-category-filter') && ($('#cal-category-filter') as HTMLInputElement).value) || '';
    const criticalOnly = ($('#cal-critical-only') && ($('#cal-critical-only') as HTMLInputElement).checked) || false;
    let data = intervals || [];
    if (filterMachine) {
        data = data.filter(i => i.machine_id === filterMachine);
    } else if (filterLine) {
        data = data.filter(i => {
            const line = machineToLine[i.machine_id as string] || '';
            return line === filterLine || line.split(',').some((l: string) => l.trim() === filterLine);
        });
    }
    if (filterCategory) data = data.filter(i => (i.category || '') === filterCategory);
    if (criticalOnly) data = data.filter(i => i.critical);

    const scheduledByDate: Record<string, OcurentaProiectata[]> = {};
    const performedByDate: Record<string, LucrareFacuta[]> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const calMonth = state.calMonth;
    const calYear = state.calYear;
    const monthStart = new Date(calYear!, calMonth!, 1);
    const monthEnd = new Date(calYear!, calMonth! + 1, 0);

    function textToCalDays(text: string | null, ih: number): number | null {
        const t = (text || '').toLowerCase();
        if (t.includes('zilnic') || t.includes('daily')) return 1;
        // săptămânal/weekly = 168h funcționare (nu 7 zile calendar).
        // Rămâne în calea pe ore (ih=168 → ~7 zile la 24h/zi doar ca proiecție).
        if (t.includes('lunar') || t.includes('monthly')) return 30;
        if (t.includes('trimestrial') || t.includes('quarterly')) return 90;
        if (t.includes('semestrial') || t.includes('bianual')) return 180;
        if (t.includes('anual') || t.includes('yearly') || t.includes('annual')) return 365;
        if (t.includes('2 ani')) return 730;
        if (t.includes('5 ani')) return 1825;
        if (t.includes('prima') || t.includes('first') || t.includes('démarrage')) return null;
        if (ih <= 24) return 1;
        if (ih <= 200) return 7;
        if (ih <= 2000) return 30;
        if (ih <= 5000) return 180;
        return 365;
    }

    data.forEach(iv => {
        const hours = state.machineHours[iv.machine_id as string] || 0;
        const intervalH = iv.interval_hours || 0;
        if (intervalH <= 0) return;

        const calDays = textToCalDays(iv.interval_text, intervalH);
        if (calDays === null) return;

        const prog = intervalHoursProgress(iv as ProgresOre, hours);   // interval_hours > 0 aici (`intervalH` mai sus)
        const hoursSinceService = prog.hoursSince;
        const lastLogDate = iv.last_log_date ? String(iv.last_log_date).slice(0, 10) : null;
        const itxt = (iv.interval_text || '').toLowerCase();
        const weeklyHours = !itxt.includes('prima') && (
            itxt.includes('săptămânal') || itxt.includes('saptamanal') || itxt.includes('weekly')
            || /s[ăa]pt[ăa]m[âa]n/.test(itxt)
        );

        const lineId = machineToLine[iv.machine_id as string] || '';
        const evtData = { machine_id: iv.machine_id, action_ro: iv.action_ro, category: iv.category, critical: iv.critical, interval_text: iv.interval_text, interval_id: iv.id, document_id: iv.document_id, page_number: iv.page_number, interval_hours: intervalH, line_id: lineId, needs_parts: iv.needs_parts };

        let firstDue: Date;
        if (lastLogDate && !weeklyHours) {
            const logDate = new Date(lastLogDate);
            logDate.setHours(0, 0, 0, 0);
            firstDue = new Date(logDate);
            firstDue.setDate(firstDue.getDate() + Math.max(1, calDays));
        } else if (hoursSinceService >= intervalH) {
            firstDue = new Date(today);
        } else {
            const remaining = intervalH - hoursSinceService;
            if (remaining <= HOURS_PER_DAY) {
                firstDue = new Date(today);
            } else {
                const daysUntil = Math.ceil(remaining / HOURS_PER_DAY);
                firstDue = new Date(today);
                firstDue.setDate(firstDue.getDate() + daysUntil);
            }
        }
        const d = new Date(firstDue);
        while (d <= monthEnd) {
            if (d >= monthStart) {
                const key = localDateKey(d);
                if (!scheduledByDate[key]) scheduledByDate[key] = [];
                scheduledByDate[key].push({ ...evtData });
            }
            d.setDate(d.getDate() + Math.max(1, calDays));
        }
    });

    (logs || []).forEach(log => {
        const d = (log.date || '').toString().slice(0, 10);
        if (!d) return;
        if (!performedByDate[d]) performedByDate[d] = [];
        performedByDate[d].push({ machine_id: log.machine_id, action: log.action, date: log.date, technician: log.technician });
    });

    return { scheduledByDate, performedByDate };
}

export function getCalendarMonthDays(year: number|undefined, month: number|undefined) {
    const first = new Date(year!, month!, 1);
    const last = new Date(year!, month! + 1, 0);
    const startPad = first.getDay();
    const startDay = startPad === 0 ? 6 : startPad - 1;
    const daysInMonth = last.getDate();
    const total = startDay + daysInMonth;
    const rows = Math.ceil(total / 7);
    const cells = rows * 7;
    const result = [];
    for (let i = 0; i < cells; i++) {
        const dayOffset = i - startDay + 1;
        const isCurrent = dayOffset >= 1 && dayOffset <= daysInMonth;
        // `new Date(an, lună, zi)` trece singur peste capetele lunii (0 = ultima zi a lunii
        // trecute, 31 în septembrie = 1 octombrie). Varianta veche scădea `daysInMonth` fără
        // să schimbe luna: celulele de după sfârșitul lunii purtau datele 1–4 ale lunii
        // AFIȘATE (30 sep 2026: „1 oct" arăta cele 25 de lucrări din 1 sep și le deschidea).
        const d = new Date(year!, month!, dayOffset);
        const key = localDateKey(d);
        result.push({ date: d, key, isCurrentMonth: isCurrent, dayNum: isCurrent ? dayOffset : d.getDate() });
    }
    return result;
}

/** Starea unei lucrări alocate, în cuvintele atelierului (cardurile arătau „pending"). */
const _STARE_LUCRARE: Record<string, string> = {
    pending: 'de făcut', assigned: 'alocată', in_progress: 'în lucru', paused: 'în pauză',
    postponed: 'amânată', waiting_parts: 'lipsă piesă', done: 'făcută', cancelled: 'anulată',
};

/** Scoate din „programate" lucrările alocate care au deja cardul lor în „Lucrări alocate". */
function _faraLucrariAlocate(programate: LucrarePlan[], alocate: LucrareAlocata[]): LucrarePlan[] {
    if (!alocate.length) return programate;
    const iduri = new Set(alocate.map(t => Number(t.id)));
    return programate.filter(s => !(s.source === 'engineer' && s.task_id && iduri.has(Number(s.task_id))));
}

/** Mută calendarul pe luna zilei date și deschide ziua (clic pe o zi a lunii vecine). */
async function _calMergiLaLunaZilei(key: string): Promise<void> {
    const [an, luna] = key.split('-').map(Number);
    if (!an || !luna) return;
    state.calYear = an;
    state.calMonth = luna - 1;
    _openPanelDateKey = key;
    calendarLogs = await _fetchCalLogsForMonth();
    await _fetchCalDailyForMonth();
    renderCalendarGrid();
    renderCalendarTable(calendarIntervals, calendarLogs);
}

export function renderCalendarGrid() {
    const titleEl = $('#cal-month-title');
    if (titleEl) titleEl.textContent = new Date(state.calYear!, state.calMonth!).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });

    // Programate: aceeași sursă ca Lucrările mele (/api/mytasks/daily).
    const scheduledByDate = _calScheduledByDate();
    const { performedByDate } = buildCalendarEvents(calendarIntervals, calendarLogs);
    const days = getCalendarMonthDays(state.calYear, state.calMonth);
    const todayKey = todayStr();

    const opTasks: LucrareAlocata[] = window._calOperatorTasks || [];
    const opTasksByDate: Record<string, LucrareAlocata[]> = {};
    opTasks.forEach(t => {
        const dk = (t.due_date || '').slice(0, 10);
        if (!dk) return;
        if (!opTasksByDate[dk]) opTasksByDate[dk] = [];
        opTasksByDate[dk].push(t);
    });
    window._calOpTasksByDate = opTasksByDate;

    const weekDays = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
    const grid = $('#cal-grid');
    if (!grid) return;
    grid.innerHTML = '';
    weekDays.forEach(w => {
        const th = document.createElement('div');
        th.className = 'cal-cell cal-cell-head';
        th.textContent = w;
        grid.appendChild(th);
    });

    days.forEach(({ date, key, isCurrentMonth, dayNum }) => {
        const dayTasks = opTasksByDate[key] || [];
        // lucrările alocate ale celui care se uită vin și prin /api/mytasks/daily (source
        // 'engineer'); au deja insigna 📋 și cardul lor — altfel se numărau și se arătau de două ori
        const scheduled = _faraLucrariAlocate(scheduledByDate[key] || [], dayTasks);
        const performed = performedByDate[key] || [];
        const isToday = key === todayKey;
        const cell = document.createElement('div');
        cell.className = 'cal-cell' + (isCurrentMonth ? '' : ' other-month') + (isToday ? ' cal-cell-today' : '');
        cell.dataset.date = key;
        const nSched = scheduled.length;
        const nPerf = performed.length;
        const nTasks = dayTasks.length;
        const nPostponed = dayTasks.filter(t => t.status === 'postponed' || t.status === 'waiting_parts').length;
        const hasCritical = scheduled.some(s => s.critical);
        let countsHtml = '';
        if (nSched > 0) {
            countsHtml += `<span class="cal-count scheduled" title="${nSched} programate">${nSched}</span>`;
        }
        if (hasCritical) {
            const nCrit = scheduled.filter(s => s.critical).length;
            countsHtml += `<span class="cal-count critical" title="${nCrit} critice">${nCrit}!</span>`;
        }
        if (nTasks > 0) {
            countsHtml += `<span class="cal-count" title="${nTasks} lucrări alocate" style="background:rgba(99,102,241,.18);color:#6366f1;border:1px solid rgba(99,102,241,.35)">📋${nTasks}</span>`;
        }
        if (nPostponed > 0) {
            countsHtml += `<span class="cal-count" title="${nPostponed} amânate" style="background:rgba(251,146,60,.18);color:#fb923c;border:1px solid rgba(251,146,60,.4)">⏸${nPostponed}</span>`;
        }
        if (nPerf > 0) {
            countsHtml += `<span class="cal-count performed" title="${nPerf} efectuate">${nPerf}&#10003;</span>`;
        }
        cell.innerHTML = `
            <div class="cal-cell-date">${dayNum}</div>
            <div class="cal-cell-counts">${countsHtml}</div>
        `;
        // o zi a lunii vecine: datele ei nu sunt încărcate (grila ține doar luna afișată),
        // deci clicul mută calendarul pe luna ei și abia apoi deschide ziua
        cell.addEventListener('click', () => {
            if (isCurrentMonth) openCalDayPanel(key, scheduled, performed);
            else void _calMergiLaLunaZilei(key);
        });
        cell.addEventListener('dragover', (ev: DragEvent) => {
            if (!ev.dataTransfer) return;
            const hasTask = Array.from(ev.dataTransfer.types || []).includes('application/x-operator-task');
            if (hasTask) {
                ev.preventDefault();
                ev.dataTransfer.dropEffect = 'move';
                cell.classList.add('cal-cell-droptarget');
            }
        });
        cell.addEventListener('dragleave', () => cell.classList.remove('cal-cell-droptarget'));
        cell.addEventListener('drop', async (ev: DragEvent) => {
            ev.preventDefault();
            cell.classList.remove('cal-cell-droptarget');
            const taskId = ev.dataTransfer?.getData('application/x-operator-task');
            if (!taskId) return;
            const origDate = ev.dataTransfer?.getData('text/x-orig-date') || '';
            if (origDate === key) return;
            /* Tragerea trimitea doar `{due_date}`, iar `/reschedule` cere motiv
               (e o amânare, se scrie în task_postpone_events). Serverul răspundea
               400 și lucrarea rămânea pe loc — reprogramarea prin tragere n-a
               funcționat niciodată. Nu deschidem o portiță fără motiv, fiindcă
               atunci mutările ar dispărea din evidența amânărilor: ducem tragerea
               în ACELAȘI dialog, cu ziua-țintă deja completată. */
            const cardTras = document.querySelector(
                `.cal-task-card[data-task-id="${taskId}"]`) as HTMLElement | null;
            _myTaskPostponeFromCalendar({
                dataset: {
                    mid: cardTras?.dataset.mid || '',
                    ivid: cardTras?.dataset.ivid || '',
                    itext: cardTras?.dataset.itext || '',
                    action: cardTras?.dataset.action || '',
                    origdate: origDate,
                    taskid: String(taskId),
                },
            }, key);
        });
        grid.appendChild(cell);
    });

    if (_openPanelDateKey) {
        const sched = _faraLucrariAlocate(scheduledByDate[_openPanelDateKey] || [], opTasksByDate[_openPanelDateKey] || []);
        const perf = performedByDate[_openPanelDateKey] || [];
        openCalDayPanel(_openPanelDateKey, sched, perf);
    }
}

export function openCalDayPanel(dateKey: string, scheduled: LucrarePlan[], performed: LucrareFacuta[]) {
    _openPanelDateKey = dateKey;
    const panel = $('#cal-day-panel');
    const titleEl = $('#cal-day-panel-title');
    const listSched = $('#cal-day-scheduled');
    const listPerf = $('#cal-day-performed');
    if (!panel || !titleEl || !listSched || !listPerf) return;
    const d = new Date(dateKey + 'T12:00:00');
    titleEl.textContent = 'Ziua ' + d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
    listSched.innerHTML = '';
    listPerf.innerHTML = '';

    const opTasksByDate: Record<string, LucrareAlocata[]> = window._calOpTasksByDate || {};
    const opTasksToday = opTasksByDate[dateKey] || [];
    // același filtru ca în grilă, și pentru vederea pe săptămână, care cheamă direct panoul
    scheduled = _faraLucrariAlocate(scheduled || [], opTasksToday);
    if (opTasksToday.length > 0) {
        const header = document.createElement('div');
        header.style.cssText = 'font-size:.82rem;color:var(--text-muted);margin-bottom:6px;padding:4px 0;border-bottom:1px dashed var(--border)';
        header.innerHTML = `📋 <strong>Lucrări alocate (${opTasksToday.length})</strong> — „📅 Mută" sau trage pe altă zi`;
        listSched.appendChild(header);
        opTasksToday.forEach(t => {
            const card = document.createElement('div');
            card.className = 'mytask-card cal-task-card';
            const statusColor: Record<string, string> = {
                'pending': '#60a5fa', 'assigned': '#60a5fa', 'in_progress': '#f59e0b',
                'paused': '#6366f1', 'postponed': '#fb923c', 'waiting_parts': '#ef4444',
            };
            const col = statusColor[t.status as string] || '#888';
            const isPostponed = t.status === 'postponed' || t.status === 'waiting_parts';
            const pausedWfMap: Record<string, LucrareInCurs> = window._calPausedWfs || {};
            const pausedWf = t.interval_id ? pausedWfMap[t.interval_id] : null;
            const isResumable = !isPostponed
                && (t.status === 'paused' || t.status === 'in_progress')
                && pausedWf
                && pausedWf.machine_id === t.machine_id;

            const reasonLabel = isPostponed
                ? (_postponeReasonLabel(t.last_reason_code) || t.postpone_reason || '—')
                : '';
            const untilLabel = isPostponed && t.postponed_until ? formatDateRO(t.postponed_until) : '';
            const tooltip = isPostponed
                ? `Amânată — motiv: ${reasonLabel}${untilLabel ? ' • reluare: ' + untilLabel : ''}`
                : (isResumable ? 'Click pentru a relua lucrarea (cronometru continuă)' : 'Click pentru a deschide procedura');

            card.style.cssText = `padding:10px 12px;border-radius:10px;border:1px solid var(--border);background:var(--surface);margin-bottom:6px;border-left:4px solid ${col};cursor:pointer`;
            card.draggable = true;
            card.dataset.taskId = String(t.id);
            // Purtate pe card ca să le poată citi și tragerea, nu doar butonul.
            card.dataset.mid = String(t.machine_id || '');
            card.dataset.ivid = String(t.interval_id || '');
            card.dataset.itext = String(t.interval_text || t.frequency || '');
            card.dataset.action = String(t.description || t.frequency || 'Lucrare');
            card.title = tooltip;

            const postponeBadge = isPostponed
                ? '<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.7rem;font-weight:700;background:rgba(251,146,60,.18);color:#fb923c;border:1px solid rgba(251,146,60,.4);margin-left:4px">⏸ Amânată</span>'
                : (isResumable
                    ? '<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.7rem;font-weight:700;background:rgba(245,158,11,.18);color:#f59e0b;border:1px solid rgba(245,158,11,.4);margin-left:4px">⏸ În pauză</span>'
                    : '');

            const postponeDetail = isPostponed
                ? `<div style="font-size:.75rem;color:var(--text-muted);margin-top:2px">⏸ <em>${escHtml(reasonLabel)}</em>${untilLabel ? ' • Reluare: <strong>' + escHtml(untilLabel) + '</strong>' : ''}</div>`
                : '';

            /* Butonul de mutare există pe ORICE ecran, nu doar pe telefon: tragerea
               HTML5 nu se declanșează din atingere pe Android, deci pe telefonul
               mecanicului — singurul loc unde se lucrează efectiv — reprogramarea
               n-avea nicio cale. Acum tragerea și butonul duc în același dialog. */
            const btnMuta = `<button type="button" class="btn btn-sm cal-task-move-btn" data-taskid="${t.id}" title="Mută lucrarea pe altă zi" style="font-size:.75rem;padding:4px 10px;min-height:32px;background:rgba(99,102,241,.15);color:#a5b4fc;border:1px solid rgba(99,102,241,.4);border-radius:7px;cursor:pointer">📅 Mută</button>`;

            let actionBtns = '';
            if (isPostponed) {
                actionBtns = `<button type="button" class="btn btn-sm cal-clear-postpone-btn" data-taskid="${t.id}" title="Reia lucrarea în lista zilei" style="font-size:.75rem;padding:4px 10px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:7px;cursor:pointer">🔓 Închide amânare</button>
                    <button type="button" class="btn btn-sm cal-task-tl-btn" data-taskid="${t.id}" data-mid="${escHtml(t.machine_id || '')}" data-ivid="${t.interval_id || ''}" title="Istoric amânări" style="font-size:.75rem;padding:4px 8px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border);border-radius:7px;cursor:pointer">🕐</button>`;
            } else if (isResumable) {
                actionBtns = `<button type="button" class="btn btn-sm cal-task-resume-btn" data-taskid="${t.id}" data-wfid="${pausedWf!.id}" data-mid="${escHtml(t.machine_id || '')}" data-ivid="${t.interval_id || ''}" title="Reia lucrarea (cronometru continuă)" style="font-size:.75rem;padding:4px 12px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:7px;cursor:pointer">▶ Reia</button>
                    <button type="button" class="btn btn-sm cal-task-tl-btn" data-taskid="${t.id}" data-mid="${escHtml(t.machine_id || '')}" data-ivid="${t.interval_id || ''}" title="Istoric" style="font-size:.75rem;padding:4px 8px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border);border-radius:7px;cursor:pointer">🕐</button>${btnMuta}`;
            } else {
                actionBtns = btnMuta;
            }

            const hintLine = isPostponed
                ? '<span style="font-size:.68rem;color:var(--text-muted)">Click pe card → istoric amânări</span>'
                : (isResumable
                    ? '<span style="font-size:.68rem;color:var(--text-muted)">Click → reia direct cu cronometru</span>'
                    : '<span style="font-size:.68rem;color:var(--text-muted)">📅 Mută sau trage pe altă zi</span>');

            card.innerHTML = `
                <div style="font-size:.75rem;color:var(--text-muted)"><strong>${escHtml(t.machine_id || '')}</strong> ${t.line_id ? '— linia ' + escHtml(t.line_id) : ''}
                    <span style="display:inline-block;padding:1px 6px;border-radius:6px;font-size:.68rem;font-weight:700;background:${col};color:#fff;margin-left:4px">${escHtml(_STARE_LUCRARE[t.status as string] || t.status || '')}</span>${postponeBadge}${t.priority === 'high' ? ' <span style="display:inline-block;padding:1px 6px;border-radius:6px;font-size:.68rem;font-weight:700;background:var(--red);color:#fff">URGENTĂ</span>' : ''}
                </div>
                <div style="font-weight:600;font-size:.9rem;margin:2px 0">${escHtml(t.description || t.frequency || 'Lucrare')}</div>
                <div style="font-size:.75rem;color:var(--text-muted)">${t.assigned_to ? 'Pentru: <strong>' + escHtml(t.assigned_to) + '</strong>' : 'Nealocată'}${t.assigned_by ? ' · alocată de ' + escHtml(t.assigned_by) : ''}</div>
                ${t.notes && String(t.notes).trim() ? `<div style="font-size:.78rem;margin-top:2px"><em>${escHtml(String(t.notes).trim())}</em></div>` : ''}
                ${postponeDetail}
                ${actionBtns ? `<div style="display:flex;gap:5px;flex-wrap:wrap;margin-top:6px;align-items:center">${actionBtns}<span style="flex:1"></span>${hintLine}</div>` : `<div style="margin-top:3px">${hintLine}</div>`}
            `;
            card.addEventListener('dragstart', (ev: DragEvent) => {
                if (!ev.dataTransfer) return;
                ev.dataTransfer.effectAllowed = 'move';
                ev.dataTransfer.setData('application/x-operator-task', String(t.id));
                ev.dataTransfer.setData('text/x-orig-date', dateKey!);
                card.style.opacity = '0.5';
            });
            card.addEventListener('dragend', () => { card.style.opacity = '1'; });
            // Card click — direct resume for paused/in_progress, timeline for postponed,
            // open procedure for pending/assigned. Buttons stop propagation themselves.
            card.addEventListener('click', async (ev) => {
                const tgt = ev.target as HTMLElement;
                if (tgt && tgt.closest('button')) return;
                if (isPostponed) {
                    _showPostponeTimeline(t.id, t.machine_id || '', t.interval_id || 0);
                    return;
                }
                if (!t.interval_id || !t.machine_id) {
                    toast('Acest task nu are interval asociat — deschide-l din Lucrările mele', 'info');
                    return;
                }
                try {
                    await _ensureMachineCache();
                    const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(t.machine_id));
                    const iv = (intervals || []).find(x => Number(x.id) === Number(t.interval_id));
                    if (!iv) { toast('Intervalul nu a fost găsit', 'error'); return; }
                    const m = (_allMachinesCache || []).find(x => x.id === t.machine_id);
                    const machineName = m ? (m.name || t.machine_id) : t.machine_id;
                    if (isResumable && pausedWf) {
                        toast('Se reia procedura cu cronometrul în curs…');
                        openRevisionDetailView(iv, t.machine_id, machineName, pausedWf.id);
                    } else {
                        openRevisionDetailView(iv, t.machine_id, machineName);
                    }
                } catch (e) {
                    toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
                }
            });
            listSched.appendChild(card);
        });
        if ((scheduled || []).length > 0) {
            const sep = document.createElement('div');
            sep.style.cssText = 'font-size:.82rem;color:var(--text-muted);margin:8px 0 6px 0;padding:4px 0;border-bottom:1px dashed var(--border)';
            sep.innerHTML = '🔧 <strong>Mentenanțe programate (interval)</strong>';
            listSched.appendChild(sep);
        }
    }

    (scheduled || []).forEach(s => {
        const lineId = s.line_id || machineToLine[s.machine_id as string] || '';
        const lineLabel = lineId ? (_lineLabels[lineId] || lineId) : '—';
        const isCritical = s.critical === 1;
        const needsParts = s.needs_parts === 1;
        const uid = 'iv-' + (s.interval_id || 0);
        const partsContainerId = 'cparts-' + (s.interval_id || Math.random().toString(36).slice(2));

        const typeBadge = needsParts
            ? '<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(251,146,60,.15);color:#fb923c;border:1px solid rgba(251,146,60,.3)">🔧 Schimb piese</span>'
            : '<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:rgba(96,165,250,.12);color:#60a5fa;border:1px solid rgba(96,165,250,.3)">👁 Verificare</span>';

        // Audit 2.2 — panoul de zi ignora complet `completed_today` și scotea
        // butoane de start pentru lucrări bifate acum câteva ore: pe 19 august,
        // 4 din 5 lucrări făcute pe M4 apăreau de două ori pe același ecran,
        // o dată „de făcut" și o dată la „efectuate". Steagul vine gata calculat
        // pe interval_id din /api/mytasks/daily, aceeași sursă ca Lucrările mele.
        const isDone = !!s.completed_today;
        const doneBadge = isDone
            ? ' <span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:#00e676;color:#000">✅ TERMINAT</span>'
            : '';

        // 30 sep 2026: butoanele de mai jos aveau `data-taskid=""` și nici `data-critical` /
        // `data-cat`. „Am terminat" scria jurnalul dar lăsa lucrarea alocată deschisă, „Amân"
        // crea o a doua lucrare amânată, iar o mentenanță CRITICĂ bifată de aici pleca drept
        // rutină („auto") și nu ajungea niciodată la „De validat".
        const card = document.createElement('div');
        card.className = 'mytask-card';
        card.style.cssText = 'padding:12px 14px;border-radius:10px;border:1px solid var(--border);background:var(--surface);margin-bottom:8px';
        if (isCritical) { card.style.borderLeftColor = 'var(--red)'; card.style.borderLeftWidth = '4px'; }
        if (isDone) { card.style.opacity = '.6'; card.style.borderLeftColor = '#00e676'; card.style.borderLeftWidth = '4px'; }

        card.innerHTML = `
            <div style="font-size:.78rem;color:var(--text-muted)">${escHtml(lineLabel)} — <strong>${escHtml(s.machine_id)}</strong> ${typeBadge}${doneBadge} ${isCritical ? '<span style="color:var(--red);font-weight:700">CRITICĂ</span>' : ''}</div>
            <div style="font-weight:600;font-size:.95rem;margin:3px 0">${escHtml(s.action_ro || 'Lucrare mentenanță')}</div>
            ${s.interval_text ? `<div style="font-size:.82rem;color:var(--text-muted)">Interval: ${escHtml(s.interval_text)}${s.interval_hours ? ' (' + s.interval_hours + 'h)' : ''}</div>` : ''}
            ${needsParts && s.interval_id ? `<div id="${partsContainerId}" class="mytask-parts-zone" style="margin-top:4px;font-size:.82rem" data-ivid="${s.interval_id}" data-mid="${escHtml(s.machine_id)}" data-loaded="false"></div>` : ''}
            <div style="display:flex;gap:5px;flex-wrap:wrap;margin-top:8px">
                ${s.document_id && s.page_number ? `<a href="/api/documents/${s.document_id}/view?page=${s.page_number}" target="_blank" class="btn btn-sm" style="font-size:.8rem;padding:5px 10px;background:rgba(0,180,255,.12);border:1px solid rgba(0,180,255,.35);color:#00b4ff;text-decoration:none;border-radius:8px">📖 Manual p.${s.page_number}</a>` : ''}
                ${isDone ? '' : `<button type="button" class="btn btn-green btn-sm mytask-start-btn" data-uid="${uid}" data-mid="${escHtml(s.machine_id)}" data-ivid="${s.interval_id || ''}" data-action="${escHtml(s.action_ro || '')}" data-ih="${s.interval_hours || 0}" data-taskid="${s.task_id || ''}" style="font-size:.8rem;padding:5px 10px">▶ Încep</button>
                <button type="button" class="btn btn-sm mytask-done-btn" data-uid="${uid}" data-mid="${escHtml(s.machine_id)}" data-ivid="${s.interval_id || ''}" data-action="${escHtml(s.action_ro || '')}" data-ih="${s.interval_hours || 0}" data-taskid="${s.task_id || ''}" data-critical="${isCritical ? 1 : 0}" data-cat="${escHtml(s.category || '')}" style="font-size:.8rem;padding:5px 10px;background:#00e676;color:#000;font-weight:700;border:none;border-radius:8px">✓ Am terminat</button>
                <button type="button" class="btn btn-sm mytask-postpone-btn" data-uid="${uid}" data-mid="${escHtml(s.machine_id)}" data-ivid="${s.interval_id || ''}" data-itext="${escHtml(s.interval_text || '')}" data-action="${escHtml(s.action_ro || '')}" data-origdate="${escHtml(s.due_date || dateKey)}" data-taskid="${s.task_id || ''}" style="font-size:.8rem;padding:5px 10px;background:#6366f1;color:#fff;border:none;border-radius:8px">⏸ Amân</button>`}
                ${s.interval_id ? `<button type="button" class="btn btn-sm mytask-timeline-btn" data-taskid="${s.task_id || ''}" data-mid="${escHtml(s.machine_id || '')}" data-ivid="${s.interval_id || ''}" title="Istoric amânări" style="font-size:.8rem;padding:5px 8px;background:rgba(255,255,255,.06);color:var(--text-main);border:1px solid var(--border);border-radius:8px">🕐</button>` : ''}
            </div>
        `;
        listSched.appendChild(card);
    });

    listSched.querySelectorAll('.mytask-start-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskStartFromCalendar(btn));
    });
    listSched.querySelectorAll('.mytask-done-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskQuickComplete(btn));
    });
    listSched.querySelectorAll('.mytask-postpone-btn').forEach(btn => {
        btn.addEventListener('click', () => _myTaskPostponeFromCalendar(btn as HTMLElement));
    });
    listSched.querySelectorAll('.mytask-timeline-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const el = btn as HTMLElement;
            const tid = parseInt(el.dataset.taskid || '0') || 0;
            const mid = el.dataset.mid || '';
            const ivid = parseInt(el.dataset.ivid || '0') || 0;
            _showPostponeTimeline(tid, mid, ivid);
        });
    });
    listSched.querySelectorAll('.cal-task-move-btn').forEach(btn => {
        btn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const card = (btn as HTMLElement).closest('.cal-task-card') as HTMLElement | null;
            if (!card) return;
            _myTaskPostponeFromCalendar({
                dataset: {
                    mid: card.dataset.mid || '', ivid: card.dataset.ivid || '',
                    itext: card.dataset.itext || '', action: card.dataset.action || '',
                    origdate: dateKey, taskid: card.dataset.taskId || '',
                },
            });
        });
    });
    listSched.querySelectorAll('.cal-clear-postpone-btn').forEach(btn => {
        btn.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            const tid = (btn as HTMLElement).dataset.taskid;
            if (!tid) return;
            if (!await customConfirm('Sigur închizi amânarea? Task-ul revine în lista zilei.', 'Închide amânare')) return;
            try {
                await api('/api/operator_tasks/' + tid + '/clear_postpone', { method: 'POST' });
                toast('Amânare închisă. Task-ul a revenit în lista zilei.');
                await loadCalendar();
            } catch (e) {
                toast('Eroare: ' + ((e as Error)?.message || String(e)), 'error');
            }
        });
    });
    listSched.querySelectorAll('.cal-task-tl-btn').forEach(btn => {
        btn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const el = btn as HTMLElement;
            const tid = parseInt(el.dataset.taskid || '0') || 0;
            const mid = el.dataset.mid || '';
            const ivid = parseInt(el.dataset.ivid || '0') || 0;
            _showPostponeTimeline(tid, mid, ivid);
        });
    });
    listSched.querySelectorAll('.cal-task-resume-btn').forEach(btn => {
        btn.addEventListener('click', async (ev) => {
            ev.stopPropagation();
            const el = btn as HTMLElement;
            const machineId = el.dataset.mid || '';
            const ivId = parseInt(el.dataset.ivid || '0', 10);
            const wfId = parseInt(el.dataset.wfid || '0', 10);
            if (!ivId || !machineId || !wfId) { toast('Date lipsă pentru reluare', 'error'); return; }
            try {
                await _ensureMachineCache();
                const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(machineId));
                const iv = (intervals || []).find(x => Number(x.id) === ivId);
                if (!iv) { toast('Intervalul nu a fost găsit', 'error'); return; }
                const m = (_allMachinesCache || []).find(x => x.id === machineId);
                const machineName = m ? (m.name || machineId) : machineId;
                toast('Se redeschide procedura…');
                openRevisionDetailView(iv, machineId, machineName, wfId);
            } catch (e) { toast('Eroare la reluare: ' + ((e as Error)?.message || String(e)), 'error'); }
        });
    });
    _loadPartsForVisibleCards(listSched as HTMLElement);

    (performed || []).forEach(p => {
        const card = document.createElement('div');
        card.style.cssText = 'padding:10px 14px;border-radius:10px;border:1px solid var(--border);background:rgba(0,230,118,.06);border-left:4px solid #00e676;margin-bottom:8px';
        card.innerHTML = `
            <div style="font-size:.78rem;color:var(--text-muted)"><strong>${escHtml(p.machine_id)}</strong> <span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.72rem;font-weight:700;background:#00e676;color:#000">✅ EFECTUAT</span></div>
            <div style="font-weight:600;font-size:.95rem;margin:3px 0">${escHtml(p.action || '')}</div>
            ${p.technician ? `<div style="font-size:.82rem;color:var(--text-muted)">Operator: ${escHtml(p.technician)}</div>` : ''}
        `;
        listPerf.appendChild(card);
    });

    // 30 sep 2026: rândul ăsta rescria lista peste cardurile „Lucrări alocate" de mai sus —
    // o intervenție programată pentru un mecanic, într-o zi fără mentenanță de interval (sau
    // cu filtrul pe alt utilaj), arăta „Nicio mentenanță programată", deși insigna zicea 📋1
    if (scheduled.length === 0 && opTasksToday.length === 0) listSched.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span>Nicio mentenanță programată</div>';
    if (performed.length === 0) listPerf.innerHTML = '<div style="font-size:.85rem;color:var(--text-muted);padding:8px">Nicio mentenanță efectuată</div>';
    panel.classList.remove('hidden');
}

export function goToOverviewDetail(machineId: string) {
    _closeProactivePanel();
    const lineId = machineToLine[machineId] || machineId;
    $$('.section').forEach(s => s.classList.remove('active'));
    $('#sec-overview')?.classList.add('active');
    document.querySelectorAll('.nav-links a').forEach(a => a.classList.remove('active'));
    const overviewLink = document.querySelector('.nav-links a[data-section="overview"]');
    if (overviewLink) overviewLink.classList.add('active');
    $('#page-title')!.textContent = (overviewLink && overviewLink.textContent.trim()) || 'Overview Fabrica';
    $('#cal-day-panel')?.classList.add('hidden');
    loadOverview().then(() => {
        const lineDiv = document.querySelector(`[data-line-id="${lineId}"]`);
        if (lineDiv) {
            const card = lineDiv.querySelector(`[data-eq="${machineId}"]`);
            if (card) (card as HTMLElement).click();
        } else {
            openLineDetail(lineId);
        }
    });
}

export async function openProcedureViewer(intervalId: number | null, machineId: string, actionRo: string): Promise<void> {
    let overlay = document.getElementById('procedure-viewer-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'procedure-viewer-overlay';
        overlay.className = 'detail-overlay';
        overlay.innerHTML = `
            <div class="detail-panel" style="max-width:700px">
                <div class="detail-header">
                    <div class="detail-title-block">
                        <span class="detail-line-id" id="pv-machine"></span>
                        <span class="detail-line-product" id="pv-action"></span>
                    </div>
                    <button class="alarm-close" id="pv-close">✕</button>
                </div>
                <div class="detail-content" id="pv-content" style="padding:16px 20px"></div>
            </div>`;
        document.body.appendChild(overlay);
        overlay.querySelector('#pv-close')?.addEventListener('click', () => overlay!.classList.add('hidden'));
    }

    const machineEl = overlay.querySelector('#pv-machine')!;
    const actionEl = overlay.querySelector('#pv-action')!;
    const contentEl = overlay.querySelector('#pv-content')!;

    machineEl.textContent = machineId;
    actionEl.textContent = actionRo || 'Procedură mentenanță';
    contentEl.innerHTML = '<div style="text-align:center;padding:30px"><div class="typing-dots"><span></span><span></span><span></span></div><p style="margin-top:8px;color:var(--text-muted)">Se încarcă...</p></div>';
    overlay.classList.remove('hidden');

    let resolvedIntervalId = intervalId;

    if (!resolvedIntervalId && machineId) {
        try {
            const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(machineId));
            if (intervals && intervals.length > 0) {
                const actionLower = (actionRo || '').toLowerCase();
                const match = intervals.find(iv => {
                    const ivAction = (iv.action_ro || '').toLowerCase();
                    return actionLower && ivAction && (
                        ivAction.includes(actionLower.substring(0, 20)) ||
                        actionLower.includes(ivAction.substring(0, 20))
                    );
                });
                if (match) resolvedIntervalId = match.id;
            }
        } catch (_e) { /* silent */ }
    }

    if (!resolvedIntervalId) {
        contentEl.innerHTML = `
            <div style="padding:20px">
                <div style="margin-bottom:16px">
                    <h3 style="color:var(--cyan);margin-bottom:8px">📋 ${escHtml(actionRo || 'Lucrare mentenanță')}</h3>
                    <p style="color:var(--text-muted);font-size:.9rem">Mașina: <strong>${escHtml(machineId)}</strong></p>
                    <p style="color:var(--text-muted);font-size:.85rem;margin-top:8px">
                        Această lucrare a fost înregistrată manual și nu este legată de un interval de mentenanță din sistem.
                    </p>
                </div>
                <div style="border-top:1px solid var(--border);padding-top:16px">
                    <h4 style="color:var(--text-muted);font-size:.85rem;margin-bottom:8px">Intervale de mentenanță disponibile pentru ${escHtml(machineId)}:</h4>
                    <div id="pv-available-intervals" style="font-size:.85rem">Se încarcă...</div>
                </div>
            </div>`;
        try {
            const intervals: IntervalPlan[] = await api('/api/maintenance_intervals?machine_id=' + encodeURIComponent(machineId));
            const container = overlay.querySelector('#pv-available-intervals');
            if (container && intervals && intervals.length > 0) {
                container.innerHTML = intervals.slice(0, 15).map(iv =>
                    `<div style="padding:6px 0;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:8px">
                        <span style="color:var(--cyan);min-width:50px;font-weight:600">${iv.interval_hours || 0}h</span>
                        <span style="flex:1">${escHtml(iv.action_ro || '')}</span>
                        <a href="#" class="pv-iv-link" data-ivid="${iv.id}" style="color:var(--cyan);font-size:.8rem;white-space:nowrap">Procedură →</a>
                    </div>`
                ).join('');
                container.querySelectorAll('.pv-iv-link').forEach((link: Element) => {
                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        const ivid = parseInt((link as HTMLElement).dataset.ivid || '0');
                        if (ivid) openProcedureViewer(ivid, machineId, actionRo);
                    });
                });
            } else if (container) {
                container.innerHTML = '<p style="color:var(--text-muted)">Nu există intervale definite pentru această mașină.</p>';
            }
        } catch (_e) { /* silent */ }
        return;
    }

    try {
        const proc: FisaInterval | null = await api(`/api/procedures/for-interval/${resolvedIntervalId}`);
        if (!proc) {
            contentEl.innerHTML = `
                <div style="text-align:center;padding:30px">
                    <div style="font-size:2.5rem;margin-bottom:12px">📋</div>
                    <h3 style="color:var(--cyan);margin-bottom:8px">Nu există procedură pentru acest interval</h3>
                    <p style="color:var(--text-muted);margin-bottom:16px">AI poate genera automat pașii pe baza documentației tehnice.</p>
                    <button class="btn btn-primary" id="pv-generate-ai" style="font-size:.9rem;padding:10px 24px">🤖 Generează cu AI</button>
                </div>`;
            overlay.querySelector('#pv-generate-ai')?.addEventListener('click', async () => {
                const btn = overlay!.querySelector('#pv-generate-ai') as HTMLButtonElement;
                btn.textContent = '⏳ Se generează...';
                btn.disabled = true;
                try {
                    const result = await fetch(`/api/procedures/generate-from-ai`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ interval_id: resolvedIntervalId, machine_id: machineId })
                    });
                    const data: FisaGenerata = await result.json();
                    if (data.error) throw new Error(data.error);
                    toast('Procedură generată!');
                    openProcedureViewer(resolvedIntervalId, machineId, actionRo);
                } catch (e: unknown) {
                    toast('Eroare generare: ' + (e instanceof Error ? e.message : String(e)), 'error');
                    btn.textContent = '🤖 Generează cu AI';
                    btn.disabled = false;
                }
            });
            return;
        }

        _renderProcedureSteps(overlay, contentEl, proc, resolvedIntervalId, machineId, actionRo);

    } catch (e: unknown) {
        contentEl.innerHTML = `<div style="padding:20px;color:var(--red)">Eroare la încărcare: ${e instanceof Error ? e.message : String(e)}</div>`;
    }
}

function _renderProcedureSteps(overlay: HTMLElement, contentEl: Element, proc: FisaInterval, intervalId: number | null, machineId: string, actionRo: string): void {
    const steps = proc.steps || [];
    let stepsHtml = '';
    steps.forEach((step, idx: number) => {
        stepsHtml += `
            <div class="pv-step" data-step-id="${step.id}" style="display:flex;gap:10px;padding:10px 0;border-bottom:1px solid var(--border);align-items:flex-start">
                <span style="background:var(--cyan);color:#000;font-weight:700;min-width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.8rem;flex-shrink:0">${idx + 1}</span>
                <div style="flex:1">
                    <textarea class="pv-step-text" data-step-id="${step.id}" rows="2" style="width:100%;background:var(--surface);border:1px solid var(--border);border-radius:6px;padding:6px 8px;color:var(--text);font-size:.85rem;resize:vertical">${escHtml(step.instruction || step.description || '')}</textarea>
                    ${step.duration_minutes ? `<span style="font-size:.75rem;color:var(--text-muted)">~${step.duration_minutes} min</span>` : ''}
                    ${step.photo_url ? `<img src="${step.photo_url}" style="max-width:120px;border-radius:4px;margin-top:4px" alt="Pas ${idx + 1}">` : ''}
                </div>
            </div>`;
    });

    const statusLabel = proc.status === 'active' ? '<span class="badge badge-green">Activă</span>' :
        proc.status === 'draft' ? '<span class="badge badge-yellow">Draft</span>' : `<span class="badge">${proc.status}</span>`;
    const aiLabel = proc.ai_generated ? ' <span title="Generată de AI" style="font-size:.8rem">🤖 AI</span>' : '';

    contentEl.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
            <div>
                <strong style="font-size:1rem">${escHtml(proc.title || actionRo)}</strong>
                ${statusLabel}${aiLabel}
            </div>
            <span style="font-size:.8rem;color:var(--text-muted)">${steps.length} pași${proc.estimated_minutes ? ' · ~' + proc.estimated_minutes + ' min' : ''}</span>
        </div>
        <div id="pv-steps-list">${stepsHtml || '<p style="color:var(--text-muted)">Procedura nu are pași definiți.</p>'}</div>
        <div style="display:flex;gap:8px;margin-top:16px;flex-wrap:wrap">
            <button class="btn btn-green btn-sm" id="pv-save-btn">💾 Salvează modificările</button>
            ${proc.status === 'draft' ? '<button class="btn btn-sm" id="pv-activate-btn" style="background:#8b5cf6;color:#fff;border:none;border-radius:8px">✓ Activează procedura</button>' : ''}
        </div>
        <div id="pv-save-status" style="margin-top:8px;font-size:.85rem"></div>`;

    overlay.querySelector('#pv-save-btn')?.addEventListener('click', async () => {
        const textareas = overlay.querySelectorAll('.pv-step-text') as NodeListOf<HTMLTextAreaElement>;
        const updates: { id: number; instruction: string }[] = [];
        textareas.forEach(ta => {
            updates.push({ id: parseInt(ta.dataset.stepId || '0'), instruction: ta.value.trim() });
        });
        try {
            for (const upd of updates) {
                await fetch(`/api/procedures/${proc.id}/steps/${upd.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ instruction: upd.instruction })
                });
            }
            const statusDiv = overlay.querySelector('#pv-save-status')!;
            statusDiv.innerHTML = '<span style="color:var(--green)">✅ Modificări salvate!</span>';
            toast('Procedură actualizată!');
        } catch (e: unknown) {
            toast('Eroare salvare: ' + (e instanceof Error ? e.message : String(e)), 'error');
        }
    });

    overlay.querySelector('#pv-activate-btn')?.addEventListener('click', async () => {
        try {
            await fetch(`/api/procedures/${proc.id}/activate`, {
                method: 'POST', credentials: 'include'
            });
            toast('Procedură activată!');
            openProcedureViewer(intervalId, machineId, actionRo);
        } catch (e: unknown) {
            toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
        }
    });
}

export function renderCalendarTable(intervals: IntervalPlan[], logs: RandMaintenanceLog[]) {
    const filterMachine = ($('#cal-machine-filter') as HTMLInputElement)?.value || '';
    const filterLine = ($('#cal-line-filter') as HTMLInputElement)?.value || '';
    const filterCategory = ($('#cal-category-filter') as HTMLInputElement)?.value || '';
    const criticalOnly = ($('#cal-critical-only') as HTMLInputElement)?.checked || false;
    // 3 aug 2026 (raport Dan „multe mentenanțe par scoase"): lista completă e
    // vedere de REFERINȚĂ — arată TOATE intervalele active, cu insignă de stare
    // (⏸ pauză tehnică / 🔧 mentenanță / inactivă) în loc să le ascundă.
    // Planul de execuție (zilnic/grid) rămâne cel care sare mașinile în pauză.
    let data = (intervals || []).slice();
    if (filterMachine) {
        data = data.filter(i => i.machine_id === filterMachine);
    } else if (filterLine) {
        data = data.filter(i => {
            const line = machineToLine[i.machine_id as string] || '';
            return line === filterLine || line.split(',').some((l: string) => l.trim() === filterLine);
        });
    }
    if (filterCategory) data = data.filter(i => (i.category || '') === filterCategory);
    if (criticalOnly) data = data.filter(i => i.critical);

    const tbody = $('#calendar-table tbody');
    if (!tbody) return;
    tbody.innerHTML = '';

    if (data.length === 0) {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state">Nicio înregistrare găsită</td></tr>';
        return;
    }

    data.forEach(iv => {
        const hours = state.machineHours[iv.machine_id as string] || 0;
        let status = '';
        let statusBadge = '';
        const activeWf = (window._calPausedWfs || {})[iv.id];

        if (activeWf) {
            const wfPct = activeWf.steps_total > 0 ? Math.round((activeWf.steps_completed / activeWf.steps_total) * 100) : 0;
            statusBadge = `
                <div class="wear-bar-wrap">
                    <div class="wear-bar">
                        <div class="wear-fill warning" style="width:${wfPct}%"></div>
                    </div>
                    <span class="wear-pct">${wfPct}%</span>
                </div>
                <div>⏸ ÎN PAUZĂ (${activeWf.steps_completed}/${activeWf.steps_total} pași)</div>
            `;
            status = 'PAUZA';
        } else if (iv.stare && (iv.stare.mod === 'ore' || iv.stare.mod === 'calendar' || iv.stare.depasit)) {
            // 1 nov 2026 (auditul surselor de ore): starea din sursa unică — aceeași cu „Intervale”, Restanțe,
            // panourile; înainte aici totul pe ore (lunarul ca 720 h), cu „APROAPE” de la 80 %
            const st = iv.stare;
            const pct = Math.min(Math.max(st.depasit ? Math.max(100, st.procent ?? 100) : (st.procent ?? 0), 0), 130);
            const sevClass = st.nivel === 'depasit' ? 'critical' : st.nivel === 'aproape' ? 'warning' : 'normal';
            if (st.nivel === 'depasit') status = 'DEPĂȘIT';
            else if (st.nivel === 'aproape') status = 'APROAPE';
            statusBadge = `
                <div class="wear-bar-wrap">
                    <div class="wear-bar">
                        <div class="wear-fill ${sevClass}" style="width:${Math.min(pct, 100)}%"></div>
                    </div>
                    <span class="wear-pct">${pct}%</span>
                </div>
                <div>${escHtml(st.text)}</div>
            `;
        } else if (iv.stare && iv.stare.mod === 'zilnic') {
            statusBadge = `<span class="badge badge-gray">${escHtml(iv.stare.text)}</span>`;
        } else if (iv.interval_hours) {
            const prog = intervalHoursProgress(iv as ProgresOre, hours);   // interval_hours nenul aici (ramura `else if`)
            const ratio = iv.interval_hours > 0 ? prog.ratio : 0;
            const pct = Math.min(Math.max(Math.round(ratio * 100), 0), 130);
            let sevClass = 'normal';
            let labelText = '';

            if (hours <= 0) {
                labelText = 'Introdu ore contor';
                sevClass = 'normal';
            } else if (prog.overdue) {
                status = 'DEPĂȘIT';
                sevClass = 'critical';
                labelText = 'DEPĂȘIT';
            } else if (ratio >= 0.8) {
                status = 'APROAPE';
                sevClass = 'warning';
                labelText = 'APROAPE';
            } else {
                const remaining = Math.max(0, prog.remaining);
                sevClass = 'normal';
                labelText = `${Math.round(remaining)}h rămase`;
            }

            statusBadge = `
                <div class="wear-bar-wrap">
                    <div class="wear-bar">
                        <div class="wear-fill ${sevClass}" style="width:${Math.min(pct, 100)}%"></div>
                    </div>
                    <span class="wear-pct">${pct}%</span>
                </div>
                <div>${labelText}</div>
            `;
        } else {
            statusBadge = '<span class="badge badge-gray">N/A</span>';
        }

        // insigna de stare a mașinii — vizibilă, nu ascunsă (3 aug 2026)
        const stM = iv.machine_state || 'active';
        if (stM === 'technical_pause') statusBadge = '<span class="badge badge-yellow" title="Mașina e oprită — ceasul intervalelor e înghețat">⏸ pauză tehnică</span>' + statusBadge;
        if (stM === 'no_data') statusBadge = '<span class="badge" style="background:rgba(148,163,184,.18);color:#cbd5e1;border:1px solid rgba(148,163,184,.35)" title="Utilajul nu mai trimite citiri. Ceasul intervalelor e înghețat la ultima citire — scadențele nu se mai calculează dintr-un contor oprit.">🔇 fără date</span>' + statusBadge;
        else if (stM === 'maintenance') statusBadge = '<span class="badge badge-blue" title="Mentenanță în curs pe mașină">🔧 în mentenanță</span>' + statusBadge;
        else if (stM === 'inactive') statusBadge = '<span class="badge badge-gray" title="Linie nepornită">inactivă</span>' + statusBadge;

        const tr = document.createElement('tr');
        if (status === 'DEPĂȘIT') tr.classList.add('critical');
        if (status === 'PAUZA') tr.classList.add('wf-paused-row');
        tr.innerHTML = `
            <td><strong>${escHtml(iv.machine_id)}</strong></td>
            <td>${escHtml(iv.interval_text)}</td>
            <td>${escHtml(iv.action_ro)}</td>
            <td><span class="badge badge-blue">${escHtml(iv.category)}</span></td>
            <td>${escHtml(iv.part_number || iv.lubricant)}</td>
            <td>${escHtml(iv.quantity)}</td>
            <td>${statusBadge}</td>
            <td style="white-space:nowrap">
                <button class="btn-done mark-done"
                    data-machine="${escHtml(iv.machine_id)}"
                    data-action="${escHtml(iv.action_ro || '')}"
                    data-ivid="${iv.id || ''}"
                    data-interval="${iv.interval_hours || 0}">Efectuată</button>
                ${(state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer'))
                    ? `<button class="btn btn-sm cal-assign-btn" data-ivid="${iv.id}" data-machine="${escHtml(iv.machine_id)}" data-action="${escHtml(iv.action_ro || '')}" style="font-size:.75rem;padding:3px 8px;margin-left:4px;background:#6366f1;color:#fff;border:none;border-radius:6px">👤 Alocă</button>` : ''}
            </td>
        `;
        tbody.appendChild(tr);
    });

    $$('.mark-done').forEach(btn => {
        btn.addEventListener('click', () => openModal(btn as HTMLElement));
    });
    $$('.cal-assign-btn').forEach(btn => {
        btn.addEventListener('click', () => _openAssignDialog(parseInt(btn.dataset.ivid!), btn.dataset.machine, btn.dataset.action));
    });
}

export async function _openAssignDialog(intervalId: number, machineId: string|null|undefined, actionText: string|null|undefined) {
    let existing = document.getElementById('cal-assign-overlay');
    if (existing) existing.remove();

    let usersHtml = '<option value="">— Selectează operator —</option>';
    try {
        const users = await api('/api/auth/usernames');
        (users || []).forEach((u: string|null|undefined) => { usersHtml += `<option value="${escHtml(u)}">${escHtml(u)}</option>`; });
    } catch (e) { /* ignore */ }

    // 🌙 Ziua LOCALĂ, nu UTC (audit 2026-08-19, constatarea 2.7): în tura de noapte
    // (00:00–03:00 local) câmpul „Data scadentă" se pre-completa cu ziua de ieri.
    const dataAzi = todayStr();

    const overlay = document.createElement('div');
    overlay.id = 'cal-assign-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
    <div class="proc-container" style="max-width:420px">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">👤 Alocă lucrare</h2>
            <button class="proc-close" id="cal-assign-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:16px">
            <div style="margin-bottom:10px;font-size:.9rem;color:var(--text-muted)">
                <strong>${escHtml(machineId)}</strong> — ${escHtml(actionText)}
            </div>
            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Operator:</label>
                <select id="cal-assign-user" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${usersHtml}</select>
            </div>
            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Data scadentă: <span style="font-weight:400;color:var(--text-muted);font-size:.78rem">(zi/lună/an)</span></label>
                <input type="date" id="cal-assign-date" value="${dataAzi}" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                <div id="cal-assign-date-display" style="font-size:.8rem;color:var(--accent);margin-top:4px;font-weight:600">→ ${escHtml(formatDateRO(new Date()))}</div>
            </div>
            <div style="margin-bottom:12px">
                <label style="font-weight:600;font-size:.95rem">Observații (opțional):</label>
                <textarea id="cal-assign-notes" rows="2" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px;resize:vertical"></textarea>
            </div>
            <button class="btn btn-green" id="cal-assign-save" style="width:100%;padding:10px">✅ Alocă</button>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    document.getElementById('cal-assign-close')!.addEventListener('click', () => overlay.remove());
    document.getElementById('cal-assign-save')!.addEventListener('click', async () => {
        const user = (document.getElementById('cal-assign-user') as HTMLInputElement).value;
        if (!user) { toast('Selectează operatorul!', 'warning'); return; }
        const dueDate = (document.getElementById('cal-assign-date') as HTMLInputElement).value;
        const notes = (document.getElementById('cal-assign-notes') as HTMLInputElement).value.trim();
        try {
            await api('/api/operator_tasks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    interval_id: intervalId,
                    machine_id: machineId,
                    assigned_to: user,
                    assigned_by: state.currentUser?.username || 'inginer',
                    due_date: dueDate,
                    // 8 aug 2026 (P4): trimitem și descrierea, și marcajul că
                    // task-ul preia ocurența de calendar din ziua aceea. Fără
                    // ele, lucrarea alocată apărea cu text gol ȘI se dubla cu
                    // ocurența originală. Serverul le completează oricum dacă
                    // lipsesc — aici le dăm explicit, ca să fie clar din apel.
                    description: actionText || '',
                    replaces_calendar_date: dueDate,
                    notes: notes
                })
            });
            toast(`Lucrarea a fost alocată lui ${user}.`);
            overlay.remove();
        } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); }
    });
}

document.getElementById('cal-new-procedure')?.addEventListener('click', _openNewProcedureForm);

export async function _openNewProcedureForm() {
    let existing = document.getElementById('proc-new-overlay');
    if (existing) existing.remove();

    await _ensureMachineCache();
    let machineOpts = '<option value="">— Selectează —</option>';
    (_allMachinesCache || []).forEach(m => { machineOpts += `<option value="${escHtml(m.id)}">${escHtml(m.id)} — ${escHtml(m.name || '')}</option>`; });

    let usersHtml = '<option value="">— Toți (nealocată) —</option>';
    try {
        const users = await api('/api/auth/usernames');
        (users || []).forEach((u: string|null|undefined) => { usersHtml += `<option value="${escHtml(u)}">${escHtml(u)}</option>`; });
    } catch (e) { /* ignore */ }

    // 🌙 Ziua LOCALĂ, nu UTC (audit 2026-08-19, constatarea 2.7) — vezi _openAssignDialog.
    const dataAzi = todayStr();

    const overlay = document.createElement('div');
    overlay.id = 'proc-new-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
    <div class="proc-container" style="max-width:600px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">+ Creează procedură de mentenanță</h2>
            <button class="proc-close" id="proc-new-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:16px;display:flex;flex-direction:column;gap:12px">
            <div style="display:flex;gap:10px;flex-wrap:wrap">
                <div style="flex:1;min-width:180px">
                    <label style="font-weight:600;font-size:.9rem">Linie:</label>
                    <select id="proc-new-line" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px"></select>
                </div>
                <div style="flex:1;min-width:180px">
                    <label style="font-weight:600;font-size:.9rem">Mașină:</label>
                    <select id="proc-new-machine" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${machineOpts}</select>
                </div>
            </div>
            <div>
                <label style="font-weight:600;font-size:.9rem">Descriere lucrare:</label>
                <textarea id="proc-new-desc" rows="3" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px;resize:vertical" placeholder="Descrie lucrarea de mentenanță..."></textarea>
            </div>
            <div style="display:flex;gap:10px;flex-wrap:wrap">
                <div style="flex:1;min-width:160px">
                    <label style="font-weight:600;font-size:.9rem">Tip:</label>
                    <select id="proc-new-type" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                        <option value="one_time">O singură dată (one-time)</option>
                        ${state.currentUser?.role === 'master'
                            ? '<option value="recurring">Recurentă (interval)</option>'
                            // serverul cere master (+2FA) pentru plan: inginerul primea „Acces insuficient"
                            // abia la salvare, după ce scrisese tot (30 sep 2026)
                            : '<option value="recurring" disabled>Recurentă — o adaugă în plan doar masterul</option>'}
                    </select>
                </div>
                <div style="flex:1;min-width:160px" id="proc-new-freq-wrap">
                    <label style="font-weight:600;font-size:.9rem">Frecvență:</label>
                    <select id="proc-new-freq" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                        <option value="zilnic|24">Zilnic (24h)</option>
                        <option value="saptamanal|168">Săptămânal (168h)</option>
                        <option value="lunar|720">Lunar (720h)</option>
                        <option value="trimestrial|2160">Trimestrial (2160h)</option>
                        <option value="semestrial|4320">Semestrial (4320h)</option>
                        <option value="anual|8640">Anual (8640h)</option>
                        <option value="custom|0">Custom (specifică ore)</option>
                    </select>
                </div>
                <div style="flex:1;min-width:120px;display:none" id="proc-new-hours-wrap">
                    <label style="font-weight:600;font-size:.9rem">Ore interval:</label>
                    <input type="number" id="proc-new-hours" min="1" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px" placeholder="ex: 500">
                </div>
            </div>
            <div id="proc-new-date-wrap">
                <label style="font-weight:600;font-size:.9rem">Data scadentă: <span style="font-weight:400;color:var(--text-muted);font-size:.78rem">(zi/lună/an)</span></label>
                <input type="date" id="proc-new-date" value="${dataAzi}" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                <div id="proc-new-date-display" style="font-size:.8rem;color:var(--accent);margin-top:4px;font-weight:600">→ ${escHtml(formatDateRO(new Date()))}</div>
            </div>
            <div>
                <label style="font-weight:600;font-size:.9rem">Pași procedură (opțional):</label>
                <div id="proc-new-steps" style="display:flex;flex-direction:column;gap:6px;margin-top:4px"></div>
                <button type="button" id="proc-new-add-step" class="btn btn-sm btn-outline" style="margin-top:6px;font-size:.8rem">+ Adaugă pas</button>
            </div>
            <div style="display:flex;gap:10px;flex-wrap:wrap">
                <div style="flex:1;min-width:140px">
                    <label style="font-weight:600;font-size:.9rem">Categorie:</label>
                    <input type="text" id="proc-new-cat" placeholder="ex: hidraulic, electric" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                </div>
                <div style="flex:1;min-width:140px">
                    <label style="font-weight:600;font-size:.9rem">Piese necesare:</label>
                    <input type="text" id="proc-new-parts" placeholder="Part number" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">
                </div>
            </div>
            <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center">
                <label style="font-weight:600;font-size:.9rem"><input type="checkbox" id="proc-new-critical"> Critică</label>
                <div style="flex:1;min-width:180px">
                    <label style="font-weight:600;font-size:.9rem">Alocă la operator:</label>
                    <select id="proc-new-operator" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${usersHtml}</select>
                </div>
            </div>
            <button class="btn btn-green" id="proc-new-save" style="width:100%;padding:12px;font-size:1rem">💾 Salvează procedura</button>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    const lineSel = document.getElementById('proc-new-line');
    const machineSel = document.getElementById('proc-new-machine');
    _populateLineSelect(lineSel);
    _linkLineToMachineSelect(lineSel, machineSel);

    const typeSel = document.getElementById('proc-new-type');
    const freqWrap = document.getElementById('proc-new-freq-wrap');
    const dateWrap = document.getElementById('proc-new-date-wrap');
    const hoursWrap = document.getElementById('proc-new-hours-wrap');
    const freqSel = document.getElementById('proc-new-freq');
    typeSel!.addEventListener('change', () => {
        freqWrap!.style.display = (typeSel as HTMLInputElement).value === 'recurring' ? '' : 'none';
        dateWrap!.querySelector('label')!.textContent = (typeSel as HTMLInputElement).value === 'recurring' ? 'Prima scadență:' : 'Data scadentă:';
    });
    freqSel!.addEventListener('change', () => {
        hoursWrap!.style.display = (freqSel as HTMLInputElement).value.startsWith('custom') ? '' : 'none';
    });
    typeSel!.dispatchEvent(new Event('change'));

    let stepIdx = 0;
    document.getElementById('proc-new-add-step')!.addEventListener('click', () => {
        stepIdx++;
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:6px;align-items:center';
        row.innerHTML = `<span style="font-size:.8rem;color:var(--text-muted);min-width:24px">${stepIdx}.</span>
            <input type="text" class="proc-step-input" placeholder="Descrie pasul..." style="flex:1;padding:6px 8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main)">
            <button type="button" style="padding:2px 8px;border:none;background:transparent;color:var(--red);cursor:pointer;font-size:1.1rem" onclick="this.parentElement.remove()">✕</button>`;
        document.getElementById('proc-new-steps')!.appendChild(row);
    });

    document.getElementById('proc-new-close')!.addEventListener('click', () => overlay.remove());

    document.getElementById('proc-new-save')!.addEventListener('click', async () => {
        const machineId = (machineSel as HTMLInputElement).value;
        const desc = (document.getElementById('proc-new-desc') as HTMLInputElement).value.trim();
        if (!machineId) { toast('Selectează mașina!', 'warning'); return; }
        if (!desc) { toast('Completează descrierea!', 'warning'); return; }

        const type = (typeSel as HTMLInputElement).value;
        const steps = Array.from(document.querySelectorAll('.proc-step-input')).map(i => (i as HTMLInputElement).value.trim()).filter(Boolean);
        const stepsJson = steps.length > 0 ? JSON.stringify(steps) : null;
        const category = (document.getElementById('proc-new-cat') as HTMLInputElement).value.trim();
        const partNumber = (document.getElementById('proc-new-parts') as HTMLInputElement).value.trim();
        const isCritical = (document.getElementById('proc-new-critical') as HTMLInputElement).checked;
        const operator = (document.getElementById('proc-new-operator') as HTMLInputElement).value;
        const dueDate = (document.getElementById('proc-new-date') as HTMLInputElement).value;
        const lineId = (lineSel as HTMLInputElement).value;

        try {
            if (type === 'recurring') {
                const freqVal = (freqSel as HTMLInputElement).value;
                const [freqText, freqHoursStr] = freqVal.split('|');
                let intervalHours = parseInt(freqHoursStr) || 0;
                if (freqText === 'custom') {
                    intervalHours = parseInt((document.getElementById('proc-new-hours') as HTMLInputElement).value) || 0;
                    if (!intervalHours) { toast('Specifică orele de interval!', 'warning'); return; }
                }
                await api('/api/maintenance_intervals', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        machine_id: machineId,
                        interval_hours: intervalHours,
                        interval_text: freqText === 'custom' ? 'la ' + intervalHours + 'h funcționare' : freqText,
                        action_ro: desc,
                        category: category,
                        part_number: partNumber,
                        critical: isCritical,
                        notes: stepsJson ? 'Pași: ' + steps.join('; ') : ''
                    })
                });
                if (operator) {
                    await api('/api/operator_tasks', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            machine_id: machineId, assigned_to: operator,
                            assigned_by: state.currentUser?.username || 'inginer',
                            due_date: dueDate, task_type: 'custom_interval',
                            description: desc, steps_data: stepsJson,
                            line_id: lineId, frequency: freqText
                        })
                    });
                }
                toast('Procedură recurentă creată cu succes!');
            } else {
                await api('/api/operator_tasks', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        machine_id: machineId,
                        assigned_to: operator || '',
                        assigned_by: state.currentUser?.username || 'inginer',
                        due_date: dueDate,
                        task_type: 'one_time',
                        description: desc,
                        steps_data: stepsJson,
                        line_id: lineId,
                        frequency: 'one-time',
                        notes: [category, partNumber].filter(Boolean).join(' | ')
                    })
                });
                toast('Task one-time creat cu succes!');
            }
            overlay.remove();
            loadCalendar();
        } catch (e) {
            toast('Eroare: ' + (e as Error).message, 'error');
        }
    });
}


/* ═══ MODAL CONFIRMARE ═════════════════════════════════════════ */
/** Câmpurile ferestrei „Am făcut” (toate există în templates/index.html). */
const _campModal = (sel: string) => $(sel) as HTMLInputElement;

/** `btn.dataset`: machine, action, interval, ivid, wflogid, taskid — și `alarmadeasa` (11 oct 2026): semnul „Am rezolvat
 * cauza” de la o alarmă prea deasă; lucrarea salvată din fereastră se leagă de el (routes/maintenance/jurnal.py). */
export function openModal(btn: { dataset: Record<string, string | undefined> }) {
    const overlay = $('#modal-overlay') as HTMLElement;
    overlay.classList.remove('hidden');
    const now = new Date();
    const today = todayStr();
    _campModal('#modal-machine-id').value = btn.dataset.machine || '';
    _campModal('#modal-action-text').value = btn.dataset.action || '';
    _campModal('#modal-interval-hours').value = btn.dataset.interval || '';
    const ividEl = $('#modal-interval-id') as HTMLInputElement | null;
    if (ividEl) ividEl.value = btn.dataset.ivid || '';
    // după un workflow finalizat, fereastra completează rândul lui din jurnal (nu scrie altul)
    const wfLogEl = $('#modal-workflow-log-id') as HTMLInputElement | null;
    if (wfLogEl) wfLogEl.value = btn.dataset.wflogid || '';
    // o lucrare alocată (fără interval) începută de aici se închide odată cu salvarea
    const taskEl = $('#modal-task-id') as HTMLInputElement | null;
    if (taskEl) taskEl.value = btn.dataset.taskid || '';
    const alarmaEl = $('#modal-alarma-deasa-id') as HTMLInputElement | null;
    if (alarmaEl) alarmaEl.value = btn.dataset.alarmadeasa || '';
    ($('#modal-desc') as HTMLElement).innerHTML = `<strong>${btn.dataset.machine}</strong>: ${btn.dataset.action || ''}`;
    _campModal('#modal-date').value = today;
    _campModal('#modal-hours').value = String(state.machineHours[btn.dataset.machine || ''] || 0);
    _campModal('#modal-technician').value = state.currentUser?.username || '';
    _campModal('#modal-notes').value = '';

    const dateDisplay = $('#modal-date-display');
    const timeDisplay = $('#modal-time-display');
    const durationDisplay = $('#modal-duration-display');
    if (dateDisplay) dateDisplay.textContent = formatDateRO(now);
    if (timeDisplay) timeDisplay.textContent = now.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    if (durationDisplay) {
        if (state.revisionDetailOpenedAt) {
            const min = Math.round((Date.now() - state.revisionDetailOpenedAt) / 60000);
            durationDisplay.textContent = min + ' min';
        } else {
            durationDisplay.textContent = '—';
        }
    }
}

($('#modal-cancel') as HTMLElement).addEventListener('click', () => {
    ($('#modal-overlay') as HTMLElement).classList.add('hidden');
    const suggestBox = document.getElementById('modal-colleague-suggestion');
    if (suggestBox) suggestBox.innerHTML = '';
    // The workflow (if there is one) is already completed at this point — still offer
    // the lesson (from server-side field notes), just without the log fields.
    const wfLesson = takePendingWorkflowLesson();
    if (wfLesson) {
        colleagueOfferLesson({
            context_type: 'workflow', workflow_id: wfLesson.workflow_id,
            machine_id: wfLesson.machine_id, problem: wfLesson.problem,
        }).catch(() => {});
    }
});

($('#modal-overlay') as HTMLElement).addEventListener('click', e => {
    // ferestre se inchid doar manual (buton X)
});

// Coleg AI: draft a proposal for the observations field; text is applied
// only when the mechanic explicitly clicks „Preia".
document.getElementById('modal-colleague-help')?.addEventListener('click', () => {
    const notesEl = document.getElementById('modal-notes') as HTMLTextAreaElement;
    const box = document.getElementById('modal-colleague-suggestion');
    if (!notesEl || !box) return;
    colleagueSuggestField({
        machine_id: ($('#modal-machine-id') as HTMLInputElement)?.value || '',
        action: ($('#modal-action-text') as HTMLInputElement)?.value || '',
        currentText: notesEl.value.trim(),
        targetEl: notesEl,
        containerEl: box,
    }).catch(() => {});
});

($('#modal-form') as HTMLFormElement).addEventListener('submit', async e => {
    e.preventDefault();
    const machineId = _campModal('#modal-machine-id').value;
    const intervalH = parseInt(_campModal('#modal-interval-hours').value) || 0;
    const hoursNow = parseInt(_campModal('#modal-hours').value, 10) || 0;
    const now = new Date();
    let durationMinutes = null;
    let completedTime = null;
    if (state.revisionDetailOpenedAt) {
        durationMinutes = Math.round((now.getTime() - state.revisionDetailOpenedAt) / 60000);
        completedTime = now.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', hour12: false });
        state.revisionDetailOpenedAt = null;
    }

    const category = (state.currentRevision && state.currentRevision.iv && state.currentRevision.iv.category) ? state.currentRevision.iv.category : '';
    const modalAction = _campModal('#modal-action-text').value;
    const modalIvid = parseInt(($('#modal-interval-id') as HTMLInputElement)?.value || '', 10)
        || (state.currentRevision && state.currentRevision.iv && state.currentRevision.iv.id) || null;
    const modalWfLog = parseInt(($('#modal-workflow-log-id') as HTMLInputElement)?.value || '', 10) || null;
    // Colegul AI (31 oct 2026): după un workflow întrebarea s-a pus la „Finalizează” — aici doar la închiderea directă
    const colegModal = modalWfLog ? null : await colegIntreaba({
        interval_id: modalIvid, machine_id: machineId, notes: (_campModal('#modal-notes').value || '').trim() });
    const modalNotes = await confirmaUnText('Observații',
        notaCuRaspuns((_campModal('#modal-notes').value || '').trim(), colegModal), 'mentenanta-efectuata');
    const res: LucrareScrisa = await api('/api/maintenance_log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            machine_id: machineId,
            date: _campModal('#modal-date').value,
            action: modalAction,
            technician: _campModal('#modal-technician').value.trim(),
            hours_at_service: hoursNow,
            next_due_hours: intervalH > 0 ? hoursNow + intervalH : null,
            interval_id: modalIvid,
            notes: modalNotes,
            coleg: colegModal,
            parts_used: '',
            duration_minutes: durationMinutes,
            completed_time: completedTime,
            category: category,
            workflow_log_id: modalWfLog,
            // 30 sep 2026: „Încep" pe o lucrare alocată fără interval deschidea fereastra asta
            // fără id-ul lucrării — jurnalul se scria, dar lucrarea rămânea deschisă și revenea
            task_id: parseInt(($('#modal-task-id') as HTMLInputElement)?.value || '', 10) || null,
            // 4 sep 2026: fără `status`, serverul punea «active» și lucrarea
            // rămânea deschisă lângă geamăna ei închisă de workflow (6 dubluri
            // într-o săptămână). Intenția se spune, nu se deduce.
            status: 'completed',
            // 11 oct 2026: lucrarea pornită din „Am rezolvat cauza” (alarmă prea deasă) se leagă de semnul ei
            alarma_deasa_id: parseInt(($('#modal-alarma-deasa-id') as HTMLInputElement | null)?.value || '', 10) || null,
        })
    });

    state.machineHours[machineId] = hoursNow;
    localStorage.setItem('machineHours', JSON.stringify(state.machineHours));

    ($('#modal-overlay') as HTMLElement).classList.add('hidden');
    toast('Mentenanță înregistrată cu succes!');

    // Coleg AI (fire-and-forget, never blocks the flow): after a finished
    // workflow always offer the lesson (field notes live server-side); for a
    // plain log entry only when the mechanic wrote observations.
    const wfLesson = takePendingWorkflowLesson();
    if (wfLesson) {
        colleagueOfferLesson({
            context_type: 'workflow', workflow_id: wfLesson.workflow_id,
            machine_id: wfLesson.machine_id, problem: wfLesson.problem,
            actions: modalAction, notes: modalNotes,
        }).catch(() => {});
    } else if (modalNotes) {
        colleagueOfferLesson({
            context_type: 'log', log_id: (res && res.id) || 0,
            machine_id: machineId, actions: modalAction, notes: modalNotes,
        }).catch(() => {});
    }
    const suggestBox = document.getElementById('modal-colleague-suggestion');
    if (suggestBox) suggestBox.innerHTML = '';
    if (state.currentRevision) state.currentRevision = null;
    loadCalendar();
    loadMyTasks();
    if (window._detailLineId) {
        try {
            const fresh = await api(`/api/line_detail/${window._detailLineId}`);
            window._detailData = fresh;
            // 11 oct 2026: era `renderDetailTab()` neimportat — ReferenceError înghițit de catch, deci detaliul liniei
            // deschise nu se reîmprospăta după salvare
            window.renderDetailTab?.();
        } catch(e) {}
    }
});
